import { QuotePriceInput } from "@/components/quote-price-input";
import { savedQuoteEditorState, savedQuoteEditLines } from "@/lib/saved-quote-editor";
import { describeQuoteSaveError } from "@/lib/quote-save-error";
import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { floorScopeLabel } from "@/lib/floor-scope-label";
import { withQuoteAnalysisDeadline } from "@/lib/quote-analysis-request";
import { recoverQuoteResponse, isFixedQuoteExample } from "@/lib/quote-response-recovery";
import { loadPendingGeneration, rememberGeneration, forgetGeneration, beginGeneration, isCompletedGeneration, markGenerationDelivered, isCurrentGeneration, runBackgroundQuote, type PendingQuoteGeneration, type GenerationConnection } from "@/lib/quote-generation-session";
import { groupQuoteRows } from "@/lib/quote-library-recognition";
import { hasManualPriceOverride, manualPriceBaseline } from "@/lib/quote-price-override";
import { usePageTracking } from "@/hooks/use-track";
import { useQuoteEntryScroll } from "@/hooks/use-quote-entry-scroll";
import { trackEvent } from "@/App";
import { QuoteGuide } from "@/components/quote-guide/QuoteGuide";
// Temporarily hidden until the mascot guide is ready; keep its implementation intact.
const QUOTE_GUIDE_ENABLED = false;

import { HelpTip } from "@/components/help-tip";
import { useSetPageTourId } from "@/components/ProductTour";
import { useQuoteHeadingPreferences } from "@/hooks/use-quote-heading-preferences";
import { quoteHeadingLabel } from "@workspace/api-zod/quote-headings";
import type { MeasurementReview } from "@workspace/api-zod/measurement-review";
import { MeasurementReviewPanel } from "@/components/measurement-review-panel";
import { acceptsDescriptionResult, descriptionPricingInputs, descriptionRemainsReady, measurementConfirmationKey, readDescriptionResult, type DescriptionRequestIdentity } from "@/lib/description-journey";
import { createMeasurementReview, buildMeasurementReview, simplifyMeasurementReviewFields, resolveMeasurementReviewFields, type MeasurementReviewField } from "@/lib/measurement-review";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation } from "wouter";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { PercentInput } from "@/components/percent-input";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { queryClient, apiRequest, fetchWithAuth } from "@/lib/queryClient";
import { trackBuilderEvent } from "@/lib/analytics";
import { useAuth } from "@/hooks/use-auth";
import { isTrialQuotaEnforced as computeIsTrialQuotaEnforced, isTrialBlocked, isQuotaErrorCode, getQuotaInfo } from "@/lib/quota-banners";
import { getTestQuoteGenerationSource } from "@/lib/signup-flow";
import { groupEquivalentLineItems, getItemSubGroup, ITEM_SUB_GROUP_LABELS, STAGE_SUB_GROUP_ORDER, type ItemSubGroupId } from "@workspace/api-zod/work-type";
import { getMarketCurrency, formatMoney } from "@/lib/utils";
import { 
  ArrowLeft, 
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Upload, 
  Image as ImageIcon, 
  Sparkles, 
  Trash2, 
  FileText,
  Check,
  AlertCircle,
  Info,
  Loader2,
  X,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  BookmarkPlus,
  Search,
  Lightbulb,
  Eye,
  HardHat,
  Package,
  Truck,
  BrickWall,
  Forklift,
  Mic,
  Square,
  Save,
  Hammer,
  Layers,
  Building2,
  TreePine,
  Paintbrush,
  Zap,
  Wrench,
  Home,
  LayoutGrid,
  Car,
  Triangle,
  Droplets,
  Flame,
  ArrowUpFromLine,
  PaintBucket,
  Landmark,
  Columns,
  DoorOpen,
  Sun,
  Share2,
  Clock,
  CircleDashed,
  MoreVertical,
  type LucideIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getCostCategories, getCostTrades, getTaxRate, getTaxLabel, COST_CATEGORIES as UK_COST_CATEGORIES, COST_TRADES as UK_COST_TRADES, UNITS, getUnits, type Client } from "@shared/schema";
import { getCategoryIcon, getItemSortPriority } from "@/lib/category-icons";
import { rescaleBreakdown, subtotalFromBreakdown } from "@/lib/cost-breakdown";
import { COST_NAV_SECTIONS } from "@/lib/cost-library-nav";
import { getItemStage } from "@/lib/section-placement";
import { validateQuoteUploadSelection } from "@/lib/guest-upload-validation";
import { getGuestAccessNotice, getGuestErrorNotice } from "@/lib/guest-access-notice";
import { normalizeExtractorClassification, reassignItemCategory, shouldResetCategoryFilter } from "@/lib/category-reassignment";
import {
  DraggableQuoteRow,
  QuotePlacementDropTarget,
  type QuotePlacementDropData,
} from "@/components/quote-placement-dnd";
import { QuoteItemRecipeBreakdown, quoteRecipeSplit, recipeAtQuotePrice } from "@/components/quote-item-recipe-breakdown";
import { QuoteDimensions, QuantityMaterialsReview } from "@/components/quote-dimensions";
import { QuoteItemFields, QuoteOptionalHeader, QuoteRowActions, optionalWorksStyles } from "@/components/quote-item-presentation";
import { QuoteMaterialsList } from "@/components/quote-materials-list";
import { commitDraftMaterialsConfirmation } from "@/components/use-materials-confirmation";
import { QuoteMaterialsWorkspace } from "@/components/quote-materials-workspace";
import { QuoteAddItemsLayout } from "@/components/quote-add-items-browser";
import { QuoteScopeReview } from "@/components/quote-scope-review";
import { createCatalogueQuoteItem } from "@/lib/quote-catalogue-item";
import { markDimensionPriceReview, priceRecipeFromDimensions as priceRecipeForQuote } from "@workspace/api-zod/dimension-pricing";
import { buildMaterialsList, updateMaterialPrice, setMaterialPackRounding, setMaterialOrderQuantity } from "@workspace/api-zod/materials-list";
import { getEffectiveStage, getEffectiveSubGroup } from "@/lib/section-placement";
import { assertDimensionQuantity, type QuantityDerivation } from "@workspace/api-zod/quantity-dimensions";
import { deriveCostRecipe, recipeForQuoteSnapshot, type CostRecipe } from "@workspace/api-zod/cost-recipe";
import {
  getEffectiveQuoteStage,
  getEffectiveQuoteSubGroup,
  moveQuoteItem,
} from "@/lib/quote-placement";

const DESCRIPTION_STEPS = [
  { label: "Reading uploaded documents", detail: "Extracting text and analysing drawings..." },
  { label: "Identifying key project elements", detail: "Detecting trades, materials and scope items..." },
  { label: "Cross-referencing requirements", detail: "Matching specifications and quantities..." },
  { label: "Running quality checks", detail: "Master Builder and Engineer review..." },
  { label: "Finalising description", detail: "Compiling structured project scope..." },
];

const QUOTE_STEPS = [
  { label: "Extracting work scope", detail: "Identifying trades, quantities and site conditions..." },
  { label: "Validating & correcting", detail: "Checking scope completeness and structural accuracy..." },
  { label: "Pricing each item", detail: "Applying market rates and cost library pricing..." },
  { label: "Building your quote", detail: "Assembling line items and calculating totals..." },
];

const DISCOVERED_ITEMS_BY_TYPE: Record<string, string[]> = {
  structural: [
    "Steel beam identified (UB section)",
    "Padstone requirements detected",
    "Load-bearing wall modification",
    "Temporary propping needed",
    "Fire protection requirement",
    "Making good to disturbed areas",
    "Building control submission",
    "Waste removal allowance",
  ],
  decorating: [
    "Skirting board installation",
    "Surface preparation required",
    "Filling and sanding identified",
    "Primer coat required",
    "Paint finish detected",
    "Architrave installation",
    "Protection to floor surfaces",
    "Waste removal allowance",
  ],
  bathroom: [
    "Sanitaryware suite identified",
    "Hot and cold water connections",
    "Waste pipework required",
    "Wall tiling detected",
    "Waterproofing requirement",
    "Extraction fan required",
    "Making good to disturbed areas",
    "Waste removal allowance",
  ],
  kitchen: [
    "Kitchen units identified",
    "Worktop installation detected",
    "Appliance connections required",
    "Plumbing connections identified",
    "Electrical outlets required",
    "Splashback tiling detected",
    "Making good to disturbed areas",
    "Waste removal allowance",
  ],
  roofing: [
    "Roof covering identified",
    "Felt underlay requirement",
    "Fascia and soffit work detected",
    "Guttering replacement required",
    "Scaffolding requirement identified",
    "Flashing details detected",
    "Making good to disturbed areas",
    "Waste removal allowance",
  ],
  groundworks: [
    "Excavation requirement detected",
    "Concrete foundation identified",
    "Drainage connections required",
    "Hardcore and compaction",
    "DPC requirement identified",
    "Reinstatement of surfaces",
    "Skip hire required",
    "Waste removal allowance",
  ],
  flooring: [
    "Floor covering identified",
    "Subfloor preparation required",
    "Threshold strips detected",
    "Underlay requirement identified",
    "Skirting reinstatement",
    "Protection to adjacent areas",
    "Adhesive and fixings required",
    "Waste removal allowance",
  ],
  electrical: [
    "Consumer unit identified",
    "First fix wiring detected",
    "Socket and switch positions",
    "Lighting circuit identified",
    "Earth bonding requirement",
    "Testing and certification",
    "Making good to disturbed areas",
    "Waste removal allowance",
  ],
  plumbing: [
    "Pipework identified",
    "Hot water connections detected",
    "Radiator connections required",
    "Stop valve installation",
    "Pressure testing required",
    "Making good to disturbed areas",
    "Isolation requirements",
    "Waste removal allowance",
  ],
  general: [
    "Preparation works identified",
    "Materials supply requirement",
    "Fixings and fittings detected",
    "Making good to disturbed areas",
    "Waste removal allowance",
    "Access requirement identified",
    "Protection to existing finishes",
    "Final clean required",
  ],
};

function getDiscoveredItemsForDescription(description: string): string[] {
  const d = description.toLowerCase();
  if (/beam|steel|rsj|structural|load.?bearing|lintel|padstone|propp|steelwork/.test(d)) return DISCOVERED_ITEMS_BY_TYPE.structural;
  if (/bathroom|toilet|wc|shower|sanitaryware|basin|bath suite/.test(d)) return DISCOVERED_ITEMS_BY_TYPE.bathroom;
  if (/kitchen|worktop|kitchen unit|cupboard.*kitchen|kitchen.*cupboard/.test(d)) return DISCOVERED_ITEMS_BY_TYPE.kitchen;
  if (/roof|re-roof|re roof|felt|fascia|soffit|gutter|ridge tile/.test(d)) return DISCOVERED_ITEMS_BY_TYPE.roofing;
  if (/ground|foundation|drainage|excavat|hardcore|substructure|concrete slab/.test(d)) return DISCOVERED_ITEMS_BY_TYPE.groundworks;
  if (/floor|carpet|laminate|vinyl|engineered wood|hardwood|parquet|screed/.test(d)) return DISCOVERED_ITEMS_BY_TYPE.flooring;
  if (/electric|wiring|consumer unit|socket|lighting|fuse board|first fix elec/.test(d)) return DISCOVERED_ITEMS_BY_TYPE.electrical;
  if (/plumb|pipework|boiler|radiator|cylinder|hot water|heating|central heat/.test(d)) return DISCOVERED_ITEMS_BY_TYPE.plumbing;
  if (/skirt|paint|decor|plaster|skim|architrave|coving|emulsion|gloss|render/.test(d)) return DISCOVERED_ITEMS_BY_TYPE.decorating;
  return DISCOVERED_ITEMS_BY_TYPE.general;
}


interface PriceAdjustment {
  category: string;
  percent: number;
  reason: string;
}

function capitalizeCategory(cat: string): string {
  return cat
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, l => l.toUpperCase());
}

function isRoofConceptFrontend(name: string, description: string): boolean {
  const nameLow = name.toLowerCase();
  const descLow = (description || "").toLowerCase();
  const combinedText = `${nameLow} ${descLow}`;
  // Use word-boundary check: "waterproofing" contains "roof" as a substring but is NOT a roof item.
  // \b(roof|roofing)\b matches standalone "roof"/"roofing" but NOT "waterproofing" (preceded by a word char).
  const hasRoofInName = /\b(roof|roofing|re-roofing)\b/.test(nameLow) || nameLow.includes("pitched");
  const hasTilesWithRoofContext = (nameLow.includes("tiled") || nameLow.includes("tiles")) && /\b(roof|roofing)\b/.test(combinedText);
  const hasFasciaGutter = nameLow.includes("fascia") || nameLow.includes("gutter") || nameLow.includes("soffit");
  const nameIsDemolition = nameLow.includes("demolition") || nameLow.includes("strip out") || 
    nameLow.includes("removal") || nameLow.includes("skip") || nameLow.includes("waste");
  const isWaterproofing = nameLow.includes("waterproof") || nameLow.includes("tanking") || nameLow.includes("damp proof");
  if (nameIsDemolition || isWaterproofing) return false;
  return hasRoofInName || hasTilesWithRoofContext || hasFasciaGutter;
}

function detectSubcategory(name: string, description: string, category: string, trade?: string): string {
  const nameLow = name.toLowerCase();
  const descLow = (description || "").toLowerCase();

  const effectiveCategory = (isRoofConceptFrontend(name, description) && category !== "roofing") ? "roofing" : category;

  let bestMatch: { id: string; score: number } | null = null;

  for (const section of COST_NAV_SECTIONS) {
    for (const item of section.items) {
      if (item.categoryFilter !== effectiveCategory) continue;
      let score = 0;
      if (item.tradeFilter && item.tradeFilter === trade) score += 10;
      if (item.keywordFilter) {
        const keywords = item.keywordFilter.split(" ");
        for (const kw of keywords) {
          const kwLow = kw.toLowerCase();
          const kwSpaced = kwLow.replace(/_/g, " ");
          if (nameLow.includes(kwLow) || (kwSpaced !== kwLow && nameLow.includes(kwSpaced))) score += 4;
          else if (descLow.includes(kwLow) || (kwSpaced !== kwLow && descLow.includes(kwSpaced))) score += 2;
        }
      }
      if (!item.keywordFilter && !score && item.tradeFilter === trade) score += 1;
      if (score > 0 && (!bestMatch || score > bestMatch.score)) {
        bestMatch = { id: item.id, score };
      }
    }
  }

  if (bestMatch) return bestMatch.id;

  for (const section of COST_NAV_SECTIONS) {
    for (const item of section.items) {
      if (item.categoryFilter === effectiveCategory && item.tradeFilter === trade) {
        return item.id;
      }
    }
  }
  for (const section of COST_NAV_SECTIONS) {
    for (const item of section.items) {
      if (item.categoryFilter === effectiveCategory && !item.keywordFilter) {
        return item.id;
      }
    }
  }
  if (effectiveCategory === "roofing") return "roof_structure";
  return "";
}

const STAGE_ORDER = [
  "Preparation",
  "Demolition",
  "Substructure",
  "Structure",
  "Roofing",
  "External Works",
  "Finishes",
  "Services",
  "Other",
] as const;

const STAGE_ICONS: Record<string, React.ElementType> = {
  "Preparation":    HardHat,
  "Demolition":     Hammer,
  "Substructure":   Layers,
  "Structure":      Building2,
  "Roofing":        Home,
  "External Works": TreePine,
  "Finishes":       Paintbrush,
  "Services":       Zap,
  "Other":          Wrench,
};

function getCategoryTradeLabel(category: string, trade?: string, subcategory?: string, market?: string | null): string {
  if (!category) return "Choose Category";
  if (subcategory) {
    for (const section of COST_NAV_SECTIONS) {
      const item = section.items.find(i => i.id === subcategory);
      if (item) return `${section.label} > ${item.label}`;
    }
  }
  for (const section of COST_NAV_SECTIONS) {
    for (const item of section.items) {
      if (item.categoryFilter === category && item.tradeFilter === trade) {
        return `${section.label} > ${item.label}`;
      }
    }
  }
  if (trade) {
    for (const section of COST_NAV_SECTIONS) {
      for (const item of section.items) {
        if (item.categoryFilter === category && !item.tradeFilter) {
          return `${section.label} > ${item.label}`;
        }
      }
    }
  }
  const cats = getCostCategories(market);
  const trades = getCostTrades(market);
  const catObj = cats.find(c => c.value === category);
  const catLabel = catObj?.label || capitalizeCategory(category);
  if (!trade) return catLabel;
  const tradeObj = trades[category]?.trades?.find((t: { value: string; label: string }) => t.value === trade);
  if (!tradeObj) return catLabel;
  return `${catLabel} > ${tradeObj.label}`;
}

function getCategoryTradeValue(category: string, trade?: string): string {
  if (trade) return `${category}::${trade}`;
  return category;
}

function parseCategoryTradeValue(value: string): { category: string; trade?: string } {
  const parts = value.split("::");
  if (parts.length === 2) return { category: parts[0], trade: parts[1] };
  return { category: parts[0] };
}

function CategoryPickerDialog({
  open,
  onOpenChange,
  currentCategory,
  currentTrade,
  currentSubcategory,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentCategory: string;
  currentTrade?: string;
  currentSubcategory?: string;
  onSelect: (category: string, trade: string, subcategory: string) => void;
}) {
  const [search, setSearch] = useState("");
  const findMatchingSection = useCallback((cat: string, trade?: string, sub?: string) => {
    if (sub) {
      const match = COST_NAV_SECTIONS.find(s => s.items.some(i => i.id === sub));
      if (match) return match.id;
    }
    const match = COST_NAV_SECTIONS.find(s =>
      s.items.some(i => i.categoryFilter === cat && (!trade || i.tradeFilter === trade))
    );
    return match?.id || null;
  }, []);
  const [expandedSection, setExpandedSection] = useState<string | null>(() => findMatchingSection(currentCategory, currentTrade, currentSubcategory));

  useEffect(() => {
    if (open) {
      setExpandedSection(findMatchingSection(currentCategory, currentTrade, currentSubcategory));
      setSearch("");
    }
  }, [open, currentCategory, currentTrade, currentSubcategory, findMatchingSection]);

  const filteredSections = useMemo(() => {
    if (!search.trim()) return COST_NAV_SECTIONS;
    const q = search.toLowerCase();
    return COST_NAV_SECTIONS
      .map((section) => {
        const matchingItems = section.items.filter(
          (item) =>
            item.label.toLowerCase().includes(q) ||
            section.label.toLowerCase().includes(q)
        );
        if (matchingItems.length > 0 || section.label.toLowerCase().includes(q)) {
          return {
            ...section,
            items: section.label.toLowerCase().includes(q) ? section.items : matchingItems,
          };
        }
        return null;
      })
      .filter(Boolean) as typeof COST_NAV_SECTIONS;
  }, [search]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0" data-testid="dialog-category-picker">
        <DialogHeader className="px-4 pt-4 pb-0">
          <DialogTitle className="text-base">Select Category</DialogTitle>
        </DialogHeader>
        <div className="px-4 py-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search categories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="input-category-picker-search"
              autoFocus
            />
          </div>
        </div>
        <ScrollArea className="max-h-[400px]">
          <div className="px-2 pb-3">
            {filteredSections.map((section) => {
              const SectionIcon = section.icon;
              const isExpanded = expandedSection === section.id || search.trim().length > 0;

              return (
                <div key={section.id} data-testid={`picker-section-${section.id}`}>
                  <button
                    onClick={() => {
                      setExpandedSection(expandedSection === section.id ? null : section.id);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-left hover-elevate transition-colors"
                    data-testid={`button-picker-section-${section.id}`}
                  >
                    <SectionIcon className="h-5 w-5 text-foreground flex-shrink-0" />
                    <span className="text-sm font-semibold text-foreground flex-1">{section.label}</span>
                    {isExpanded
                      ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      : <ChevronDown className="h-4 w-4 text-muted-foreground rotate-[-90deg]" />
                    }
                  </button>
                  <div
                    className="overflow-hidden transition-all duration-200 ease-in-out"
                    style={{
                      maxHeight: isExpanded ? `${section.items.length * 40}px` : "0",
                      opacity: isExpanded ? 1 : 0,
                    }}
                  >
                    {section.items.map((item) => {
                      const ItemIcon = item.icon;
                      const isActive = currentSubcategory
                        ? item.id === currentSubcategory
                        : item.categoryFilter === currentCategory && item.tradeFilter === currentTrade;
                      return (
                        <button
                          key={item.id}
                          onClick={() => {
                            onSelect(item.categoryFilter || "", item.tradeFilter || "", item.id);
                            onOpenChange(false);
                          }}
                          className={`w-full flex items-center gap-2.5 pl-10 pr-3 py-2 rounded-md text-left transition-colors ${
                            isActive
                              ? "bg-primary/10 text-foreground font-medium"
                              : "hover-elevate text-muted-foreground"
                          }`}
                          data-testid={`button-picker-item-${item.id}`}
                        >
                          <ItemIcon className={`h-4 w-4 flex-shrink-0 ${isActive ? "text-foreground" : ""}`} />
                          <span className="text-sm flex-1">{item.label}</span>
                          {isActive && <Check className="h-4 w-4 text-foreground" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {filteredSections.length === 0 && search.trim() && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                No categories match your search
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

interface UploadedImage {
  id: string;
  file: File;
  preview: string;
  base64: string;
  mimeType: string;
}

interface GuestSample {
  id: string;
  label: string;
  description: string;
  projectType?: string;
}

interface GuestStatus {
  status?: string;
  revision?: number;
  draftRevision?: number;
  descriptionRevision?: number;
  snapshot?: any;
  result?: any;
  analysisResult?: any;
  retryAllowed?: boolean;
  detailedDescription?: string;
  simplifiedDescription?: string;
  draftState?: any;
  finalizedSnapshot?: any;
}

interface GuestPricing {
  subtotal: number;
  contingencyPercent: number;
  contingencyAmount: number;
  overheadPercent: number;
  overheadAmount: number;
  profitPercent: number;
  profitAmount: number;
  totalPrice: number;
  includeVat: boolean;
  vatRate: number;
  vatAmount: number;
  grandTotal: number;
}

function getGuestAttemptId(): string {
  if (typeof window !== "undefined") {
    const existing = window.sessionStorage.getItem("tpt_guest_quote_attempt");
    if (existing) return existing;
  }
  const id = typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `guest-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  if (typeof window !== "undefined") {
    window.sessionStorage.setItem("tpt_guest_quote_attempt", id);
  }
  return id;
}

async function parseGuestApiError(
  response: Response,
  fallback: string,
): Promise<Error & { code?: string }> {
  const payload = await response.json().catch(() => ({})) as {
    message?: string;
    code?: string;
  };
  const error = new Error(payload.message || fallback) as Error & { code?: string };
  error.code = payload.code;
  return error;
}

function unwrapGuestResult(payload: any): any {
  const value = payload?.analysisResult || payload?.result || payload?.quote || payload?.data || payload;
  return value?.analysisResult || value?.result || value;
}

interface AnalyzedLineItem {
  materialAllowanceApproval?: unknown;
  sourceVariant?: { name?: string; specification?: string };
  catalogueTaskKey?: string;
  quantityDerivation?: QuantityDerivation | null;
  category: string;
  trade?: string;
  subcategory?: string;
  name: string;
  description: string;
  quantity: number;
  unit: string;
  unitCost: number;
  labourHours: number;
  materialCost: number;
  section?: string;
  inclusions?: string[];
  exclusions?: string[];
  labourCost?: number;
  plantCost?: number;
  isOptional?: boolean;
  priceSource?: string;
  costLibraryTaskId?: string | null;
  costLibraryPrice?: number | null;
  costLibraryPricingCompatible?: boolean;
  manualPriceBaseline?: number;
  quoteCostRecipe?: CostRecipe | null;
  // Multi-scope quotes (Task #680): which detected scope this line belongs to.
  // Shared site-cost lines carry SHARED_SCOPE_NAME. Absent on single-scope jobs.
  scopeName?: string;
  /** Quote-local visual placement only; never changes Cost Library classification. */
  displayStage?: string | null;
  displaySubGroup?: string | null;
}

interface ValidationFlag {
  type: "scope_gap" | "measurement_issue" | "quantity_concern" | "unit_mismatch";
  itemIndex?: number;
  itemName?: string;
  detail: string;
  suggestion?: string;
  confidence: "high" | "medium" | "low";
  source: "master_builder" | "chartered_engineer";
  suggestedUnitCost?: number;
  suggestedQuantity?: number;
  suggestRemoval?: boolean;
}

interface SuggestedItem {
  section: string;
  category: string;
  trade?: string;
  name: string;
  description: string;
  inclusions: string[];
  exclusions: string[];
  quantity: number;
  unit: string;
  unitCost: number;
  labourHours: number;
  labourCost: number;
  materialCost: number;
  plantCost: number;
  reason: string;
  confidence: "high" | "medium" | "low";
  source: string;
}

interface QuoteAnalysisResult {
  projectType: string;
  projectTypes: string[];
  projectSummary: string;
  lineItems: AnalyzedLineItem[];
  assumptions: string[];
  exclusions: string[];
  estimatedDays: number;
  confidenceLevel: "high" | "medium" | "low";
  notes: string;
  extractedCustomerName?: string;
  extractedAddress?: string;
  generatedDescription?: string;
  reviewerNotes?: string[];
  autoAddedItems?: { name: string; reason: string }[];
  validationFlags?: ValidationFlag[];
  suggestedItems?: SuggestedItem[];
  dimensionSource?: "extracted" | "estimated";
  uploadedFileKeys?: string[];
  uploadedFileNames?: string[];
  generationLogId?: string;
  testQuoteRecorded?: boolean;
  // Multi-scope quotes (Task #680). When isMultiScope is true, lineItems is the
  // merged Combined view (each tagged scopeName; shared costs counted once) and
  // scopes lists the detected scopes in order. Absent/false on single-scope.
  isMultiScope?: boolean;
  scopes?: { name: string; description: string; projectSummary?: string; estimatedDays?: number }[];
}

// Sentinel scopeName used for shared site-cost line items in a multi-scope quote.
const SHARED_SCOPE_NAME = "__shared__";

const DISPLAY_CATEGORIES: { label: string; icon: LucideIcon }[] = [
  // Shared project-wide costs (multi-scope quotes only — the group is hidden
  // automatically when no item carries SHARED_SCOPE_NAME).
  { label: "Shared", icon: Layers },
  { label: "Preparation", icon: HardHat },
  { label: "Demolition",  icon: Trash2 },
  { label: "Structure",   icon: Building2 },
  { label: "Roofing",     icon: Home },
  { label: "Plumbing",    icon: Droplets },
  { label: "Electrical",  icon: Zap },
  { label: "Finishes",    icon: PaintBucket },
];

function normCat(cat: string): string {
  return (cat || "").toLowerCase().replace(/[\s&-]+/g, "_").replace(/_+/g, "_");
}

const ROOFING_CATS = new Set(["roofing", "flat_roofing", "flat_roof", "roof_repair", "fascias_soffits", "fascias"]);

// Display group for the section chips: shared project-wide costs get their own
// "Shared Costs" group; everything else groups by category/stage as before.
function getDisplayGroup(li: { category?: string; trade?: string; name?: string; scopeName?: string }): string | null {
  if (li.scopeName === SHARED_SCOPE_NAME) return "Shared";
  return getCategoryGroup(li.category || "", li.trade, li.name);
}

function getCategoryGroup(category: string, trade?: string, name?: string): string | null {
  const cat = normCat(category);
  // Roofing override: CATEGORY_TO_STAGE maps roofing → "Structure" but we show it separately
  if (ROOFING_CATS.has(cat) || cat.startsWith("roof_")) return "Roofing";
  const stage = getItemStage(cat, trade, name);
  switch (stage) {
    case "Preparation":    return "Preparation";
    case "Demolition":     return "Demolition";
    case "Roofing":        return "Roofing";
    case "Substructure":
    case "Structure":
    case "External Works": return "Structure";
    case "Services":
      return (cat === "electrics" || cat.startsWith("electric")) ? "Electrical" : "Plumbing";
    case "Finishes":       return "Finishes";
    default:               return null;
  }
}

function shortenScopeLabel(label: string): string {
  const floorLabel = floorScopeLabel(label);
  if (floorLabel) return floorLabel;
  const l = label.toLowerCase();
  if (l === "combined") return "Combined";
  if (l.includes("shared")) return "Shared";
  // "extension" must be checked before "porch"/"loft": a combined label like
  // "Extension and Porch" is primarily an extension scope, not a porch.
  if (l.includes("extension")) return "Extension";
  if (l.includes("loft")) return "Loft";
  if (l.includes("porch")) return "Porch";
  if (l.includes("renovation") || l.includes("refurb")) return "Renovation";
  if (l.includes("kitchen")) return "Kitchen";
  if (l.includes("bathroom")) return "Bathroom";
  if (l.includes("garage")) return "Garage";
  if (l.includes("new build")) return "New Build";
  if (l.includes("roof")) return "Roofing";
  if (l.includes("structural") || l.includes("opening")) return "Structural";
  const first = label.split(/\s+/)[0] || label;
  return first.charAt(0).toUpperCase() + first.slice(1);
}

const projectFormSchema = z.object({
  clientId: z.string().optional(),
  customerName: z.string().optional(),
  projectAddress: z.string().optional(),
  description: z.string().optional(),
  projectTypes: z.array(z.string()).optional(),
});

type ProjectFormData = z.infer<typeof projectFormSchema>;

const DRAFT_STORAGE_KEY = "tpt_draft_quote";
const STEP1_STORAGE_KEY = "tpt_step1_form";
const STEP1_FILES_DB = "tpt_step1_files";

function openFilesDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(STEP1_FILES_DB, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("files")) {
        db.createObjectStore("files", { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function saveFilesToDB(images: UploadedImage[]) {
  try {
    const db = await openFilesDB();
    const tx = db.transaction("files", "readwrite");
    const store = tx.objectStore("files");
    store.clear();
    for (const img of images) {
      store.put({ id: img.id, base64: img.base64, mimeType: img.mimeType, preview: img.preview, fileName: img.file?.name || "file" });
    }
    db.close();
  } catch {}
}

async function loadFilesFromDB(): Promise<(Omit<UploadedImage, "file"> & { fileName?: string })[]> {
  try {
    const db = await openFilesDB();
    const tx = db.transaction("files", "readonly");
    const store = tx.objectStore("files");
    return new Promise((resolve) => {
      const req = store.getAll();
      req.onsuccess = () => { db.close(); resolve(req.result || []); };
      req.onerror = () => { db.close(); resolve([]); };
    });
  } catch { return []; }
}

async function clearFilesDB() {
  try {
    const db = await openFilesDB();
    const tx = db.transaction("files", "readwrite");
    tx.objectStore("files").clear();
    db.close();
  } catch {}
}

interface DraftQuoteState {
  dimensionEvidence?: any[];
  descriptionWorkItems?: any[];
  preparedQuote?: any;
  generatedDescriptionBasis?: string | null;
  confirmedMeasurementKey?: string | null;
  measurementReviewSkipped?: boolean;
  measurementReviewFields?: MeasurementReviewField[];
  measurementJobLabel?: string;
  descriptionIsGenerated?: boolean;
  step: number;
  analysisResult: QuoteAnalysisResult;
  lineItems: AnalyzedLineItem[];
  baseLineItems: AnalyzedLineItem[];
  baseEstDays: number;
  formValues: ProjectFormData;
  selectedTypes: string[];
  includeVat: boolean;
  contingencyPercent: string;
  overheadPercent: string;
  profitPercent: string;
  contingencyEnabled: boolean;
  overheadEnabled: boolean;
  profitEnabled: boolean;
  customEstDays: number | null;
  editedAssumptions: string[];
  editedExclusions: string[];
  editedPrices: Record<string, { originalPrice: number; currentIndex: number }>;
  appliedAdjustments: PriceAdjustment[];
  dismissedSuggestions: number[];
  appliedFlags: number[];
  appliedDescFlags: number[];
  descriptionFlags: any[];
  structuralSummary: any | null;
  pricingMode: "standard" | "learned" | "custom";
  savedAt: number;
}

function saveDraftToStorage(draft: DraftQuoteState) {
  try {
    sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    return true;
  } catch (e) {
    return false;
  }
}

function loadDraftFromStorage(): DraftQuoteState | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as DraftQuoteState;
    if (Date.now() - draft.savedAt > 4 * 60 * 60 * 1000) {
      sessionStorage.removeItem(DRAFT_STORAGE_KEY);
      return null;
    }
    return draft;
  } catch (e) {
    return null;
  }
}

function clearDraftFromStorage() {
  sessionStorage.removeItem(DRAFT_STORAGE_KEY);
  sessionStorage.removeItem("tpt_quote_active");
  sessionStorage.removeItem(STEP1_STORAGE_KEY);
  clearFilesDB();
}


const STANDARD_ASSUMPTIONS_UK: string[] = [
  "The price is based on the information provided at time of quoting (drawings/photos/site notes).",
  "Works are carried out during normal weekday working hours with reasonable access to the work area.",
  "Existing structure/services are assumed to be in reasonable condition unless stated otherwise.",
  "Making good is limited to areas directly affected by the works described in the scope.",
  "Final quantities may vary slightly once works open up; any changes will be agreed before proceeding.",
  "Client provides clear working area and protection/removal of personal belongings as needed.",
];

const STANDARD_EXCLUSIONS_UK: string[] = [
  "Statutory fees and professional fees (e.g., Building Control, structural engineer, architect).",
  "Party Wall matters and neighbour agreements.",
  "Asbestos removal or specialist hazardous material handling.",
  "Repairs to pre-existing defects, hidden damage, rot, infestation, or structural issues discovered after opening up.",
  "Upgrades to existing electrical/plumbing systems beyond local alterations required for the described works.",
  "Full redecoration of rooms (only local making-good areas, if included, are covered).",
  "Any works not listed in the \"Scope of Works\" section.",
];

const STANDARD_ASSUMPTIONS_AU: string[] = [
  "The price is based on the information provided at time of quoting (drawings/photos/site notes).",
  "Works are carried out during normal weekday working hours with reasonable site access.",
  "Existing structure and services are assumed to be in reasonable condition unless stated otherwise.",
  "Making good is limited to areas directly affected by the works described in the scope.",
  "Final quantities may vary once works open up; any changes will be agreed before proceeding.",
  "Client provides clear working area and protection or removal of personal belongings as needed.",
];

const STANDARD_EXCLUSIONS_AU: string[] = [
  "Statutory and professional fees (e.g., Council DA / CC / OC fees, Principal Certifier, structural engineer, architect, draftsperson).",
  "Asbestos survey, containment or removal (Class A or Class B licensed work).",
  "Termite barrier or treatment beyond items expressly listed in the scope.",
  "Bushfire (BAL) upgrades, cyclone tie-down upgrades or specialist engineering not listed in the scope.",
  "Boundary, dilapidation or dividing fence matters with neighbours.",
  "Repairs to pre-existing defects, hidden damage, rot, decay or structural issues discovered after opening up.",
  "Upgrades to existing electrical, plumbing or gas systems beyond the alterations required for the described works.",
  "Full redecoration of rooms (only local making-good areas, if included, are covered).",
  "Any works not listed in the \"Scope of Works\" section.",
];

const STANDARD_ASSUMPTIONS_CA: string[] = [
  "The price is based on the information provided at time of quoting (drawings/photos/site notes).",
  "Works are carried out during normal weekday working hours with reasonable site access.",
  "Existing structure and services are assumed to be in reasonable condition unless stated otherwise.",
  "Making good is limited to areas directly affected by the works described in the scope.",
  "Final quantities may vary once works open up; any changes will be agreed before proceeding.",
  "Client provides clear working area and protection or removal of personal belongings as needed.",
];

const STANDARD_EXCLUSIONS_CA: string[] = [
  "Statutory and professional fees (e.g., municipal building permit fees, plan-review fees, development charges, structural engineer, architect, ESA inspection).",
  "Asbestos, vermiculite or lead-paint pre-renovation testing and abatement (priced as a change order if identified).",
  "Knob-and-tube or aluminum wiring replacement beyond items expressly listed in the scope.",
  "Underpinning, parging, waterproofing or weeping-tile replacement not listed in the scope.",
  "Boundary, fence-line or party-wall matters with neighbours.",
  "Repairs to pre-existing defects, hidden damage, rot, infestation, or structural issues discovered after opening up.",
  "Upgrades to existing electrical, plumbing or HVAC systems beyond local alterations required for the described works.",
  "Full redecoration of rooms (only local making-good areas, if included, are covered).",
  "Any works not listed in the \"Scope of Works\" section.",
];

const STANDARD_ASSUMPTIONS_US: string[] = [
  "The price is based on the information provided at time of quoting (drawings/photos/site notes).",
  "Works are carried out during normal weekday working hours with reasonable site access.",
  "Existing structure and services are assumed to be in reasonable condition unless stated otherwise.",
  "Making good is limited to areas directly affected by the works described in the scope.",
  "Final quantities may vary once works open up; any changes will be agreed before proceeding.",
  "Client provides clear working area and protection or removal of personal belongings as needed.",
];

const STANDARD_EXCLUSIONS_US: string[] = [
  "Statutory and professional fees (e.g., AHJ building permit fees, plan-review fees, impact fees, structural engineer, architect, third-party inspections).",
  "Asbestos, vermiculite or lead-paint pre-renovation testing and abatement under the EPA RRP Rule (priced as a change order if identified).",
  "Knob-and-tube or aluminum wiring replacement beyond items expressly listed in the scope.",
  "Underpinning, waterproofing or perimeter drain replacement not listed in the scope.",
  "Boundary, fence-line or party-wall matters with neighbors.",
  "Repairs to pre-existing defects, hidden damage, rot, infestation, or structural issues discovered after opening up.",
  "Upgrades to existing electrical, plumbing or HVAC systems beyond local alterations required for the described works.",
  "Full redecoration of rooms (only local making-good areas, if included, are covered).",
  "Any works not listed in the \"Scope of Works\" section.",
];

function getStandardAssumptions(market?: string | null): string[] {
  if (market === "au") return STANDARD_ASSUMPTIONS_AU;
  if (market === "ca") return STANDARD_ASSUMPTIONS_CA;
  if (market === "us") return STANDARD_ASSUMPTIONS_US;
  return STANDARD_ASSUMPTIONS_UK;
}
function getStandardExclusions(market?: string | null): string[] {
  if (market === "au") return STANDARD_EXCLUSIONS_AU;
  if (market === "ca") return STANDARD_EXCLUSIONS_CA;
  if (market === "us") return STANDARD_EXCLUSIONS_US;
  return STANDARD_EXCLUSIONS_UK;
}

export default function QuoteAIPage({ guestMode = false, editingQuoteId }: { guestMode?: boolean; editingQuoteId?: string; [key: string]: any } = {}) {
  useQuoteEntryScroll();
  const editingSavedQuote = !!editingQuoteId;
  const { names: headingNames } = useQuoteHeadingPreferences(!guestMode);
  usePageTracking("Smart Quote");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const { data: editingQuote, isLoading: editingQuoteLoading, error: editingQuoteError, refetch: reloadEditingQuote } = useQuery<any>({
    queryKey: ["/api/quotes", editingQuoteId],
    enabled: editingSavedQuote && !guestMode,
    queryFn: async () => (await apiRequest("GET", `/api/quotes/${editingQuoteId}`)).json(),
  });
  const [editHydrated, setEditHydrated] = useState(false);
  const [termsConditions, setTermsConditions] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  // Derive this on every navigation rather than capturing it once. Navigating
  // from the introduction to the normal /quotes/new route can reuse this same
  // component instance; a captured value would mislabel that later generation
  // as a Test Quote.
  const testQuoteGenerationSource = getTestQuoteGenerationSource(
    guestMode,
    window.location.search,
  );
  const firstQuoteMode = testQuoteGenerationSource !== undefined;
  const guestAttemptIdRef = useRef<string>(getGuestAttemptId());
  const guestStatusHydratedRef = useRef(false);
  const guestDescriptionRefreshRef = useRef(false);
  const [guestRevision, setGuestRevision] = useState(0);
  const [guestDescriptionRevision, setGuestDescriptionRevision] = useState(0);
  const guestDescriptionRevisionRef = useRef(0);
  const guestApprovedDescriptionRef = useRef<{ description: string; revision: number } | null>(null);
  // The status query is intentionally read-only.  Keep bootstrap separate so
  // a first visit can establish the signed cookie before PATCH /description
  // (which must never silently switch to a different guest entitlement).
  const guestSessionBootstrapRef = useRef<Promise<void> | null>(null);
  const [guestPricing, setGuestPricing] = useState<GuestPricing | null>(null);
  const [guestFinalized, setGuestFinalized] = useState(false);
  const [selectedGuestSample, setSelectedGuestSample] = useState<GuestSample | null>(null);
  const [previewGuestSample, setPreviewGuestSample] = useState<GuestSample | null>(null);
  const COST_CATEGORIES = getCostCategories(user?.market);
  const COST_TRADES = getCostTrades(user?.market);
  const [step, setStep] = useState(1);
  useSetPageTourId(step === 2 ? "auto-quote-review" : "auto-quote");
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [isProcessingFiles, setIsProcessingFiles] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const processingFilesRef = useRef(false);
  const [analysisResult, setAnalysisResult] = useState<QuoteAnalysisResult | null>(null);
  const [lineItems, setLineItems] = useState<AnalyzedLineItem[]>([]);
  const placementSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor),
  );
  // Multi-scope quotes (Task #680): which scope tab is active in the review UI.
  // "__combined__" shows the merged Combined/Total view. Only rendered when the
  // AI detected 2+ scopes; single-scope quotes never show tabs.
  const [activeScopeTab, setActiveScopeTab] = useState<string>("__combined__");
  const [activeCategoryFilter, setActiveCategoryFilter] = useState<string | null>(null);
  const [includeVat, setIncludeVat] = useState(true);
  const [contingencyPercent, setContingencyPercent] = useState("0");
  const [overheadPercent, setOverheadPercent] = useState("0");
  const [profitPercent, setProfitPercent] = useState("0");
  const [contingencyEnabled, setContingencyEnabled] = useState(false);
  const [overheadEnabled, setOverheadEnabled] = useState(false);
  const [profitEnabled, setProfitEnabled] = useState(false);
  const applyGuestPricing = useCallback((pricing: any) => {
    if (!pricing || typeof pricing !== "object") return;
    setGuestPricing(pricing as GuestPricing);
    if (typeof pricing.includeVat === "boolean") setIncludeVat(pricing.includeVat);
    if (Number.isFinite(Number(pricing.contingencyPercent))) {
      setContingencyPercent(String(pricing.contingencyPercent));
      setContingencyEnabled(Number(pricing.contingencyPercent) > 0);
    }
    if (Number.isFinite(Number(pricing.overheadPercent))) {
      setOverheadPercent(String(pricing.overheadPercent));
      setOverheadEnabled(Number(pricing.overheadPercent) > 0);
    }
    if (Number.isFinite(Number(pricing.profitPercent))) {
      setProfitPercent(String(pricing.profitPercent));
      setProfitEnabled(Number(pricing.profitPercent) > 0);
    }
  }, []);
  const baseLineItemsRef = useRef<AnalyzedLineItem[]>([]);
  // Snapshot of core-item prices at the moment the slider is first touched.
  // Keeps the scale anchor stable (no drift) and ensures the slider always
  // operates relative to the user's CURRENT prices, not the original AI output.
  const sliderBaseRef = useRef<AnalyzedLineItem[] | null>(null);
  const baseEstDaysRef = useRef<number>(0);
  const [subtotalSliderVal, setSubtotalSliderVal] = useState<number | null>(null);
  const [customEstDays, setCustomEstDays] = useState<number | null>(null);
  const [editedAssumptions, setEditedAssumptions] = useState<string[]>([]);
  const [categoryPickerIndex, setCategoryPickerIndex] = useState<number | null>(null);
  const [categoryPickerPurpose, setCategoryPickerPurpose] = useState<"add" | "move">("move");
  const [editedExclusions, setEditedExclusions] = useState<string[]>([]);
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);
  const [exclusionsOpen, setExclusionsOpen] = useState(false);
  const [editedPrices, setEditedPrices] = useState<Record<string, { originalPrice: number; currentIndex: number }>>({});
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const isTrialQuotaEnforced = computeIsTrialQuotaEnforced(user);
  const trialBlocked = isTrialBlocked(user);
  const quotaInfo = getQuotaInfo(user as any);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [guestAccessNoticeCode, setGuestAccessNoticeCode] = useState<string | null>(null);
  useEffect(() => {
    if (trialBlocked && !editingSavedQuote) setQuotaExceeded(true);
  }, [trialBlocked, editingSavedQuote]);
  const [upgradeCategory, setUpgradeCategory] = useState("all");
  const [projectDetailsExpanded, setProjectDetailsExpanded] = useState(false);
  const [pricingModeExpanded, setPricingModeExpanded] = useState(false);
  const [descGenError, setDescGenError] = useState<string | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [recoveringQuote, setRecoveringQuote] = useState(false);
  const [generationConnection, setGenerationConnection] = useState<GenerationConnection>("running");
  const [pendingGenerationId, setPendingGenerationId] = useState<string | null>(null);
  const generationController = useRef<AbortController | null>(null);
  const completedGenerationRef = useRef<string | null>(null);
  const autoRecoveryRef = useRef<string | null>(null);
  const activeGenerationRequestRef = useRef<string | null>(null);
  const generationOwnerRef = useRef(user?.id);
  generationOwnerRef.current = user?.id;
  const previousGenerationOwnerRef = useRef(user?.id);
  useEffect(() => {
    if (previousGenerationOwnerRef.current !== user?.id) {
      autoRecoveryRef.current = null;
      completedGenerationRef.current = null;
      activeGenerationRequestRef.current = null;
      setPendingGenerationId(null);
      setAnalysisError(null);
      setAnalysisResult(null);
      setLineItems([]);
      setStep(1);
    }
    previousGenerationOwnerRef.current = user?.id;
    return () => generationController.current?.abort();
  }, [user?.id]);
  const [expandedSuggestions, setExpandedSuggestions] = useState<Set<number>>(new Set());
  const [suggestedItemsExpanded, setSuggestedItemsExpanded] = useState(false);
  const [collapsedStages, setCollapsedStages] = useState<Set<string>>(() => new Set(STAGE_ORDER));
  const toggleStage = (stage: string) => setCollapsedStages(prev => { const n = new Set(prev); n.has(stage) ? n.delete(stage) : n.add(stage); return n; });
  // Service sub-groups (First-Fix Electrics, Second-Fix Electrics, etc.) — collapsed by default.
  // The set stores EXPANDED groups; an absent key means collapsed.
  const [expandedServiceGroups, setExpandedServiceGroups] = useState<Set<string>>(new Set());
  const [summaryMode, setSummaryMode] = useState(false);
  const toggleServiceGroup = (key: string) => setExpandedServiceGroups(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const toggleItem = (index: number) => setExpandedItems(prev => { const n = new Set(prev); n.has(index) ? n.delete(index) : n.add(index); return n; });
  const toggleSuggestion = (i: number) => setExpandedSuggestions(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const [upgradePercent, setUpgradePercent] = useState("0");
  const [upgradeReason, setUpgradeReason] = useState("");
  const [appliedAdjustments, setAppliedAdjustments] = useState<PriceAdjustment[]>([]);
  const [isAdjustmentsExpanded, setIsAdjustmentsExpanded] = useState(false);
  const [isExpertReviewExpanded, setIsExpertReviewExpanded] = useState(false);
  const [isReviewFindingsExpanded, setIsReviewFindingsExpanded] = useState(false);
  const [editingAdjustmentIndex, setEditingAdjustmentIndex] = useState<number | null>(null);
  const [editAdjustmentPercent, setEditAdjustmentPercent] = useState("");
  const [editAdjustmentReason, setEditAdjustmentReason] = useState("");
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<number>>(new Set());
  const [appliedFlags, setAppliedFlags] = useState<Set<number>>(new Set());
  const [addDetailText, setAddDetailText] = useState("");
  const [descriptionFlags, setDescriptionFlags] = useState<any[]>([]);
  const [descriptionWorkItems, setDescriptionWorkItems] = useState<any[]>([]);
  const [preparedQuote, setPreparedQuote] = useState<any>(null);
  const [generatedDescriptionBasis, setGeneratedDescriptionBasis] = useState<string | null>(null);
  const [dimensionEvidence, setDimensionEvidence] = useState<any[]>([]);
  const [measurementReviewFields, setMeasurementReviewFields] = useState<MeasurementReviewField[]>([]);
  const [measurementJobLabel, setMeasurementJobLabel] = useState("Your project");
  const [measurementReviewError, setMeasurementReviewError] = useState<string>();
  const [measurementReviewOpen, setMeasurementReviewOpen] = useState(false);
  const [measurementReviewInlineOpen, setMeasurementReviewInlineOpen] = useState(false);
  const [measurementGenerationRequested, setMeasurementGenerationRequested] = useState(false);
  const [confirmedMeasurementKey, setConfirmedMeasurementKey] = useState<string | null>(null);
  const [measurementReviewSkipped, setMeasurementReviewSkipped] = useState(false);
  const [measurementReviewSubmitting, setMeasurementReviewSubmitting] = useState(false);
  const measurementReviewSubmittingRef = useRef(false);
  const [structuralSummary, setStructuralSummary] = useState<any | null>(null);
  const [simplifiedDescription, setSimplifiedDescription] = useState<string | null>(null);
  const [showSimplified, setShowSimplified] = useState(false);
  const [descriptionIsGenerated, setDescriptionIsGenerated] = useState(false);
  const [descRequiredFlash, setDescRequiredFlash] = useState(false);
  const [descRequiredHint, setDescRequiredHint] = useState(false);
  const [showDescConfirmModal, setShowDescConfirmModal] = useState(false);
  const descConfirmShownRef = useRef(false);
  const descriptionEditTrackedRef = useRef(false);
  const activeDescriptionRequestRef = useRef<DescriptionRequestIdentity | null>(null);
  const descriptionEditRevisionRef = useRef(0);
  const draftStorageWarningRef = useRef(false);
  const descriptionImagesRef = useRef(images);
  descriptionImagesRef.current = images;
  useEffect(() => () => { activeDescriptionRequestRef.current = null; }, []);
  const [appliedDescFlags, setAppliedDescFlags] = useState<Set<number>>(new Set());
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const draftCheckedRef = useRef(false);
  const { toast } = useToast();

  const { data: clients } = useQuery<Client[]>({
    queryKey: ["/api/clients"],
    enabled: !guestMode,
  });

  const { data: adminCheck } = useQuery<{ isAdmin: boolean }>({
    queryKey: ["/api/admin/check"],
    enabled: !guestMode,
  });
  const isAdminUser = adminCheck?.isAdmin === true;

  const { data: guestSamples } = useQuery<{ samples: GuestSample[] }>({
    queryKey: ["/api/guest/samples"],
    enabled: guestMode || firstQuoteMode,
    retry: false,
  });

  const { data: guestStatus, isLoading: guestStatusLoading, refetch: refetchGuestStatus } = useQuery<GuestStatus | null>({
    queryKey: ["/api/guest/status", guestAttemptIdRef.current],
    enabled: guestMode,
    retry: false,
    staleTime: 0,
    queryFn: async () => {
      const response = await fetch(`/api/guest/status?attemptId=${encodeURIComponent(guestAttemptIdRef.current)}`, {
        credentials: "include",
        cache: "no-store",
      });
      if (response.status === 401) return null;
      if (response.status === 404) {
        const error = await parseGuestApiError(response, "Your guest session has expired.");
        if (error.code === "GUEST_SESSION_EXPIRED") setGuestAccessNoticeCode(error.code);
        return null;
      }
      if (!response.ok) throw await parseGuestApiError(response, "Unable to restore guest quote");
      return response.json();
    },
    refetchInterval: (query) => guestMode && query.state.data?.status === "processing" ? 3000 : false,
  });

  const ensureGuestSession = useCallback(async () => {
    if (!guestMode) return;
    if (!guestSessionBootstrapRef.current) {
      const bootstrap = fetch("/api/guest/start", {
        credentials: "include",
        cache: "no-store",
      }).then(async (response) => {
        if (!response.ok) {
          throw await parseGuestApiError(response, "Unable to start your guest quote session.");
        }
      }).catch((error) => {
        // A transient network failure should be retryable, while a signed
        // expired/invalid cookie is surfaced by its specific API code.
        guestSessionBootstrapRef.current = null;
        throw error;
      });
      guestSessionBootstrapRef.current = bootstrap;
    }
    try {
      await guestSessionBootstrapRef.current;
    } catch (error) {
      const code = (error as { code?: string })?.code;
      if (code) setGuestAccessNoticeCode(code);
      throw error;
    }
  }, [guestMode]);

  // Do this on mount rather than waiting for the first submit.  A browser
  // that has no cookie gets the same session used by the later PATCH/analyze
  // calls, while a returning browser's signed cookie resolves to its existing
  // completed/finalized entitlement and is never reset.
  useEffect(() => {
    if (!guestMode) return;
    void ensureGuestSession()
      .then(() => {
        guestStatusHydratedRef.current = false;
        void refetchGuestStatus();
      })
      .catch(() => {
        // The specific API code is rendered by the access banner/toast.
      });
  }, [guestMode, ensureGuestSession, refetchGuestStatus]);

  const approveGuestDescriptionIfChanged = useCallback(async (description: string) => {
    if (!guestMode) return guestDescriptionRevisionRef.current;
    const approved = guestApprovedDescriptionRef.current;
    if (approved && approved.description === description) return approved.revision;

    // PATCH is deliberately bound to the signed guest cookie.  Bootstrap here
    // as well as on mount to cover a fast first interaction before the mount
    // effect has completed.
    await ensureGuestSession();
    const response = await fetch("/api/guest/description", {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attemptId: guestAttemptIdRef.current,
        descriptionRevision: guestDescriptionRevisionRef.current,
        description,
        sampleId: selectedGuestSample?.id,
      }),
    });
    if (response.status === 409) {
      guestStatusHydratedRef.current = false;
      guestDescriptionRefreshRef.current = true;
      await refetchGuestStatus();
      const error = await parseGuestApiError(
        response,
        "This description changed in another tab. We refreshed the approved version; please review it and try again.",
      );
      throw error;
    }
    if (!response.ok) {
      throw await parseGuestApiError(response, "Unable to approve your edited description. Please try again.");
    }
    const payload = await response.json().catch(() => ({}));
    const revision = payload.descriptionRevision ?? payload.revision ?? payload.data?.descriptionRevision;
    if (!Number.isInteger(revision) || revision <= 0) {
      throw new Error("Your edited description was not confirmed. Please try again.");
    }
    const approvedDescription = payload.detailedDescription || payload.description || description;
    guestApprovedDescriptionRef.current = { description: approvedDescription, revision };
    guestDescriptionRevisionRef.current = revision;
    setGuestDescriptionRevision(revision);
    return revision;
  }, [ensureGuestSession, guestMode, refetchGuestStatus, selectedGuestSample]);

  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [pricingMode, setPricingMode] = useState<"standard" | "learned" | "custom">("standard");
  const [useCostLibraryRates, setUseCostLibraryRates] = useState(true);
  const [addedToLibraryItems, setAddedToLibraryItems] = useState<Set<number>>(new Set());
  const [addToCostLibOpen, setAddToCostLibOpen] = useState(false);
  const [addToCostLibItem, setAddToCostLibItem] = useState<AnalyzedLineItem | null>(null);
  const [addToCostLibIndex, setAddToCostLibIndex] = useState<number>(-1);
  const [addToCostLibTrade, setAddToCostLibTrade] = useState("general");
  const [addToCostLibName, setAddToCostLibName] = useState("");
  const [addToCostLibCategory, setAddToCostLibCategory] = useState("");
  const [addToCostLibRate, setAddToCostLibRate] = useState("");
  const [addToCostLibSubcategory, setAddToCostLibSubcategory] = useState("");
  const [addToCostLibUnit, setAddToCostLibUnit] = useState("item");
  const [addToCostLibCategoryPickerOpen, setAddToCostLibCategoryPickerOpen] = useState(false);

  // "Set Price" confirmation dialog state
  const [setPriceOpen, setSetPriceOpen] = useState(false);
  const [setPriceItem, setSetPriceItem] = useState<AnalyzedLineItem | null>(null);
  const [setPriceItemIndex, setSetPriceItemIndex] = useState<number>(-1);
  const [setPriceRateLow, setSetPriceRateLow] = useState("");
  const [setPriceRateTypical, setSetPriceRateTypical] = useState("");
  const [setPriceRateHigh, setSetPriceRateHigh] = useState("");

  // Admin "Publish to Default Library" dialog state
  const [publishToDefaultLibOpen, setPublishToDefaultLibOpen] = useState(false);
  const [publishToDefaultLibItem, setPublishToDefaultLibItem] = useState<AnalyzedLineItem | null>(null);
  const [publishToDefaultLibIndex, setPublishToDefaultLibIndex] = useState<number>(-1);
  const [publishToDefaultLibName, setPublishToDefaultLibName] = useState("");
  const [publishToDefaultLibDescription, setPublishToDefaultLibDescription] = useState("");
  const [publishToDefaultLibCategory, setPublishToDefaultLibCategory] = useState("");
  const [publishToDefaultLibTrade, setPublishToDefaultLibTrade] = useState("general");
  const [publishToDefaultLibSubcategory, setPublishToDefaultLibSubcategory] = useState("");
  const [publishToDefaultLibUnit, setPublishToDefaultLibUnit] = useState("item");
  const [publishToDefaultLibRateLow, setPublishToDefaultLibRateLow] = useState("");
  const [publishToDefaultLibRateTypical, setPublishToDefaultLibRateTypical] = useState("");
  const [publishToDefaultLibRateHigh, setPublishToDefaultLibRateHigh] = useState("");
  const [publishToDefaultLibCategoryPickerOpen, setPublishToDefaultLibCategoryPickerOpen] = useState(false);
  const [publishedToDefaultLibItems, setPublishedToDefaultLibItems] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (addToCostLibItem) {
      const catTrades = COST_TRADES[addToCostLibItem.category];
      setAddToCostLibTrade(catTrades?.trades?.[0]?.value || "general");
    }
  }, [addToCostLibItem]);

  useEffect(() => {
    if (publishToDefaultLibItem) {
      const catTrades = COST_TRADES[publishToDefaultLibItem.category];
      setPublishToDefaultLibTrade(catTrades?.trades?.[0]?.value || "general");
    }
  }, [publishToDefaultLibItem]);

  const savedStep1 = useMemo(() => {
    if (guestMode || editingSavedQuote) return null;
    try {
      const raw = sessionStorage.getItem(STEP1_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as {
          ownerId?: string; description?: string; customerName?: string; projectAddress?: string;
          selectedTypes?: string[]; savedAt?: number; descriptionIsGenerated?: boolean;
          generatedDescriptionBasis?: string | null; confirmedMeasurementKey?: string | null; measurementReviewSkipped?: boolean;
          dimensionEvidence?: any[]; descriptionWorkItems?: any[]; preparedQuote?: any;
          measurementReviewFields?: MeasurementReviewField[]; measurementJobLabel?: string;
        };
        if (parsed.ownerId && parsed.ownerId !== user?.id) return null;
        const lifetime = parsed.ownerId && parsed.descriptionIsGenerated ? 24 * 60 * 60 * 1000 : 30 * 60 * 1000;
        if (parsed.savedAt && Date.now() - parsed.savedAt > lifetime) {
          sessionStorage.removeItem(STEP1_STORAGE_KEY);
          return null;
        }
        return parsed;
      }
    } catch {}
    return null;
  }, [guestMode, editingSavedQuote, user?.id]);

  const form = useForm<ProjectFormData>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      clientId: "",
      customerName: savedStep1?.customerName || "",
      projectAddress: savedStep1?.projectAddress || "",
      description: savedStep1?.description || "",
      projectTypes: [],
    },
  });
  useEffect(() => {
    if (guestMode || editingSavedQuote || !user?.id || savedStep1?.ownerId !== user.id) return;
    form.reset({ ...form.getValues(), description: savedStep1.description || "",
      customerName: savedStep1.customerName || "", projectAddress: savedStep1.projectAddress || "" });
    setDescriptionIsGenerated(descriptionRemainsReady(!!savedStep1.descriptionIsGenerated, savedStep1.description || ""));
    setGeneratedDescriptionBasis(savedStep1.generatedDescriptionBasis ?? null);
    setPreparedQuote(savedStep1.preparedQuote ?? null);
    setDescriptionWorkItems(savedStep1.descriptionWorkItems ?? []);
    setDimensionEvidence(savedStep1.dimensionEvidence ?? []);
    setMeasurementReviewFields(savedStep1.measurementReviewFields ?? []);
    setMeasurementJobLabel(savedStep1.measurementJobLabel ?? "Your project");
    setConfirmedMeasurementKey(savedStep1.confirmedMeasurementKey ?? null);
    setMeasurementReviewSkipped(savedStep1.measurementReviewSkipped ?? false);
  }, [savedStep1, user?.id, guestMode, editingSavedQuote, form]);

  useEffect(() => {
    let previous = form.getValues("description") || "";
    const subscription = form.watch(values => {
      const next = values.description || "";
      if (next !== previous) {
        descriptionEditRevisionRef.current++;
        previous = next;
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);
  const applyDescriptionEdit = (description: string) => {
    if (descriptionIsGenerated && generatedDescriptionBasis === null) {
      setGeneratedDescriptionBasis(form.getValues("description") || "");
    }
    form.setValue("description", description, { shouldDirty: true });
    setDescriptionIsGenerated(ready => descriptionRemainsReady(ready, description));
    setSimplifiedDescription(null);
    setShowSimplified(false);
    if (descriptionIsGenerated && !descriptionEditTrackedRef.current && !guestMode) {
      descriptionEditTrackedRef.current = true;
      trackEvent("generated_description_edited", "/quotes/new");
    }
    if (!description.trim()) {
      setGeneratedDescriptionBasis(null);
      setPreparedQuote(null);
      setDescriptionWorkItems([]);
      setDimensionEvidence([]);
      setMeasurementReviewFields([]);
      setConfirmedMeasurementKey(null);
      setMeasurementReviewSkipped(false);
      setMeasurementReviewOpen(false);
      setMeasurementReviewInlineOpen(false);
      setMeasurementGenerationRequested(false);
    }
  };
  useEffect(() => {
    if (!editingSavedQuote || !editingQuote || editHydrated) return;
    const restored = savedQuoteEditorState(editingQuote);
    setLineItems(restored.lines);
    baseLineItemsRef.current = restored.lines;
    baseEstDaysRef.current = restored.analysis.estimatedDays;
    setAnalysisResult(restored.analysis);
    setEditedAssumptions(restored.analysis.assumptions);
    setEditedExclusions(restored.analysis.exclusions);
    setIncludeVat(editingQuote.includeVat ?? false);
    setSummaryMode(editingQuote.summaryMode ?? false);
    setCustomEstDays(editingQuote.estimatedDays ?? null);
    setContingencyPercent(restored.contingencyPercent);
    setOverheadPercent(restored.overheadPercent);
    setProfitPercent(restored.profitPercent);
    setContingencyEnabled(Number(restored.contingencyPercent) !== 0);
    setOverheadEnabled(Number(restored.overheadPercent) !== 0);
    setProfitEnabled(Number(restored.profitPercent) !== 0);
    setTermsConditions(editingQuote.termsConditions ?? "");
    setPaymentTerms(editingQuote.paymentTerms ?? "");
    form.reset({ clientId: editingQuote.clientId ?? "", customerName: "",
      projectAddress: editingQuote.projectAddress ?? "", description: editingQuote.description ?? "", projectTypes: [] });
    setStep(2);
    setEditHydrated(true);
  }, [editingSavedQuote, editingQuote, editHydrated, form]);
  const fixedQuoteExample = isFixedQuoteExample(guestMode || firstQuoteMode, images.length,
    form.watch("description") || "", guestSamples?.samples);
  const currentMeasurementFields = resolveMeasurementReviewFields(measurementReviewFields, dimensionEvidence,
    [form.watch("description") || "", ...selectedTypes]);
  const activeQuoteProgressStep = step === 1 ? (descriptionIsGenerated && !fixedQuoteExample ? 2 : 1) : step + 1;
  const generatedDescriptionEdited = descriptionIsGenerated && generatedDescriptionBasis !== null
    && form.watch("description") !== generatedDescriptionBasis;

  // ── Voice-to-description ─────────────────────────────────────────────────
  const [isDescRecording, setIsDescRecording] = useState(false);
  const [isDescTranscribing, setIsDescTranscribing] = useState(false);
  const descMediaRecorderRef = useRef<MediaRecorder | null>(null);
  const descAudioChunksRef = useRef<Blob[]>([]);

  const startDescRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";
      const recorder = new MediaRecorder(stream, { mimeType });
      descAudioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) descAudioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(descAudioChunksRef.current, { type: mimeType });
        setIsDescTranscribing(true);
        try {
          const base64: string = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              const idx = result.indexOf(",");
              resolve(idx >= 0 ? result.slice(idx + 1) : result);
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
          });
          const res = await fetch("/api/audio/transcribe", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ audio: base64 }),
          });
          if (!res.ok) throw new Error("Transcription failed");
          const { text } = await res.json();
          if (text?.trim()) {
            const current = form.getValues("description") || "";
            const separator = current.trim() ? "\n" : "";
            applyDescriptionEdit(current + separator + text.trim());
          }
        } catch {
          toast({ title: "Could not transcribe audio", description: "Please try again", variant: "destructive" });
        } finally {
          setIsDescTranscribing(false);
        }
      };
      recorder.start();
      descMediaRecorderRef.current = recorder;
      setIsDescRecording(true);
    } catch {
      toast({ title: "Microphone not available", description: "Please allow microphone access and try again", variant: "destructive" });
    }
  }, [form, toast]);

  const stopDescRecording = useCallback(() => {
    descMediaRecorderRef.current?.stop();
    descMediaRecorderRef.current = null;
    setIsDescRecording(false);
  }, []);

  useEffect(() => {
    if (guestMode || editingSavedQuote) return;
    if (savedStep1?.selectedTypes?.length) {
      setSelectedTypes(savedStep1.selectedTypes);
    }
    // Skip restoring stale step-1 files when we are landing here from the
    // Site Survey handover — those would race with (and overwrite) the
    // freshly imported survey files. The handover effect below will own
    // the upload tray in that case.
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") === "site-survey") return;
    loadFilesFromDB().then((saved) => {
      if (saved.length > 0) {
        const restored: UploadedImage[] = saved.map((f) => ({
          id: f.id,
          base64: f.base64,
          mimeType: f.mimeType,
          preview: f.preview,
          file: new File([], f.fileName || "file", { type: f.mimeType }),
        }));
        setImages(restored);
      }
    });
  }, [savedStep1, guestMode, editingSavedQuote]);

  // ── Site Survey handover ────────────────────────────────────────────────
  // When the builder presses "Send to Auto Quote" inside Site Survey, we
  // stash a handover blob in sessionStorage and navigate here. Pull the PDF
  // report into the upload tray and pre-fill the address/description fields.
  // Note: we deliberately do NOT pull the room photos in separately — the
  // report PDF already embeds every photo at full resolution, so attaching
  // them again would mean Auto Quote sees each image twice. The
  // `photoObjectPaths` field is kept optional below for backward compatibility
  // with any handover blobs already in sessionStorage from before this change.
  const surveyHandoverConsumedRef = useRef(false);
  useEffect(() => {
    if (guestMode || editingSavedQuote) return;
    if (surveyHandoverConsumedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") !== "site-survey") return;
    const raw = sessionStorage.getItem("site-survey-handover");
    if (!raw) return;
    surveyHandoverConsumedRef.current = true;
    sessionStorage.removeItem("site-survey-handover");
    let payload: {
      surveyId?: string;
      reportObjectPath?: string;
      reportFileName?: string;
      photoObjectPaths?: string[];
      project?: {
        clientName?: string;
        projectAddress?: string;
        projectType?: string;
        description?: string;
      };
    } | null = null;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    if (!payload) return;

    if (payload.project?.projectAddress) {
      form.setValue("projectAddress", payload.project.projectAddress);
    }
    if (payload.project?.clientName) {
      form.setValue("customerName", payload.project.clientName);
    }
    if (payload.project?.description) {
      form.setValue(
        "description",
        `From Site Visit Report: ${payload.project.description}`
      );
    }

    // Fetch the PDF + photos and convert to UploadedImage entries
    const paths = [
      ...(payload.reportObjectPath ? [{ path: payload.reportObjectPath, name: payload.reportFileName || "site-visit-report.pdf" }] : []),
      ...(payload.photoObjectPaths || []).map((p, i) => ({ path: p, name: `site-photo-${i + 1}.jpg` })),
    ];
    if (paths.length === 0) return;

    (async () => {
      const loaded: UploadedImage[] = [];
      for (const { path, name } of paths) {
        try {
          const res = await fetch(path);
          if (!res.ok) continue;
          const blob = await res.blob();
          const file = new File([blob], name, { type: blob.type || "application/octet-stream" });
          const base64: string = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              // Strip the "data:...;base64," prefix to match fileToBase64 contract
              const idx = result.indexOf(",");
              resolve(idx >= 0 ? result.slice(idx + 1) : result);
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
          });
          const preview = file.type.startsWith("image/") ? URL.createObjectURL(blob) : "";
          loaded.push({
            id: Math.random().toString(36).slice(2),
            file,
            preview,
            base64,
            mimeType: file.type || "application/octet-stream",
          });
        } catch {
          // skip files that fail to load
        }
      }
      if (loaded.length > 0) {
        setImages((prev) => [...prev, ...loaded].slice(0, 10));
        toast({
          title: "Site visit imported",
          description: `${loaded.length} file${loaded.length === 1 ? "" : "s"} from your survey added below`,
        });
      }
      // Clean URL so a refresh won't re-trigger
      window.history.replaceState({}, "", window.location.pathname);
    })();
  }, [form, toast, guestMode, editingSavedQuote]);

  useEffect(() => {
    if (guestMode || editingSavedQuote || step !== 1) return;
    saveFilesToDB(images);
  }, [images, step, guestMode, editingSavedQuote]);

  useEffect(() => {
    if (guestMode || editingSavedQuote || step !== 1 || !user?.id) return;
    const persist = () => {
      const values = form.getValues();
      try {
        sessionStorage.setItem(STEP1_STORAGE_KEY, JSON.stringify({
          ownerId: user.id,
          description: values.description || "",
          customerName: values.customerName || "",
          projectAddress: values.projectAddress || "",
          selectedTypes,
          descriptionIsGenerated, generatedDescriptionBasis, preparedQuote, descriptionWorkItems,
          dimensionEvidence, measurementReviewFields, measurementJobLabel, confirmedMeasurementKey, measurementReviewSkipped,
          savedAt: Date.now(),
        }));
      } catch {
        if (!draftStorageWarningRef.current) {
          draftStorageWarningRef.current = true;
          toast({ title: "Progress could not be kept in this browser", description: "Your work is still on this page. Keep this tab open until you finish your quotation." });
          trackEvent("project_draft_save_failed", "/quotes/new");
        }
      }
    };
    persist();
    const subscription = form.watch(persist);
    return () => subscription.unsubscribe();
  }, [form, step, selectedTypes, guestMode, editingSavedQuote, user?.id,
    descriptionIsGenerated, generatedDescriptionBasis, preparedQuote, descriptionWorkItems,
    dimensionEvidence, measurementReviewFields, measurementJobLabel, confirmedMeasurementKey, measurementReviewSkipped, toast]);

  const restoreDraft = useCallback((draft: DraftQuoteState) => {
    setDimensionEvidence(draft.dimensionEvidence ?? []);
    setDescriptionWorkItems(draft.descriptionWorkItems ?? []);
    setPreparedQuote(draft.preparedQuote ?? null);
    setGeneratedDescriptionBasis(draft.generatedDescriptionBasis ?? draft.preparedQuote?.description ?? null);
    setConfirmedMeasurementKey(draft.confirmedMeasurementKey ?? null);
    setMeasurementReviewSkipped(draft.measurementReviewSkipped ?? false);
    setMeasurementReviewFields(simplifyMeasurementReviewFields(draft.measurementReviewFields ?? [],
      [draft.formValues?.description ?? "", draft.measurementJobLabel ?? ""]));
    setMeasurementJobLabel(draft.measurementJobLabel ?? "Your project");
    setDescriptionIsGenerated(draft.descriptionIsGenerated ?? Boolean(draft.formValues?.description));
    setAnalysisResult(draft.analysisResult);
    setLineItems(draft.lineItems.map(normalizeExtractorClassification));
    baseLineItemsRef.current = draft.baseLineItems.map(normalizeExtractorClassification);
    baseEstDaysRef.current = draft.baseEstDays;
    setIncludeVat(draft.includeVat);
    setContingencyPercent(draft.contingencyPercent);
    setOverheadPercent(draft.overheadPercent);
    setProfitPercent(draft.profitPercent);
    setContingencyEnabled(draft.contingencyEnabled);
    setOverheadEnabled(draft.overheadEnabled);
    setProfitEnabled(draft.profitEnabled);
    setCustomEstDays(draft.customEstDays);
    setEditedAssumptions(draft.editedAssumptions);
    setEditedExclusions(draft.editedExclusions);
    setEditedPrices(draft.editedPrices);
    setAppliedAdjustments(draft.appliedAdjustments);
    setDismissedSuggestions(new Set(draft.dismissedSuggestions));
    setAppliedFlags(new Set(draft.appliedFlags));
    setAppliedDescFlags(new Set(draft.appliedDescFlags));
    setDescriptionFlags(draft.descriptionFlags);
    setStructuralSummary(draft.structuralSummary);
    setSelectedTypes(draft.selectedTypes);
    setPricingMode(draft.pricingMode);
    if (draft.formValues) {
      form.setValue("clientId", draft.formValues.clientId || "");
      form.setValue("customerName", draft.formValues.customerName || "");
      form.setValue("projectAddress", draft.formValues.projectAddress || "");
      form.setValue("description", draft.formValues.description || "");
    }
    setStep(draft.step);
    setShowDraftBanner(false);
  }, [form]);

  useEffect(() => {
    if (guestMode || editingSavedQuote || !user?.id) return;
    if (draftCheckedRef.current) return;
    draftCheckedRef.current = true;

    const fullDraft = loadDraftFromStorage();
    if (fullDraft && fullDraft.analysisResult && fullDraft.lineItems.length > 0) {
      // An older unsaved draft is not the result of a newer interrupted request.
      let pending: PendingQuoteGeneration | null;
      try { pending = loadPendingGeneration(user.id); }
      catch { return; } // The recovery effect displays corrupt-reference errors.
      if (pending && !isCompletedGeneration(pending)) return;
      if (pending?.generationLogId && pending.generationLogId !== fullDraft.analysisResult.generationLogId) return;
      const params = new URLSearchParams(window.location.search);
      const wasActiveSession = sessionStorage.getItem("tpt_quote_active") === "1";
      const navigation = performance.getEntriesByType?.("navigation")[0] as PerformanceNavigationTiming | undefined;
      if (params.get("resume") === "1" || (wasActiveSession && ["reload", "back_forward"].includes(navigation?.type || ""))) {
        if (pending) {
          activeGenerationRequestRef.current = pending.requestId;
          completedGenerationRef.current = pending.requestId;
          autoRecoveryRef.current = pending.requestId;
          setPendingGenerationId(pending.requestId);
        }
        restoreDraft(fullDraft);
        if (params.get("resume") === "1") {
          window.history.replaceState({}, "", window.location.pathname);
        }
        if (wasActiveSession) {
          toast({ title: "Quote restored", description: "Your review estimate has been recovered" });
        }
      } else {
        setShowDraftBanner(true);
      }
      return;
    }
  }, [restoreDraft, form, toast, guestMode, user?.id]);

  useEffect(() => {
    if (!guestMode || guestStatusLoading || guestStatusHydratedRef.current || !guestStatus) return;
    const status = guestStatus as any;
    if (status.simplifiedDescription) {
      setSimplifiedDescription(status.simplifiedDescription);
    }
    const restoredPricing = status.pricing
      || status.result?.pricing
      || status.snapshot?.pricing
      || status.draft?.pricing
      || status.draftState?.pricing
      || status.finalizedSnapshot?.pricing;
    if (restoredPricing) applyGuestPricing(restoredPricing);
    if (status.detailedDescription && !form.getValues("description")) {
      form.setValue("description", status.detailedDescription);
      setDescriptionIsGenerated(true);
      descriptionEditTrackedRef.current = false;
      setDescriptionFlags(status.flags || []);
      setDescriptionWorkItems(status.workItems || []);
      setDimensionEvidence(status.dimensionEvidence || []);
      const review = createMeasurementReview(status.dimensionEvidence || [], [status.detailedDescription],
        status.measurementSubjects || []);
      setMeasurementReviewFields(review.fields);
      setMeasurementJobLabel(review.jobLabel);
      setDimensionEvidence(status.dimensionEvidence || []);
      setStructuralSummary(status.structuralSummary || null);
    } else if (status.detailedDescription && guestDescriptionRefreshRef.current) {
      // A stale-tab response explicitly asks us to replace the old local
      // prose. Initial hydration remains non-destructive so a user who is
      // already typing is not overwritten by the first status request.
      form.setValue("description", status.detailedDescription);
      setDescriptionIsGenerated(true);
      setDescriptionFlags(status.flags || []);
      setDescriptionWorkItems(status.workItems || []);
      setStructuralSummary(status.structuralSummary || null);
      guestDescriptionRefreshRef.current = false;
    }
    if (typeof status.descriptionRevision === "number") {
      guestDescriptionRevisionRef.current = status.descriptionRevision;
      setGuestDescriptionRevision(status.descriptionRevision);
    }
    if (typeof status.detailedDescription === "string" && typeof status.descriptionRevision === "number") {
      guestApprovedDescriptionRef.current = {
        description: status.detailedDescription,
        revision: status.descriptionRevision,
      };
    }
    const restoredSampleId = status.inputMetadata?.sampleId;
    if (restoredSampleId && guestSamples?.samples) {
      setSelectedGuestSample(guestSamples.samples.find(sample => sample.id === restoredSampleId) || null);
    }
    const snapshot = status.snapshot || status.draft || status.draftState || status.finalSnapshot || status.finalizedSnapshot || status.state;
    const restored = snapshot?.analysisResult ? snapshot : (
      status.analysisResult || status.result ? {
        ...(snapshot || {}),
        analysisResult: snapshot?.analysisResult || status.analysisResult || unwrapGuestResult(status.result),
        lineItems: snapshot?.lineItems || unwrapGuestResult(status.result)?.lineItems || [],
      } : null
    );
    if (!restored?.analysisResult || !restored.lineItems?.length) {
      // A completed/finalized row is still consumed even if an older or
      // partial result has no line items to render. Do not leave the visitor
      // on the input form where a second submit would end in a misleading
      // description-approval error.
      if (["completed", "finalized", "claimed", "failed"].includes(status.status)) {
        setGuestAccessNoticeCode("GUEST_REGISTRATION_REQUIRED");
        guestStatusHydratedRef.current = true;
      }
      return;
    }
    guestStatusHydratedRef.current = true;
    const normalizedDraft: DraftQuoteState = {
      dimensionEvidence: restored.dimensionEvidence ?? status.dimensionEvidence ?? [],
      descriptionWorkItems: restored.descriptionWorkItems ?? status.workItems ?? [],
      measurementReviewFields: restored.measurementReviewFields ?? [],
      measurementJobLabel: restored.measurementJobLabel,
      descriptionIsGenerated: restored.descriptionIsGenerated,
      step: status.status === "finalized" || status.status === "claimed"
        ? 3
        : status.status === "completed"
        ? 2
        : (restored.step || 2),
      analysisResult: restored.analysisResult,
      lineItems: restored.lineItems,
      baseLineItems: restored.baseLineItems || restored.lineItems,
      baseEstDays: restored.baseEstDays || restored.analysisResult.estimatedDays || 0,
      formValues: restored.formValues || {
        customerName: "",
        projectAddress: "",
        description: status.detailedDescription || restored.analysisResult.generatedDescription || "",
      },
      selectedTypes: restored.selectedTypes || [],
      includeVat: restored.includeVat ?? restoredPricing?.includeVat ?? true,
      contingencyPercent: restored.contingencyPercent ?? (restoredPricing?.contingencyPercent != null ? String(restoredPricing.contingencyPercent) : "0"),
      overheadPercent: restored.overheadPercent ?? (restoredPricing?.overheadPercent != null ? String(restoredPricing.overheadPercent) : "0"),
      profitPercent: restored.profitPercent ?? (restoredPricing?.profitPercent != null ? String(restoredPricing.profitPercent) : "0"),
      contingencyEnabled: restored.contingencyEnabled ?? Number(restoredPricing?.contingencyPercent || 0) > 0,
      overheadEnabled: restored.overheadEnabled ?? Number(restoredPricing?.overheadPercent || 0) > 0,
      profitEnabled: restored.profitEnabled ?? Number(restoredPricing?.profitPercent || 0) > 0,
      customEstDays: restored.customEstDays ?? null,
      editedAssumptions: restored.editedAssumptions || restored.analysisResult.assumptions || [],
      editedExclusions: restored.editedExclusions || restored.analysisResult.exclusions || [],
      editedPrices: restored.editedPrices || {},
      appliedAdjustments: restored.appliedAdjustments || [],
      dismissedSuggestions: restored.dismissedSuggestions || [],
      appliedFlags: restored.appliedFlags || [],
      appliedDescFlags: restored.appliedDescFlags || [],
      descriptionFlags: restored.descriptionFlags || [],
      structuralSummary: restored.structuralSummary || null,
      pricingMode: restored.pricingMode || "standard",
      savedAt: Date.now(),
    };
    restoreDraft(normalizedDraft);
    setDescriptionIsGenerated(Boolean(restored.formValues?.description));
    setGuestRevision(typeof status.revision === "number" ? status.revision : (status.draftRevision || 0));
    const restoredDescriptionRevision = typeof status.descriptionRevision === "number" ? status.descriptionRevision : 0;
    guestDescriptionRevisionRef.current = restoredDescriptionRevision;
    setGuestDescriptionRevision(restoredDescriptionRevision);
    setGuestFinalized(status.status === "finalized" || status.status === "claimed");
  }, [guestMode, guestStatusLoading, guestStatus, guestSamples, restoreDraft, form, applyGuestPricing]);


  const saveDraftState = useCallback(() => {
    if (editingSavedQuote || !analysisResult || lineItems.length === 0) return;
    const draft: DraftQuoteState = {
      dimensionEvidence, descriptionWorkItems, preparedQuote, generatedDescriptionBasis, confirmedMeasurementKey, measurementReviewSkipped, measurementReviewFields, measurementJobLabel, descriptionIsGenerated,
      step,
      analysisResult,
      lineItems,
      baseLineItems: baseLineItemsRef.current,
      baseEstDays: baseEstDaysRef.current,
      formValues: form.getValues(),
      selectedTypes,
      includeVat,
      contingencyPercent,
      overheadPercent,
      profitPercent,
      contingencyEnabled,
      overheadEnabled,
      profitEnabled,
      customEstDays,
      editedAssumptions,
      editedExclusions,
      editedPrices,
      appliedAdjustments,
      dismissedSuggestions: Array.from(dismissedSuggestions),
      appliedFlags: Array.from(appliedFlags),
      appliedDescFlags: Array.from(appliedDescFlags),
      descriptionFlags,
      structuralSummary,
      pricingMode,
      savedAt: Date.now(),
    };
    // Keep a recoverable reference, but distinguish delivered results from work
    // still in progress so the next project can start with a fresh request.
    if (saveDraftToStorage(draft) && user?.id && completedGenerationRef.current) {
      try { markGenerationDelivered(user.id, completedGenerationRef.current); }
      catch { /* Keep the existing durable recovery reference if storage fills. */ }
    }
  }, [
    editingSavedQuote, step, analysisResult, lineItems, includeVat,
    contingencyPercent, overheadPercent, profitPercent,
    contingencyEnabled, overheadEnabled, profitEnabled,
    customEstDays, editedAssumptions, editedExclusions, editedPrices,
    appliedAdjustments, dismissedSuggestions, appliedFlags,
    appliedDescFlags, descriptionFlags, structuralSummary,
    selectedTypes, pricingMode, form, dimensionEvidence, descriptionWorkItems, preparedQuote, generatedDescriptionBasis, confirmedMeasurementKey, measurementReviewSkipped, measurementReviewFields, measurementJobLabel, descriptionIsGenerated, guestMode, user?.id,
  ]);

  useEffect(() => {
    if (guestMode || editingSavedQuote) return;
    if (step >= 2 && analysisResult) {
      sessionStorage.setItem("tpt_quote_active", "1");
      saveDraftState();
    } else if (step === 1) {
      sessionStorage.removeItem("tpt_quote_active");
    }
  }, [step, analysisResult, lineItems, saveDraftState, guestMode, editingSavedQuote]);

  const buildGuestSnapshot = useCallback(() => ({
    dimensionEvidence, descriptionWorkItems, generatedDescriptionBasis, confirmedMeasurementKey, measurementReviewSkipped, measurementReviewFields, measurementJobLabel, descriptionIsGenerated,
    step,
    analysisResult,
    lineItems,
    baseLineItems: baseLineItemsRef.current,
    baseEstDays: baseEstDaysRef.current,
    formValues: {
      customerName: form.getValues("customerName") || "",
      projectAddress: form.getValues("projectAddress") || "",
      description: form.getValues("description") || "",
      projectTypes: selectedTypes,
    },
    selectedTypes,
    includeVat,
    contingencyPercent,
    overheadPercent,
    profitPercent,
    contingencyEnabled,
    overheadEnabled,
    profitEnabled,
    customEstDays,
    editedAssumptions,
    editedExclusions,
    appliedAdjustments,
    dismissedSuggestions: Array.from(dismissedSuggestions),
    appliedFlags: Array.from(appliedFlags),
    appliedDescFlags: Array.from(appliedDescFlags),
    descriptionFlags,
    structuralSummary,
    pricingMode,
    summaryMode,
  }), [
    step, analysisResult, lineItems, form, selectedTypes, includeVat,
    contingencyPercent, overheadPercent, profitPercent, contingencyEnabled,
    overheadEnabled, profitEnabled, customEstDays, editedAssumptions,
    editedExclusions, appliedAdjustments, dismissedSuggestions, appliedFlags,
    appliedDescFlags, descriptionFlags, structuralSummary, pricingMode,
    summaryMode, dimensionEvidence, descriptionWorkItems, generatedDescriptionBasis, confirmedMeasurementKey, measurementReviewSkipped, measurementReviewFields, measurementJobLabel, descriptionIsGenerated,
  ]);

  const analyzeProjectMutation = useMutation({
    mutationFn: async (data: ProjectFormData & { guestDescriptionRevision?: number; measurementReview?: MeasurementReview; recoveryRequestId?: string; generationRequestId?: string; generationOwnerId?: string; generationStarted?: boolean }) => {
      setAnalysisError(null);
      setRecoveringQuote(!!data.recoveryRequestId);
      const requestId = data.recoveryRequestId || crypto.randomUUID();
      const owner = user?.id;
      data.generationRequestId = requestId;
      data.generationOwnerId = owner;
      const payload = {
        ...(guestMode ? {
          attemptId: guestAttemptIdRef.current,
          descriptionRevision: data.guestDescriptionRevision ?? guestDescriptionRevisionRef.current,
        } : {}),
        ...(testQuoteGenerationSource ? {
          generationSource: testQuoteGenerationSource,
        } : {}),
        images: images.map(img => ({
          base64: img.base64,
          mimeType: img.mimeType,
          name: img.file?.name || "drawing",
        })),
        description: data.description || "",
        projectTypes: selectedTypes,
        customerName: data.customerName,
        projectAddress: data.projectAddress,
        pricingMode,
        useCostLibraryRates,
        ...descriptionPricingInputs(data.description || "", generatedDescriptionBasis,
          descriptionWorkItems, guestMode ? undefined : preparedQuote),
        dimensionEvidence,
        measurementReview: data.measurementReview,
      };
      try {
        if (!guestMode) {
          if (!owner) throw new Error("Please sign in before generating your quotation.");
          const existing = loadPendingGeneration(owner);
          if (!data.recoveryRequestId && existing && !isCompletedGeneration(existing)) {
            setPendingGenerationId(existing.requestId);
            throw Object.assign(new Error("An earlier quotation still needs checking. Recover it before starting another."),
              { code: "RECOVERY_UNCONFIRMED" });
          }
          if (data.recoveryRequestId && existing?.requestId !== requestId) {
            throw Object.assign(new Error("The saved generation reference has changed. Please reload this page."),
              { code: "RECOVERY_UNCONFIRMED" });
          }
          if (!data.recoveryRequestId) {
            beginGeneration(owner, { requestId, startedAt: Date.now(),
            formValues: { description: data.description, customerName: data.customerName, projectAddress: data.projectAddress },
            reviewSettings: { includeVat, contingencyPercent, overheadPercent, profitPercent,
              contingencyEnabled, overheadEnabled, profitEnabled } });
            completedGenerationRef.current = null;
            setShowDraftBanner(false);
            setAnalysisResult(null);
            setLineItems([]);
            try {
              sessionStorage.removeItem(DRAFT_STORAGE_KEY);
              sessionStorage.removeItem("tpt_quote_active");
            } catch { /* The new request reference takes precedence over an old cached draft. */ }
          }
          setPendingGenerationId(requestId);
          activeGenerationRequestRef.current = requestId;
          data.generationStarted = true;
          autoRecoveryRef.current = requestId;
          generationController.current?.abort();
          const controller = new AbortController();
          generationController.current = controller;
          const result = await runBackgroundQuote<QuoteAnalysisResult>({
            signal: controller.signal,
            onConnection: state => {
              setGenerationConnection(state);
              setRecoveringQuote(!!data.recoveryRequestId || state !== "running");
            },
            start: data.recoveryRequestId ? undefined : async signal => {
              trackEvent("quote_request_started", "/quotes/new", { requestId });
              const response = await fetch("/api/analyze-project", {
                method: "POST", credentials: "include", signal,
                headers: { "Content-Type": "application/json", "X-Request-Id": requestId },
                body: JSON.stringify(payload),
              });
              if (!response.ok) {
                const error = await parseGuestApiError(response, "The quotation request was not accepted.");
                throw Object.assign(error, { generationTerminal: error.code !== "QUOTE_GENERATION_EXISTS" });
              }
              const body = JSON.parse((await response.text()).trim());
              if (body.error) throw Object.assign(new Error(body.message || "The quotation could not be generated."),
                { code: body.code || "GENERATION_FAILED", generationTerminal: true });
              if (!Array.isArray(body.lineItems)) throw new SyntaxError("The quotation response was incomplete.");
              return body;
            },
            lookup: async signal => {
              const response = await fetch(`/api/quote-generation-results/${requestId}`, { credentials: "include", signal, cache: "no-store" });
              if (response.status === 503) throw new TypeError("Failed to fetch generation status");
              if (!response.ok) throw Object.assign(
                new Error("We could not check this quotation. Please sign in again and reopen this page."),
                { code: "RECOVERY_UNCONFIRMED" });
              return response.json();
            },
          });
          if (!isCurrentGeneration(requestId, owner, activeGenerationRequestRef.current, generationOwnerRef.current) || controller.signal.aborted) {
            throw Object.assign(new Error("Generation checking paused."), { code: "RECOVERY_PAUSED" });
          }
          completedGenerationRef.current = requestId;
          const pending = loadPendingGeneration(owner);
          if (pending?.requestId === requestId && result.generationLogId) {
            try { rememberGeneration(owner, { ...pending, generationLogId: result.generationLogId }); }
            catch { /* The original reference is already durable; server recovery still works. */ }
          }
          return result;
        }
        return await withQuoteAnalysisDeadline(async signal => recoverQuoteResponse(async () => {
          const response = await fetch(guestMode ? "/api/guest/analyze-project" : "/api/analyze-project", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json", "X-Request-Id": requestId },
            body: JSON.stringify(payload),
            signal,
          });
          if (response.status === 401) {
            throw await parseGuestApiError(response, "Your guest session has expired.");
          }
          if (!response.ok) {
            throw await parseGuestApiError(response, "Quote generation could not continue");
          }
          const text = await response.text();
          const result = JSON.parse(text.trim());
          if (result.error) {
            throw Object.assign(new Error(result.message || "Failed to analyze project"), { code: result.code });
          }
          return result as QuoteAnalysisResult;
        }, async () => {
          // Guest sessions already have a separate saved-result/recovery flow.
          if (guestMode) throw new Error("Connection lost. Please reload to recover your test quote.");
          const response = await fetch(`/api/quote-generation-results/${requestId}`, { credentials: "include", signal });
          if (response.status === 202 || response.status === 503) return null;
          if (!response.ok) throw await parseGuestApiError(response, "Could not recover your quotation. Please sign in again.");
          const recovered = await response.json();
          if (recovered.status !== "completed" || !Array.isArray(recovered.result?.lineItems)) {
            throw new Error("The saved quotation could not be read. Please contact us rather than starting another generation.");
          }
          return recovered.result as QuoteAnalysisResult;
        }, signal, () => setRecoveringQuote(true)));
      } catch (err: any) {
        if (!guestMode && (generationOwnerRef.current !== owner || generationController.current?.signal.aborted)) {
          throw Object.assign(new Error("Generation checking paused."), { code: "RECOVERY_PAUSED" });
        }
        if (err.name === "AbortError") {
          if (!guestMode) throw Object.assign(new Error("Generation checking paused. Reopen this page to recover the same quotation."),
            { code: "RECOVERY_PAUSED" });
          throw new Error("We could not confirm the quotation result within the time limit. Your inputs are kept on this page. The request may still finish; please check before starting another generation.");
        }
        if (err.message === "Failed to fetch" || err.message?.includes("NetworkError")) {
          throw new Error("Connection lost during analysis. Your files and description are saved — please try again.");
        }
        throw err;
      } finally {
        if (guestMode || isCurrentGeneration(requestId, owner, activeGenerationRequestRef.current, generationOwnerRef.current))
          setRecoveringQuote(false);
      }
    },
    onSuccess: (result, variables) => {
      if (!guestMode && !isCurrentGeneration(variables.generationRequestId, variables.generationOwnerId,
        activeGenerationRequestRef.current, generationOwnerRef.current)) return;
      const normalizedResult = guestMode ? unwrapGuestResult(result) : result;
      if (!guestMode) trackEvent("quote_result_accepted", "/quotes/new", { requestId: variables.generationRequestId });
      if (guestMode && (!normalizedResult || !Array.isArray(normalizedResult.lineItems))) {
        if (typeof (result as any)?.revision === "number") setGuestRevision((result as any).revision);
        void refetchGuestStatus();
        return;
      }
      if (guestMode && normalizedResult?.pricing) {
        applyGuestPricing(normalizedResult.pricing);
      }
      setAnalysisResult(normalizedResult);
      // Reset the scope tab to the Combined view on every fresh analysis so a
      // previously-selected (now-nonexistent) scope tab never leaves the review
      // filtered to an empty stale scope.
      setActiveScopeTab("__combined__");
      const itemsWithSubcategory = normalizedResult.lineItems.map((li: AnalyzedLineItem) => {
        let category = li.category;
        if (isRoofConceptFrontend(li.name, li.description) && category !== "roofing") {
          category = "roofing";
        }
        return normalizeExtractorClassification({
          ...li,
          category,
          subcategory: li.subcategory || detectSubcategory(li.name, li.description, category, li.trade),
        });
      });
      const sortedItems = [...itemsWithSubcategory].sort((a, b) => {
        const prioA = getItemSortPriority(a.category, a.name);
        const prioB = getItemSortPriority(b.category, b.name);
        return prioA - prioB;
      });
      setLineItems(sortedItems);
      setCollapsedStages(new Set(STAGE_ORDER));
      setEditedAssumptions([...getStandardAssumptions(user?.market)]);
      setEditedExclusions([...getStandardExclusions(user?.market)]);
      baseLineItemsRef.current = sortedItems.map((item: AnalyzedLineItem) => ({ ...item }));
      sliderBaseRef.current = null; // reset so slider re-anchors to new prices
      baseEstDaysRef.current = normalizedResult.estimatedDays;
      setEditedPrices({});
      setAddedToLibraryItems(new Set());
      setDismissedSuggestions(new Set());
      setAppliedFlags(new Set());
      setAppliedAdjustments([]);
      setIsAdjustmentsExpanded(false);
      setEditingAdjustmentIndex(null);
      setStep(2);
      window.scrollTo({ top: 0, behavior: "instant" });
      if (normalizedResult.extractedCustomerName && !form.getValues("customerName")) {
        form.setValue("customerName", normalizedResult.extractedCustomerName);
      }
      if (normalizedResult.extractedAddress && !form.getValues("projectAddress")) {
        form.setValue("projectAddress", normalizedResult.extractedAddress);
      }
      // Store generated description for reference
      if (normalizedResult.generatedDescription && !form.getValues("description")) {
        form.setValue("description", normalizedResult.generatedDescription);
      }
      // Update selected project types if AI detected them
      if (normalizedResult.projectTypes && normalizedResult.projectTypes.length > 0) {
        setSelectedTypes(normalizedResult.projectTypes);
      }
      
      // Check if AI returned valid line items
      if (normalizedResult.lineItems.length === 0) {
        toast({ 
          title: "Analysis incomplete", 
          description: "No work items were identified. Try adding more details to your description.",
          variant: "destructive" 
        });
      } else if (firstQuoteMode && normalizedResult.testQuoteRecorded !== true) {
        toast({
          title: "Test Quote tracking needs attention",
          description: "Your quote was generated, but we couldn't confirm the Test Quote completion mark. Please contact support rather than generating it again.",
          variant: "destructive",
        });
      } else {
        const extractedInfo = [];
        if (normalizedResult.extractedCustomerName) extractedInfo.push("customer name");
        if (normalizedResult.extractedAddress) extractedInfo.push("address");
        const extraInfo = extractedInfo.length > 0 ? ` (extracted ${extractedInfo.join(", ")})` : "";
        toast({ title: "Project analyzed successfully", description: `${normalizedResult.lineItems.length} work items identified${extraInfo}` });
      }
      if (guestMode) {
        const revision = typeof (result as any)?.revision === "number" ? (result as any).revision : guestRevision;
        setGuestRevision(revision);
      }
    },
    onError: (error: Error, variables) => {
      if ((error as any).code === "RECOVERY_PAUSED") return;
      // A blocked new request may explain the existing pending request, but an
      // old request's late failure must never modify a newer project's state.
      if (!guestMode && (variables.generationOwnerId !== generationOwnerRef.current ||
        (variables.generationStarted && !isCurrentGeneration(variables.generationRequestId, variables.generationOwnerId,
          activeGenerationRequestRef.current, generationOwnerRef.current)))) return;
      if (!guestMode) trackEvent("quote_client_failed", "/quotes/new", {
        requestId: variables.generationRequestId, code: (error as any).code || "QUOTE_FAILED",
      });
      if (!guestMode && user?.id && (error as any).generationTerminal && activeGenerationRequestRef.current) {
        forgetGeneration(user.id, activeGenerationRequestRef.current);
        setPendingGenerationId(null);
      }
      if ((error as any).code === "RECOVERY_UNCONFIRMED") {
        setAnalysisError(error.message);
        return;
      }
      if (guestMode && getGuestAccessNotice((error as any).code)) {
        setGuestAccessNoticeCode((error as any).code);
        return;
      }
      if (guestMode) {
        const notice = getGuestErrorNotice((error as any).code);
        if (notice) {
          toast({
            title: notice.title,
            description: notice.message,
            variant: "destructive",
          });
          return;
        }
      }
      if (isQuotaErrorCode((error as any).code)) {
        setQuotaExceeded(true);
        return;
      }
      setAnalysisError(error.message || "The quotation could not be completed. Please try again.");
      toast({ 
        title: "Analysis failed", 
        description: error.message || "Please try again or add more details",
        variant: "destructive" 
      });
    },
  });

  const resumePendingGeneration = (pending: PendingQuoteGeneration) => {
      autoRecoveryRef.current = pending.requestId;
      setPendingGenerationId(pending.requestId);
      activeGenerationRequestRef.current = pending.requestId;
      const localDraft = loadDraftFromStorage();
      if (pending.generationLogId && localDraft?.analysisResult.generationLogId === pending.generationLogId) {
        // Prefer the edited draft over the original server-generated prices.
        completedGenerationRef.current = pending.requestId;
        restoreDraft(localDraft);
        return;
      }
      for (const field of ["description", "customerName", "projectAddress"] as const) {
        const value = pending.formValues[field];
        if (typeof value === "string") form.setValue(field, value);
      }
      const settings = pending.reviewSettings;
      if (settings) {
        if (typeof settings.includeVat === "boolean") setIncludeVat(settings.includeVat);
        if (typeof settings.contingencyPercent === "string") setContingencyPercent(settings.contingencyPercent);
        if (typeof settings.overheadPercent === "string") setOverheadPercent(settings.overheadPercent);
        if (typeof settings.profitPercent === "string") setProfitPercent(settings.profitPercent);
        if (typeof settings.contingencyEnabled === "boolean") setContingencyEnabled(settings.contingencyEnabled);
        if (typeof settings.overheadEnabled === "boolean") setOverheadEnabled(settings.overheadEnabled);
        if (typeof settings.profitEnabled === "boolean") setProfitEnabled(settings.profitEnabled);
      }
      analyzeProjectMutation.mutate({ ...pending.formValues, recoveryRequestId: pending.requestId });
  };

  useEffect(() => {
    if (guestMode || editingSavedQuote || !user?.id || analysisResult || analyzeProjectMutation.isPending) return;
    try {
      const pending = loadPendingGeneration(user.id);
      if (!pending || autoRecoveryRef.current === pending.requestId) return;
      // Completed unsaved work is opt-in, not an automatic replacement for a
      // fresh project's description. Interrupted work still recovers itself.
      if (isCompletedGeneration(pending) && new URLSearchParams(window.location.search).get("resume") !== "1") {
        setPendingGenerationId(pending.requestId);
        setShowDraftBanner(true);
        return;
      }
      resumePendingGeneration(pending);
    } catch (error) {
      setAnalysisError(error instanceof Error ? error.message : "The earlier quotation needs checking.");
    }
  }, [guestMode, editingSavedQuote, user?.id, analysisResult, analyzeProjectMutation.isPending]);

  useEffect(() => {
    if (!guestMode) return;
    const checkGuest = () => { if (!document.hidden) void refetchGuestStatus(); };
    document.addEventListener("visibilitychange", checkGuest);
    window.addEventListener("pageshow", checkGuest);
    window.addEventListener("online", checkGuest);
    return () => {
      document.removeEventListener("visibilitychange", checkGuest);
      window.removeEventListener("pageshow", checkGuest);
      window.removeEventListener("online", checkGuest);
    };
  }, [guestMode, refetchGuestStatus]);

  const generateDescriptionMutation = useMutation({
    mutationFn: async (request: DescriptionRequestIdentity & { files: UploadedImage[] }) => {
      const keywords = request.description;
      if (!guestMode) trackEvent("description_request_started", "/quotes/new", { requestId: request.id });
      const payload = guestMode
        ? {
            attemptId: guestAttemptIdRef.current,
            ...(selectedGuestSample ? { sampleId: selectedGuestSample.id } : {
              description: keywords,
              files: request.files.map(img => ({
                base64: img.base64,
                mimeType: img.mimeType,
                name: img.file?.name || "drawing",
              })),
            }),
          }
        : {
            images: request.files.map(img => ({
              base64: img.base64,
              mimeType: img.mimeType,
            })),
            keywords,
          };
      const response = await fetch(guestMode ? "/api/guest/generate-description" : "/api/generate-description", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Request-Id": request.id },
        body: JSON.stringify(payload),
      });
      if (response.status === 401) {
        throw await parseGuestApiError(response, "Your guest session has expired.");
      }
      if (response.status === 422) {
        const data = await response.json().catch(() => ({}));
        const msg = (data as any).message || "The uploaded file couldn't be read. Please check it's a clear, unencrypted PDF or image and try again.";
        const err = new Error(msg) as Error & { code: string };
        err.code = (data as any).code || "PDF_UNREADABLE";
        throw err;
      }
      if (!response.ok) {
        throw await parseGuestApiError(response, "Failed to generate description");
      }
      const text = await response.text();
      const result = readDescriptionResult(text);
      if (result.error) {
        throw Object.assign(new Error(result.message || "Failed to generate description"), { code: result.code });
      }
      const guestResult = guestMode ? { ...result, ...(result.data || {}) } : result;
      if (guestMode && !guestResult.description && guestResult.generatedDescription) {
        guestResult.description = guestResult.generatedDescription;
      }
      if (guestMode && !guestResult.description && guestResult.detailedDescription) {
        guestResult.description = guestResult.detailedDescription;
      }
      if (typeof guestResult.description !== "string" || !guestResult.description.trim()) {
        throw new Error("No description was returned. Your project details are still here; please retry.");
      }
      return guestResult;
    },
    onSuccess: (result, request) => {
      if (!acceptsDescriptionResult(request, activeDescriptionRequestRef.current?.id, {
        ownerId: generationOwnerRef.current, description: form.getValues("description") || "",
        imageIds: descriptionImagesRef.current.map(image => image.id),
        editRevision: descriptionEditRevisionRef.current,
      })) {
        if (request.ownerId === generationOwnerRef.current) {
          setDescGenError("Your project changed while the description was being created, so the result has not replaced your edits. Your current text is kept. Create a description again only if you want a fresh AI version.");
          if (!guestMode) trackEvent("description_result_discarded", "/quotes/new", { requestId: request.id, reason: "project_changed" });
        }
        return;
      }
      setDescGenError(null);
      form.setValue("description", result.description);
      descriptionEditTrackedRef.current = false;
      setGeneratedDescriptionBasis(result.description);
      setPreparedQuote(!guestMode ? result.preparedQuote ?? null : null);
      setDescriptionFlags(result.flags || []);
      setDescriptionWorkItems(result.workItems || []);
      setDimensionEvidence(result.dimensionEvidence || []);
      const review = createMeasurementReview(result.dimensionEvidence || [],
        [result.description, ...(result.workItems || []).map((item: any) => `${item.type ?? ""} ${item.name ?? ""} ${item.description ?? ""}`)],
        result.measurementSubjects || []);
      setMeasurementReviewFields(review.fields);
      setMeasurementJobLabel(review.jobLabel);
      setMeasurementReviewError(undefined);
      setStructuralSummary(result.structuralSummary || null);
      setAppliedDescFlags(new Set());
      setSimplifiedDescription(result.simplifiedDescription || null);
      setShowSimplified(false);
      setDescriptionIsGenerated(true);
      setMeasurementGenerationRequested(false);
      if (!guestMode) trackEvent("description_result_accepted", "/quotes/new", {
        requestId: request.id, measurementCount: review.fields.length,
      });
      // Only fresh description completion opens the automatic, first-time pop-out.
      setConfirmedMeasurementKey(null);
      setMeasurementReviewSkipped(false);
      setMeasurementReviewInlineOpen(false);
      setMeasurementReviewOpen(true);
      if (guestMode) {
        const revision = typeof result.descriptionRevision === "number"
          ? result.descriptionRevision
          : typeof result.revision === "number" ? result.revision : guestDescriptionRevision + 1;
        guestDescriptionRevisionRef.current = revision;
        guestApprovedDescriptionRef.current = { description: result.description, revision };
        setGuestDescriptionRevision(revision);
        setGuestRevision(typeof result.revision === "number" ? result.revision : guestRevision);
      }
      const flagCount = result.flags?.length || 0;
      const beamCount = result.structuralSummary?.beams?.length || 0;
      const desc = flagCount > 0
        ? `Description created with ${beamCount} structural members found. ${flagCount} item${flagCount !== 1 ? "s" : ""} flagged for review.`
        : "Description ready. You can edit it without generating it again. Check the measurements next; unknown sizes can stay blank.";
      toast({ title: "Description generated", description: desc });
    },
    onError: (error: Error & { code?: string }, request) => {
      if (activeDescriptionRequestRef.current?.id !== request.id || request.ownerId !== generationOwnerRef.current) return;
      if (!guestMode) trackEvent("description_request_failed", "/quotes/new", {
        requestId: request.id, code: error.code || "DESCRIPTION_FAILED",
      });
      if (guestMode && getGuestAccessNotice(error.code)) {
        setDescGenError(null);
        setGuestAccessNoticeCode(error.code ?? null);
        return;
      }
      if (guestMode) {
        const notice = getGuestErrorNotice(error.code);
        if (notice) {
          setDescGenError(null);
          toast({
            title: notice.title,
            description: notice.message,
            variant: "destructive",
          });
          return;
        }
      }
      if (error.code === "PDF_UNREADABLE" || error.code === "DOCUMENT_REQUEST_TOO_LARGE" || error.code === "DOCUMENT_IMAGE_PREPARATION_FAILED") {
        setDescGenError(error.message);
      } else {
        setDescGenError(error.message || "The description could not be completed. Please try Create Description again.");
        toast({
          title: "Failed to generate description",
          description: error.message || "Please try again",
          variant: "destructive",
        });
      }
    },
    onSettled: (_result, _error, request) => {
      if (activeDescriptionRequestRef.current?.id === request.id) activeDescriptionRequestRef.current = null;
    },
  });

  const startDescriptionGeneration = () => {
    if (processingFilesRef.current || activeDescriptionRequestRef.current || analyzeProjectMutation.isPending) return;
    const description = form.getValues("description") || "";
    if (!description.trim() && images.length === 0 && !selectedGuestSample) {
      setDescGenError("Add a few project details or upload a drawing first, then choose Create Description.");
      if (!guestMode) trackEvent("description_request_not_started", "/quotes/new", { reason: "project_information_required" });
      return;
    }
    const request = { id: crypto.randomUUID(), ownerId: user?.id, description,
      imageIds: images.map(image => image.id), editRevision: descriptionEditRevisionRef.current, files: images };
    activeDescriptionRequestRef.current = request;
    setDescGenError(null);
    discoveredItemsPoolRef.current = getDiscoveredItemsForDescription(description);
    generateDescriptionMutation.mutate(request);
  };

  const simplifyDescriptionMutation = useMutation({
    mutationFn: async (description: string) => {
      const response = await fetchWithAuth("/api/simplify-description", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ description }),
      });
      if (response.status === 401) {
        throw new Error("Your session has expired. Please log in again.");
      }
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to simplify description");
      }
      const data = await response.json();
      return data.simplifiedDescription as string;
    },
    onSuccess: (simplified) => {
      setSimplifiedDescription(simplified);
      setShowSimplified(true);
    },
    onError: (error: Error) => {
      toast({
        title: "Could not simplify description",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    },
  });

  const createQuoteMutation = useMutation({
    mutationFn: async () => {
      const formData = form.getValues();
      if (editingSavedQuote) {
        if (!editHydrated || !editingQuote) throw new Error("Wait for the saved quotation to load.");
        const response = await apiRequest("PUT", `/api/quotes/${editingQuoteId}/line-items`, {
          lineItems: savedQuoteEditLines(lineItems, editingQuote.lineItems ?? []),
          contingencyPercent: contingencyEnabled ? Number(contingencyPercent) || 0 : 0,
          overheadPercent: overheadEnabled ? Number(overheadPercent) || 0 : 0,
          profitPercent: profitEnabled ? Number(profitPercent) || 0 : 0,
          includeVat, summaryMode, estimatedDays: customEstDays ?? editingQuote.estimatedDays ?? null,
          assumptions: editedAssumptions.join("\n"), exclusions: editedExclusions.join("\n"),
          reviewerNotes: analysisResult?.reviewerNotes ?? [],
          termsConditions, paymentTerms,
          description: formData.description ?? "", projectAddress: formData.projectAddress ?? "",
        });
        return response.json();
      }
      
      // Ensure projectType is a string (AI might return an array)
      let projectType = "general_works";
      if (analysisResult?.projectType) {
        projectType = Array.isArray(analysisResult.projectType) 
          ? analysisResult.projectType.join(", ") 
          : analysisResult.projectType;
      }

      const quoteData = {
        clientId: formData.clientId || undefined,
        projectAddress: formData.projectAddress ?? "",
        projectType,
        description: formData.description ?? "",
        includeVat,
        contingencyPercent,
        overheadPercent,
        profitPercent,
        assumptions: editedAssumptions.join("\n") || "",
        exclusions: editedExclusions.join("\n") || "",
        paymentTerms: "50% deposit upon instruction, 50% upon completion",
        learnedAdjustments: appliedAdjustments.map(adj => `${adj.percent > 0 ? "+" : ""}${adj.percent}% ${adj.category === "all" ? "all items" : adj.category}`),
        reviewerNotes: analysisResult?.reviewerNotes ?? [],
        validationFlags: analysisResult?.validationFlags ?? [],
        uploadedFileNames: analysisResult?.uploadedFileNames ?? [],
        uploadedFileKeys: analysisResult?.uploadedFileKeys ?? [],
        isMultiScope: !!analysisResult?.isMultiScope,
        scopeNames: (analysisResult?.scopes ?? []).map(s => s.name),
        summaryMode,
        lineItems: lineItems.map((item, index) => {
          if (item.quantityDerivation) assertDimensionQuantity(item.quantityDerivation, item.quantity, item.unit);
          const detail = item.quoteCostRecipe
            ? recipeForQuoteSnapshot(recipeAtQuotePrice(item.quoteCostRecipe, Number(item.unitCost)))
            : undefined;
          const split = detail ? quoteRecipeSplit(detail, Number(item.quantity) || 0) : null;
          return {
          section: (item as any).section || "MAIN WORKS",
          scopeName: item.scopeName ?? null,
          displayStage: item.displayStage ?? null,
          displaySubGroup: item.displaySubGroup ?? null,
          category: item.category ?? "",
          name: item.name ?? "",
          description: item.description ?? "",
          inclusions: (item as any).inclusions ?? [],
          exclusions: (item as any).exclusions ?? [],
          quantity: String(item.quantity ?? 0),
          unit: item.unit ?? "item",
          unitCost: String(item.unitCost ?? 0),
          labourHours: String(item.labourHours ?? 0),
          labourCost: String(split?.labourCost ?? item.labourCost ?? 0),
          materialCost: String(split?.materialCost ?? item.materialCost ?? 0),
          plantCost: String(split?.plantCost ?? item.plantCost ?? 0),
          quoteCostRecipe: detail,
          materialAllowanceApproval: item.materialAllowanceApproval ?? null,
          quantityDerivation: item.quantityDerivation ?? undefined,
          isOptional: (item as any).isOptional ?? false,
          priceVerificationFlag: (item as any).priceVerificationFlag ?? null,
          sortOrder: index,
        };
        }),
      };
      const response = await apiRequest("POST", "/api/quotes", {
        ...quoteData,
        quoteType: "auto",
        generationLogId: analysisResult?.generationLogId,
        originalLineItems: baseLineItemsRef.current.map((item, index) => ({
          section: (item as any).section || "MAIN WORKS",
          category: item.category ?? "",
          name: item.name ?? "",
          description: item.description ?? "",
          quantity: String(item.quantity ?? 0),
          unit: item.unit ?? "item",
          unitCost: String(item.unitCost ?? 0),
          sortOrder: index,
        })),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail ? ` (${errorData.detail})` : "";
        const err = new Error((errorData.message || `Server error ${response.status}`) + detail);
        (err as any).code = errorData.code;
        throw err;
      }
      return response.json();
    },
    onSuccess: (quote: any) => {
      commitDraftMaterialsConfirmation(quote.id, buildMaterialsList(lineItems, true));
      if (!editingSavedQuote) clearDraftFromStorage();
      if (!editingSavedQuote && user?.id && activeGenerationRequestRef.current) {
        forgetGeneration(user.id, activeGenerationRequestRef.current);
        setPendingGenerationId(null);
      }
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes", quote.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes/recent"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats"] });
      if (!editingSavedQuote) trackBuilderEvent("quote_created", { product: "builder", mode: "auto" });
      toast({ title: editingSavedQuote ? "Changes saved" : "Quote created successfully" });
      navigate(`/quotes/${quote.id}`);
    },
    onError: (error: Error) => {
      if (editingSavedQuote) {
        toast({ title: "Changes not saved", description: describeQuoteSaveError(error), variant: "destructive" });
        return;
      }
      if (isQuotaErrorCode((error as any).code)) {
        toast({ title: "Trial limit reached", description: "You've reached the shared allowance of 5 total quote creations across Auto, Template, and Manual quotes. Subscribe to a plan to keep quoting.", variant: "destructive" });
        return;
      }
      toast({
        title: "Failed to create quote",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    },
  });

  const guestFinalizeMutation = useMutation({
    mutationFn: async () => {
      const snapshot = buildGuestSnapshot();
      let revision = guestRevision;
      // Persist the final editable snapshot before finalizing. This also
      // closes the small race where a user clicks Finish immediately after
      // moving from review to the final step.
      const draftResponse = await fetch("/api/guest/draft", {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attemptId: guestAttemptIdRef.current,
          revision,
          snapshot,
          lineItems: snapshot.lineItems,
          assumptions: snapshot.editedAssumptions,
          exclusions: snapshot.editedExclusions,
          findings: snapshot.analysisResult?.validationFlags || [],
          includeVat: snapshot.includeVat,
          contingencyEnabled: snapshot.contingencyEnabled,
          contingencyPercent: Number(snapshot.contingencyPercent) || 0,
          overheadEnabled: snapshot.overheadEnabled,
          overheadPercent: Number(snapshot.overheadPercent) || 0,
          profitEnabled: snapshot.profitEnabled,
          profitPercent: Number(snapshot.profitPercent) || 0,
          mode: snapshot.summaryMode ? "summary" : "detailed",
        }),
      });
      if (draftResponse.ok) {
        const draftPayload = await draftResponse.json().catch(() => ({}));
        const returnedRevision = draftPayload.revision ?? draftPayload.draftRevision;
        if (typeof returnedRevision !== "number") {
          throw new Error("Your latest quote edits were not confirmed. Please try Finish & Save again.");
        }
        revision = returnedRevision;
        if (draftPayload.pricing || draftPayload.draft?.pricing) {
          applyGuestPricing(draftPayload.pricing || draftPayload.draft.pricing);
        }
      } else if (draftResponse.status !== 409) {
        throw await parseGuestApiError(draftResponse, "Unable to save your guest quote");
      } else {
        await draftResponse.json().catch(() => ({}));
        // Never finalize against a stale revision: the server's draft may
        // contain edits from a previous tab/session. Rehydrate it and make
        // the user explicitly retry with the visible current state.
        guestStatusHydratedRef.current = false;
        await refetchGuestStatus();
        throw new Error("This quote changed in another tab. We refreshed the latest draft; please review it and try Finish & Save again.");
      }
      const response = await fetch("/api/guest/finalize", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attemptId: guestAttemptIdRef.current,
          revision,
          snapshot,
        }),
      });
      if (!response.ok) {
        throw await parseGuestApiError(response, "Unable to finish your guest quote");
      }
      return response.json();
    },
    onSuccess: (payload: any) => {
      if (typeof payload?.revision === "number") setGuestRevision(payload.revision);
      if (payload?.pricing || payload?.snapshot?.pricing) {
        applyGuestPricing(payload.pricing || payload.snapshot.pricing);
      }
      setGuestFinalized(true);
      toast({ title: "Quote ready to save", description: "Create a free account to save your quote." });
    },
    onError: (error: Error & { code?: string }) => {
      if (getGuestAccessNotice(error.code)) {
        setGuestAccessNoticeCode(error.code ?? null);
        return;
      }
      const notice = getGuestErrorNotice(error.code);
      toast({
        title: notice?.title ?? "Could not finish quote",
        description: notice?.message || error.message,
        variant: "destructive",
      });
    },
  });

  const [isDragging, setIsDragging] = useState(false);

  const processFiles = useCallback(async (files: File[]) => {
    if (processingFilesRef.current || files.length === 0) return;
    setFileUploadError(null);
    const validation = validateQuoteUploadSelection(files, guestMode, images.map((image) => image.file));
    if (!validation.valid) {
      setFileUploadError(validation.message ?? "These files could not be added. Please try again.");
      toast({
        title: "Upload limit reached",
        description: validation.message,
        variant: "destructive",
      });
      return;
    }
    processingFilesRef.current = true;
    setIsProcessingFiles(true);
    try {
      const newImages: UploadedImage[] = [];
      let failedFiles = 0;
      for (const file of files) {
        const isImage = file.type.startsWith("image/");
        const isPdf = file.type === "application/pdf";
        if (!isImage && !isPdf) {
          failedFiles++;
          toast({ title: "Unsupported file type", description: "Please upload images (PNG, JPG) or PDF drawings", variant: "destructive" });
          continue;
        }
        let preview = "";
        try {
          const base64 = await fileToBase64(file);
          preview = isPdf ? "" : URL.createObjectURL(file);
          newImages.push({
            id: Math.random().toString(36).slice(2),
            file, preview, base64, mimeType: file.type,
          });
        } catch {
          if (preview) URL.revokeObjectURL(preview);
          failedFiles++;
        }
      }
      setImages(prev => [...prev, ...newImages].slice(0, guestMode ? 8 : 10));
      if (failedFiles > 0) {
        setFileUploadError(`${failedFiles} file${failedFiles === 1 ? "" : "s"} could not be loaded. Please try again using PNG, JPG or PDF files.`);
      }
    } finally {
      processingFilesRef.current = false;
      setIsProcessingFiles(false);
    }
  }, [guestMode, images, toast]);

  const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    await processFiles(Array.from(files));
    e.target.value = "";
  }, [processFiles]);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    await processFiles(Array.from(files));
  }, [processFiles]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const removeImage = (id: string) => {
    setImages(prev => {
      const img = prev.find(i => i.id === id);
      if (img) URL.revokeObjectURL(img.preview);
      return prev.filter(i => i.id !== id);
    });
  };

  const measurementReviewKey = (data: ProjectFormData, fields: MeasurementReviewField[]) =>
    measurementConfirmationKey(data.description || "", selectedTypes, images.map(image => image.id), fields);

  const handleAnalyze = async (data: ProjectFormData) => {
    if (activeDescriptionRequestRef.current || measurementReviewSubmittingRef.current || analyzeProjectMutation.isPending) return;
    if (!guestMode) trackEvent("quote_generate_clicked", "/quotes/new");
    const hasImages = images.length > 0;
    const hasDescription = data.description && data.description.length >= 10;
    
    if (!hasImages && !hasDescription && !selectedGuestSample) {
      if (!guestMode) trackEvent("quote_generation_blocked", "/quotes/new", { reason: "project_information_required" });
      toast({ 
        title: "Need more information", 
        description: "Please add photos/drawings or provide a project description",
        variant: "destructive" 
      });
      return;
    }
    if (isFixedQuoteExample(guestMode || firstQuoteMode, images.length, data.description || "", guestSamples?.samples)) {
      setMeasurementReviewError(undefined);
      await startAnalysisAfterMeasurementReview(data);
      return;
    }
    const needsDescriptionGeneration = guestMode
      ? !guestApprovedDescriptionRef.current
      : true;
    if (!descriptionIsGenerated && needsDescriptionGeneration) {
      if (!guestMode) trackEvent("quote_generation_blocked", "/quotes/new", { reason: "description_required" });
      setDescRequiredFlash(true);
      setDescRequiredHint(true);
      setTimeout(() => setDescRequiredFlash(false), 1600);
      setTimeout(() => setDescRequiredHint(false), 4000);
      document.querySelector('[data-testid="button-generate-description"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setMeasurementReviewError(undefined);
    const fields = resolveMeasurementReviewFields(measurementReviewFields, dimensionEvidence, [data.description || "", ...selectedTypes]);
    if (fields.length === 0) {
      await continueMeasurementReview(data, true);
      return;
    }
    if (measurementReviewSkipped) {
      // Dismissing an untouched review is not confirmation of AI assumptions.
      // Keep the original drawing evidence, without adding builder overrides.
      await startAnalysisAfterMeasurementReview(data);
      return;
    }
    if (confirmedMeasurementKey === measurementReviewKey(data, fields)) {
      await continueMeasurementReview(data, true);
      return;
    }
    if (!guestMode) trackEvent("quote_generation_blocked", "/quotes/new", { reason: "measurement_confirmation_required" });
    setMeasurementGenerationRequested(true);
    setMeasurementReviewOpen(false);
    setMeasurementReviewError("One step left: check the sizes, then choose Confirm measurements & create quote. Unknown sizes can stay blank.");
    setMeasurementReviewInlineOpen(true);
    document.querySelector('[data-testid="panel-measurement-review"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const startAnalysisAfterMeasurementReview = async (data: ProjectFormData, measurementReview?: MeasurementReview) => {
    if (guestMode) {
      try {
        const descriptionRevision = await approveGuestDescriptionIfChanged(data.description || "");
        analyzeProjectMutation.mutate({ ...data, measurementReview, guestDescriptionRevision: descriptionRevision });
      } catch (error) {
        const code = (error as { code?: string })?.code;
        if (getGuestAccessNotice(code)) {
          setGuestAccessNoticeCode(code ?? null);
          return;
        }
        const notice = getGuestErrorNotice(code);
        toast({
          title: notice?.title ?? "Description approval required",
          description: notice?.message || (error instanceof Error ? error.message : "Please review your description and try again."),
          variant: "destructive",
        });
      }
      return;
    }
    analyzeProjectMutation.mutate({ ...data, measurementReview });
  };

  const continueMeasurementReview = async (data: ProjectFormData, generateQuote = false) => {
    if (measurementReviewSubmittingRef.current || analyzeProjectMutation.isPending) return;
    measurementReviewSubmittingRef.current = true;
    setMeasurementReviewSubmitting(true);
    try {
      const fields = resolveMeasurementReviewFields(measurementReviewFields, dimensionEvidence, [data.description || "", ...selectedTypes]);
      const review = fields.length ? buildMeasurementReview(fields) : undefined;
      setMeasurementReviewError(undefined);
      setConfirmedMeasurementKey(measurementReviewKey(data, fields));
      if (!guestMode && !measurementReviewSkipped) trackEvent("measurements_confirmed", "/quotes/new", { measurementCount: fields.filter(field => !field.removed && field.value.trim()).length, generateQuote });
      if (generateQuote) await startAnalysisAfterMeasurementReview(data, review);
      setMeasurementGenerationRequested(false);
      setMeasurementReviewOpen(false);
      setMeasurementReviewInlineOpen(false);
    } catch (error) {
      setMeasurementReviewError(error instanceof Error ? error.message : "Check the measurement values.");
      setMeasurementReviewInlineOpen(true);
      document.querySelector('[data-testid="panel-measurement-review"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    } finally {
      measurementReviewSubmittingRef.current = false;
      setMeasurementReviewSubmitting(false);
    }
  };

  const getItemIdentityKey = (item: AnalyzedLineItem): string => {
    const cat = (item.category || "").toLowerCase().trim();
    const name = (item.name || "").toLowerCase().trim();
    const unit = (item.unit || "item").toLowerCase().trim();
    return `${cat}::${name}::${unit}`;
  };

  const updateLineItem = (index: number, field: keyof AnalyzedLineItem, value: string | number | boolean, dimensionUpdate?: QuantityDerivation) => {
    if (field === "unitCost") {
      const entered = Number(value);
      if (!Number.isFinite(entered) || entered < 0) return;
      // Saved quote unit rates have two decimal places. A line total divided
      // by its quantity may not; normalize once, before reconciling a recipe.
      value = Math.round(entered * 100) / 100;
      if (Math.abs(Number(value) - entered) > 0.0000001) {
        toast({ title: "Unit rate rounded to pennies", description: "The line total uses the rounded unit rate shown in the item details." });
      }
    }
    if (field === "unitCost") {
      const currentItem = lineItems[index];
      if (currentItem) {
        const key = getItemIdentityKey(currentItem);
        const currentStoredOriginal = editedPrices[key]?.originalPrice;
        const originalCost = currentStoredOriginal ?? currentItem.unitCost;
        if (originalCost !== value) {
          setEditedPrices(prev => ({
            ...prev,
            [key]: { originalPrice: originalCost, currentIndex: index }
          }));
        }
      }
    }
    setLineItems(prev => {
      const updated = [...prev];
      const cur = updated[index];
      if (!cur) return prev;
      const next = { ...cur, [field]: value } as AnalyzedLineItem;
      if (field === "unitCost") next.priceSource = "manual";
      if (field === "unitCost") next.manualPriceBaseline = manualPriceBaseline(cur, Number(value));
      if (cur.quantityDerivation && field === "quantity" && (!Number.isFinite(Number(value)) || Number(value) < 0)) return prev;
      if (dimensionUpdate) next.quantityDerivation = dimensionUpdate;
      else if ((field === "quantity" || field === "unit") && cur.quantityDerivation)
        next.quantityDerivation = { ...cur.quantityDerivation, quantity_overridden: true };
      // Two-way linking: when qty or unitCost changes, rescale the
      // labour/material/plant breakdown to keep the same proportions.
      if (field === "quantity" || field === "unitCost") {
        const qty = Number(field === "quantity" ? value : next.quantity) || 0;
        if (dimensionUpdate && !next.quoteCostRecipe) {
          next.quantityDerivation = markDimensionPriceReview(cur.quantityDerivation, dimensionUpdate, cur.unit);
        }
        const uc = Number(field === "unitCost" ? value : next.unitCost) || 0;
        const newSubtotal = qty * uc;
        const rescaled = rescaleBreakdown(
          {
            labour: Number(cur.labourCost ?? 0),
            material: Number(cur.materialCost ?? 0),
            plant: Number(cur.plantCost ?? 0),
          },
          newSubtotal,
          cur.category,
        );
        next.labourCost = rescaled.labour;
        next.materialCost = rescaled.material;
        next.plantCost = rescaled.plant;
        if (next.quoteCostRecipe) {
          try {
            next.quoteCostRecipe = field === "unitCost"
              ? recipeAtQuotePrice(next.quoteCostRecipe, uc) : next.quoteCostRecipe;
            if (dimensionUpdate && next.quoteCostRecipe.quote_pricing?.status !== "manual") {
              next.quoteCostRecipe = priceRecipeForQuote(next.quoteCostRecipe, uc, dimensionUpdate, `${next.name} ${next.description ?? ""} ${next.sourceVariant?.name ?? ""} ${next.sourceVariant?.specification ?? ""}`, qty, next.unit);
              next.unitCost = Number(deriveCostRecipe(next.quoteCostRecipe).rateTypical);
            }
            Object.assign(next, quoteRecipeSplit(next.quoteCostRecipe, qty));
          } catch (error) {
            toast({ title: "Unable to update breakdown", description: error instanceof Error ? error.message : "Review the pricing inputs.", variant: "destructive" });
            return prev;
          }
        }
      }
      if (field === "unit" && next.quoteCostRecipe && value !== cur.unit) {
        // Per-unit material quantities cannot be reused for a different unit.
        next.quoteCostRecipe = null;
      }
      updated[index] = next;
      return updated;
    });
  };

  const openCategoryPicker = (index: number, purpose: "add" | "move" = "move") => {
    setCategoryPickerPurpose(purpose);
    setCategoryPickerIndex(index);
  };

  const reassignLineItemCategory = (
    index: number,
    category: string,
    trade: string,
    subcategory: string,
  ) => {
    setLineItems(prev => {
      const current = prev[index];
      if (!current) return prev;
      const updated = [...prev];
      updated[index] = reassignItemCategory(current, { category, trade, subcategory });
      return updated;
    });
  };

  const moveLineItemPlacement = useCallback((
    sourceIndex: number,
    destination: { stage: string; subGroup?: string | null; targetIndex?: number; position?: "before" | "after" },
  ) => {
    setLineItems(prev => moveQuoteItem(prev, sourceIndex, destination));
    setActiveCategoryFilter(null);
    setCollapsedStages(prev => {
      const next = new Set(prev);
      next.delete(destination.stage);
      return next;
    });
    if (destination.subGroup) {
      setExpandedServiceGroups(prev => new Set([
        ...prev,
        `${destination.stage}::${destination.subGroup}`,
      ]));
    }
  }, []);

  const handlePlacementDragEnd = useCallback((event: DragEndEvent) => {
    const sourceIndex = event.active.data.current?.sourceIndex;
    const target = event.over?.data.current as QuotePlacementDropData | undefined;
    if (typeof sourceIndex !== "number" || !target) return;
    moveLineItemPlacement(sourceIndex, {
      stage: target.stage,
      subGroup: target.subGroup,
      targetIndex: target.targetIndex,
      position: target.kind === "after" ? "after" : "before",
    });
  }, [moveLineItemPlacement]);

  // Two-way linking: when the user edits a single breakdown component, the
  // line subtotal and unitCost recompute from the new sum.
  const updateBreakdownField = (
    index: number,
    field: "labourCost" | "materialCost" | "plantCost",
    value: number,
  ) => {
    setLineItems(prev => {
      const updated = [...prev];
      const cur = updated[index];
      if (!cur) return prev;
      const v = Math.max(0, Number(value) || 0);
      const next = { ...cur, [field]: v } as AnalyzedLineItem;
      const newSubtotal = subtotalFromBreakdown(
        Number(next.labourCost ?? 0),
        Number(next.materialCost ?? 0),
        Number(next.plantCost ?? 0),
      );
      const qty = Number(cur.quantity) || 0;
      next.unitCost = qty > 0 ? newSubtotal / qty : Number(cur.unitCost) || 0;
      updated[index] = next;
      return updated;
    });
  };

  // Stores the last deleted item so an undo toast can restore it.
  const lastDeletedRef = React.useRef<{ item: AnalyzedLineItem; index: number } | null>(null);

  const removeLineItem = (index: number) => {
    setLineItems(prev => {
      lastDeletedRef.current = { item: prev[index], index };
      return prev.filter((_, i) => i !== index);
    });
    toast({
      title: "Item removed",
      description: lastDeletedRef.current?.item?.name
        ? `"${lastDeletedRef.current.item.name}" has been removed.`
        : "Work item removed.",
      duration: 4500,
      action: (
        <ToastAction
          altText="Undo remove"
          onClick={() => {
            const deleted = lastDeletedRef.current;
            if (!deleted) return;
            setLineItems(prev => {
              const items = [...prev];
              items.splice(deleted.index, 0, deleted.item);
              return items;
            });
            lastDeletedRef.current = null;
          }}
        >
          Undo
        </ToastAction>
      ),
    });
  };

  const moveLineItem = (index: number, direction: "up" | "down") => {
    setLineItems(prev => {
      const items = [...prev];
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= items.length) return prev;
      [items[index], items[newIndex]] = [items[newIndex], items[index]];
      return items;
    });
  };

  // Multi-scope quotes (Task #680): reassign a line item to a different scope.
  const moveItemToScope = (index: number, scopeName: string) => {
    setLineItems(prev => {
      const items = [...prev];
      if (index < 0 || index >= items.length) return prev;
      items[index] = { ...items[index], scopeName };
      return items;
    });
  };

  const learnPriceMutation = useMutation({
    mutationFn: async (data: { item: AnalyzedLineItem; oldPrice: number; itemIndex: number }) => {
      const response = await apiRequest("POST", "/api/cost-library/learn-price", {
        category: data.item.category,
        itemName: data.item.name,
        unit: data.item.unit,
        unitCost: data.item.unitCost.toString(),
      });
      return response.json();
    },
    onSuccess: (result, variables) => {
      const key = getItemIdentityKey(variables.item);
      setEditedPrices(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
      if (result.matched) {
        setLineItems(prev => prev.map((li, i) => i === variables.itemIndex ? {
          ...li,
          costLibraryTaskId: result.taskId,
          costLibraryPrice: li.unitCost,
          manualPriceBaseline: undefined,
        } : li));
        toast({ title: "Price learned", description: "Your Cost Library has been updated with this rate" });
      } else {
        toast({ title: "Failed to learn price", description: "No matching task found in Cost Library", variant: "destructive" });
      }
    },
    onError: () => {
      toast({ title: "Failed to learn price", variant: "destructive" });
    },
  });

  // Admin-only: update a master cost library entry's rates directly from the quote
  const adminSetPriceMutation = useMutation({
    mutationFn: async (data: {
      taskId: string;
      rateLow: string;
      rateTypical: string;
      rateHigh: string;
      itemIndex: number;
    }) => {
      const response = await apiRequest("PATCH", `/api/admin/cost-library/default/${data.taskId}`, {
        rateLow: data.rateLow,
        rateTypical: data.rateTypical,
        rateHigh: data.rateHigh,
        isPriceLocked: true,
      });
      return response.json();
    },
    onSuccess: (result, variables) => {
      setLineItems(prev => prev.map((li, i) => i === variables.itemIndex ? {
        ...li,
        costLibraryPrice: parseFloat(variables.rateTypical) || li.unitCost,
        manualPriceBaseline: undefined,
      } : li));
      queryClient.invalidateQueries({ queryKey: ["/api/cost-library"] });
      toast({
        title: "Master Cost Library updated",
        description: "The new rate is now the default for all users. Individual overrides are unchanged.",
      });
    },
    onError: () => {
      toast({ title: "Failed to update Master Cost Library", variant: "destructive" });
    },
  });

  const applyFlagMutation = useMutation({
    mutationFn: async (data: { flag: ValidationFlag; flagIndex: number }) => {
      const { flag } = data;

      // Prefer name-based lookup — indices can shift when items are inserted post-validation.
      // The expert validator assigns itemIndex before auto-add steps run, so indices may be stale.
      let itemIdx = typeof flag.itemIndex === "number" ? flag.itemIndex : -1;
      if (flag.itemName) {
        const nameIdx = lineItems.findIndex(
          li => (li.name || "").toLowerCase().trim() === (flag.itemName || "").toLowerCase().trim()
        );
        if (nameIdx >= 0) itemIdx = nameIdx;
      }
      if (itemIdx < 0 || itemIdx >= lineItems.length) return { applied: false };

      const item = lineItems[itemIdx];
      if (!item) return { applied: false };
      const results: any = { applied: true, resolvedIdx: itemIdx, priceApplied: false, quantityApplied: false, removalApplied: false };

      if (flag.suggestRemoval) {
        const response = await apiRequest("POST", "/api/adjustments/learn-exclusion", {
          category: item.category,
          itemName: item.name,
          reason: flag.detail,
        });
        results.exclusionResult = await response.json();
        results.removalApplied = true;
        return results;
      }

      if (flag.suggestedUnitCost != null && flag.suggestedUnitCost !== item.unitCost) {
        results.priceApplied = true;
      }

      if (flag.suggestedQuantity != null && flag.suggestedQuantity !== item.quantity) {
        results.quantityApplied = true;
      }

      return results;
    },
    onSuccess: (result, variables) => {
      if (!result?.applied) return;
      const { flag, flagIndex } = variables;

      // Use the index resolved during mutationFn (name-matched, not stale itemIndex)
      const itemIdx: number = result.resolvedIdx ?? flag.itemIndex!;

      if (result.removalApplied) {
        setLineItems(prev => prev.filter((_, i) => i !== itemIdx));
        baseLineItemsRef.current = baseLineItemsRef.current.filter((_, i) => i !== itemIdx);
        sliderBaseRef.current = null;
      } else {
        setLineItems(prev => prev.map((li, i) => {
          if (i !== itemIdx) return li;
          const updated = { ...li };
          if (flag.suggestedUnitCost != null) updated.unitCost = flag.suggestedUnitCost;
          if (flag.suggestedQuantity != null) {
            updated.quantity = flag.suggestedQuantity;
            if (updated.quantityDerivation) updated.quantityDerivation = { ...updated.quantityDerivation, quantity_overridden: true };
          }
          return updated;
        }));

        baseLineItemsRef.current = baseLineItemsRef.current.map((li, i) => {
          if (i !== itemIdx) return li;
          const updated = { ...li };
          if (flag.suggestedUnitCost != null) updated.unitCost = flag.suggestedUnitCost;
          if (flag.suggestedQuantity != null) {
            updated.quantity = flag.suggestedQuantity;
            if (updated.quantityDerivation) updated.quantityDerivation = { ...updated.quantityDerivation, quantity_overridden: true };
          }
          return updated;
        });
      }

      setAppliedFlags(prev => {
        const next = new Set(Array.from(prev));
        next.add(flagIndex);
        return next;
      });

      const parts: string[] = [];
      if (result.removalApplied) parts.push("item removed & exclusion rule saved");
      if (result.priceApplied) parts.push("price updated & learned");
      if (result.quantityApplied) parts.push("quantity updated & rule saved");
      toast({ title: "Finding applied", description: parts.join(", ") || "Applied" });
    },
    onError: () => {
      toast({ title: "Failed to apply finding", variant: "destructive" });
    },
  });

  const addToCostLibMutation = useMutation({
    mutationFn: async (data: { item: AnalyzedLineItem; trade: string; itemIndex: number }) => {
      const response = await apiRequest("POST", "/api/cost-library/add-from-quote", {
        name: data.item.name,
        category: data.item.category,
        trade: data.trade,
        unit: data.item.unit,
        rateTypical: data.item.unitCost.toString(),
      });
      return response.json();
    },
    onSuccess: (result, variables) => {
      setAddToCostLibOpen(false);
      setAddToCostLibItem(null);
      setAddedToLibraryItems(prev => new Set(Array.from(prev).concat(variables.itemIndex)));
      setLineItems(prev => prev.map((li, i) => i === variables.itemIndex ? {
        ...li,
        costLibraryTaskId: result.id || "added",
        costLibraryPrice: li.unitCost,
        manualPriceBaseline: undefined,
      } : li));
      queryClient.invalidateQueries({ queryKey: ["/api/cost-library"] });
      // The backend saves a location-free work-type name ("Porch Skim Finish" is
      // saved as "Skim Finish") — show the actual saved name so the builder knows.
      const savedName = result?.name;
      const renamed = savedName && savedName !== variables.item.name;
      toast({
        title: "Added to Cost Library",
        description: renamed
          ? `Saved as "${savedName}" — location kept on the quote only, so all areas share one library rate`
          : "This item is now in your Cost Library for future quotes",
      });
    },
    onError: (err: any) => {
      if (String(err?.message || "").startsWith("409")) {
        setAddToCostLibOpen(false);
        setAddToCostLibItem(null);
        toast({ title: "Already in your Cost Library", description: "This item is already saved, so no duplicate was created." });
        return;
      }
      toast({ title: "Failed to add to Cost Library", variant: "destructive" });
    },
  });

  // Admin-only: publish a quote line item as a new default CL entry for all users
  const publishToDefaultLibMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      description: string;
      category: string;
      trade: string;
      unit: string;
      rateLow: string;
      rateTypical: string;
      rateHigh: string;
      itemIndex: number;
    }) => {
      const response = await apiRequest("POST", "/api/admin/cost-library/default", {
        name: data.name,
        description: data.description || null,
        category: data.category,
        trade: data.trade,
        unit: data.unit,
        rateLow: data.rateLow,
        rateTypical: data.rateTypical,
        rateHigh: data.rateHigh,
        isPriceLocked: true,
        market: user?.market || "uk",
      });
      return response.json();
    },
    onSuccess: (result, variables) => {
      setPublishToDefaultLibOpen(false);
      setPublishToDefaultLibItem(null);
      setPublishedToDefaultLibItems(prev => new Set(Array.from(prev).concat(variables.itemIndex)));
      setLineItems(prev => prev.map((li, i) => i === variables.itemIndex ? {
        ...li,
        costLibraryTaskId: result.id || "published",
        costLibraryPrice: li.unitCost,
        manualPriceBaseline: undefined,
      } : li));
      queryClient.invalidateQueries({ queryKey: ["/api/cost-library"] });
      toast({
        title: "Published to default library",
        description: `"${variables.name}" is now available to all users in the Master Cost Library`,
      });
    },
    onError: () => {
      toast({ title: "Failed to publish to default library", variant: "destructive" });
    },
  });

  const generateWorkItemMutation = useMutation({
    mutationFn: async (description: string) => {
      const projectContext = analysisResult?.projectSummary || form.getValues("description") || "";
      const response = await apiRequest("POST", "/api/generate-work-item", {
        description,
        projectContext,
        useCostLibraryRates,
      });
      return response.json();
    },
    onSuccess: (item: AnalyzedLineItem) => {
      if (!item || !item.name || !item.category || !item.unit || item.unitCost <= 0) {
        toast({ title: "Invalid item generated", description: "Please try again with more detail", variant: "destructive" });
        return;
      }
      const category = item.category || "";
      const subcategory = detectSubcategory(item.name, item.description, category, item.trade);
      setLineItems(prev => [...prev, { ...item, subcategory }]);
      baseLineItemsRef.current = [...baseLineItemsRef.current, { ...item, subcategory }];
      sliderBaseRef.current = null;
      setAddDetailText("");
      toast({ title: "Work item added", description: `${item.name} has been added to your quote` });
    },
    onError: () => {
      toast({ title: "Failed to generate work item", description: "Please try again or add manually", variant: "destructive" });
    },
  });

  const applySubtotalScale = (newSubtotal: number) => {
    setLineItems(prev => {
      // Snapshot core prices the first time the slider is touched, so the
      // anchor is the user's CURRENT prices (not the original AI output).
      // This prevents a jump when the user has manually edited prices first.
      if (!sliderBaseRef.current) {
        sliderBaseRef.current = prev.map(item => ({ ...item }));
      }
      const baseItems = sliderBaseRef.current;
      // Only operate on core items — optional items are excluded from the
      // subtotal and must not be scaled by the core-price slider.
      const coreBaseSubtotal = baseItems.reduce(
        (sum, item) => item.isOptional ? sum : sum + item.quantity * item.unitCost, 0
      );
      if (coreBaseSubtotal <= 0) return prev;
      const scaleFactor = newSubtotal / coreBaseSubtotal;
      return prev.map((item, idx) => {
        if (item.isOptional) return item;
        const baseUnitCost = baseItems[idx]?.unitCost ?? item.unitCost;
        const newUnitCost = parseFloat((baseUnitCost * scaleFactor).toFixed(2));
        const r = rescaleBreakdown(
          {
            labour: Number(item.labourCost ?? 0),
            material: Number(item.materialCost ?? 0),
            plant: Number(item.plantCost ?? 0),
          },
          item.quantity * newUnitCost,
          item.category,
        );
        return {
          ...item,
          unitCost: newUnitCost,
          labourCost: r.labour,
          materialCost: r.material,
          plantCost: r.plant,
        };
      });
    });
  };


  const applyUpgradePrices = async () => {
    if (guestMode) return;
    const percent = parseFloat(upgradePercent);
    if (isNaN(percent) || percent === 0 || percent <= -100) return;
    const factor = 1 + percent / 100;
    setLineItems(prev => prev.map(item => {
      if (upgradeCategory === "all" || item.category === upgradeCategory) {
        const newUnitCost = Math.round(item.unitCost * factor);
        const r = rescaleBreakdown(
          {
            labour: Number(item.labourCost ?? 0),
            material: Number(item.materialCost ?? 0),
            plant: Number(item.plantCost ?? 0),
          },
          item.quantity * newUnitCost,
          item.category,
        );
        return {
          ...item,
          unitCost: newUnitCost,
          labourCost: r.labour,
          materialCost: r.material,
          plantCost: r.plant,
        };
      }
      return item;
    }));

    setAppliedAdjustments(prev => {
      const existingIdx = prev.findIndex(a => a.category === upgradeCategory);
      if (existingIdx >= 0) {
        const existing = prev[existingIdx];
        const combined = existing.percent + percent;
        const combinedRounded = Math.round(combined * 100) / 100;
        const updated = [...prev];
        updated[existingIdx] = { ...existing, percent: combinedRounded, reason: upgradeReason || existing.reason };
        return updated;
      }
      return [...prev, { category: upgradeCategory, percent, reason: upgradeReason }];
    });

    try {
      await apiRequest("POST", "/api/adjustments/learn", {
        percent: upgradePercent,
        reason: upgradeReason,
        category: upgradeCategory,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/adjustments"] });
    } catch {
    }

    const categoryLabel = upgradeCategory === "all" ? "all items" : capitalizeCategory(upgradeCategory);
    toast({
      title: "Adjustment learned",
      description: `${percent > 0 ? "+" : ""}${percent}% applied to ${categoryLabel}`,
    });
    setIsUpgradeDialogOpen(false);
    setUpgradePercent("0");
    setUpgradeReason("");
  };

  const calculateTotals = () => {
    if (guestMode && guestFinalized && guestPricing) {
      return {
        subtotal: Number(guestPricing.subtotal) || 0,
        contingency: Number(guestPricing.contingencyAmount) || 0,
        overhead: Number(guestPricing.overheadAmount) || 0,
        profit: Number(guestPricing.profitAmount) || 0,
        totalPrice: Number(guestPricing.totalPrice) || 0,
        vatAmount: Number(guestPricing.vatAmount) || 0,
        grandTotal: Number(guestPricing.grandTotal) || 0,
      };
    }
    const subtotal = lineItems.filter(item => !item.isOptional).reduce((sum, item) => sum + (item.quantity * item.unitCost), 0);
    const contingency = contingencyEnabled ? subtotal * (parseFloat(contingencyPercent) / 100) : 0;
    const overhead = overheadEnabled ? subtotal * (parseFloat(overheadPercent) / 100) : 0;
    const profit = profitEnabled ? subtotal * (parseFloat(profitPercent) / 100) : 0;
    const totalPrice = subtotal + contingency + overhead + profit;
    const vatRate = editingSavedQuote && editingQuote?.vatRate != null
      ? Number(editingQuote.vatRate) : getTaxRate(user?.market);
    const vatAmount = includeVat ? totalPrice * (vatRate / 100) : 0;
    const grandTotal = totalPrice + vatAmount;

    return { subtotal, contingency, overhead, profit, totalPrice, vatAmount, grandTotal };
  };

  const totals = calculateTotals();

  const formatCurrency = (amount: number) => formatMoney(amount, user?.market);

  const [descStep, setDescStep] = useState(0);
  const [descSeconds, setDescSeconds] = useState(120);
  const [discoveredItems, setDiscoveredItems] = useState<string[]>([]);
  const [quoteStep, setQuoteStep] = useState(0);
  const [quoteSeconds, setQuoteSeconds] = useState(180);
  const discoveredItemsPoolRef = useRef<string[]>(DISCOVERED_ITEMS_BY_TYPE.general);

  useEffect(() => {
    if (!generateDescriptionMutation.isPending) {
      setDescStep(0);
      setDescSeconds(120);
      setDiscoveredItems([]);
      return;
    }
    setDescSeconds(120);
    const countdownTimer = setInterval(() => {
      setDescSeconds(prev => Math.max(prev - 1, 0));
    }, 1000);
    const stepTimer = setInterval(() => {
      setDescStep(prev => prev < DESCRIPTION_STEPS.length - 1 ? prev + 1 : prev);
    }, 6000);
    return () => { clearInterval(countdownTimer); clearInterval(stepTimer); };
  }, [generateDescriptionMutation.isPending]);

  useEffect(() => {
    if (!analyzeProjectMutation.isPending) {
      setQuoteStep(0);
      setQuoteSeconds(180);
      setDiscoveredItems([]);
      return;
    }
    setQuoteSeconds(180);
    const countdownTimer = setInterval(() => {
      setQuoteSeconds(prev => Math.max(prev - 1, 0));
    }, 1000);
    const stepTimer = setInterval(() => {
      setQuoteStep(prev => prev < QUOTE_STEPS.length - 1 ? prev + 1 : prev);
    }, 10000);
    return () => { clearInterval(countdownTimer); clearInterval(stepTimer); };
  }, [analyzeProjectMutation.isPending]);

  if (editingSavedQuote && (editingQuoteLoading || (!editHydrated && !editingQuoteError))) {
    return <div className="p-6 flex items-center gap-2" role="status"><Loader2 className="h-4 w-4 animate-spin" />Loading quotation…</div>;
  }
  if (editingSavedQuote && editingQuoteError) {
    return <div className="p-6 space-y-3" role="alert"><h1 className="text-lg font-semibold">Unable to open quotation</h1>
      <p>{describeQuoteSaveError(editingQuoteError)}</p>
      <Button onClick={() => void reloadEditingQuote()}>Try again</Button>
      <Button variant="outline" onClick={() => navigate("/quotes")}>Back to quotes</Button></div>;
  }
  return (
    <div className={`p-4 sm:p-6 space-y-4 sm:space-y-6 mx-auto ${step === 2 && analysisResult && !guestMode ? "max-w-[1440px]" : "max-w-5xl"}`}>
      {QUOTE_GUIDE_ENABLED && !guestMode && user?.id && (
        <QuoteGuide key={user.id} userId={user.id} stage={step === 1 ? 1 : step === 2 ? 2 : 3}
          busy={generateDescriptionMutation.isPending || analyzeProjectMutation.isPending || measurementReviewSubmitting || measurementReviewOpen} />
      )}
      {showDraftBanner && step === 1 && (
        <Card className="border-primary/30 bg-primary/5" data-testid="card-draft-resume">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm font-medium" data-testid="text-draft-title">You have an unsaved quote in progress</p>
                <p className="text-xs text-muted-foreground" data-testid="text-draft-subtitle">
                  {(() => {
                    const draft = loadDraftFromStorage();
                    if (!draft) return "";
                    const itemCount = draft.lineItems.length;
                    const addr = draft.formValues?.projectAddress;
                    const mins = Math.round((Date.now() - draft.savedAt) / 60000);
                    const timeAgo = mins < 1 ? "just now" : mins < 60 ? `${mins}m ago` : `${Math.round(mins / 60)}h ago`;
                    return `${itemCount} line items${addr ? ` - ${addr}` : ""} (saved ${timeAgo})`;
                  })()}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  clearDraftFromStorage();
                  if (user?.id && pendingGenerationId) forgetGeneration(user.id, pendingGenerationId);
                  generationController.current?.abort();
                  activeGenerationRequestRef.current = null;
                  completedGenerationRef.current = null;
                  autoRecoveryRef.current = null;
                  setPendingGenerationId(null);
                  setShowDraftBanner(false);
                }}
                data-testid="button-draft-discard"
              >
                Start Fresh
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  const draft = loadDraftFromStorage();
                  const pending = user?.id ? loadPendingGeneration(user.id) : null;
                  if (pending) {
                    setShowDraftBanner(false);
                    resumePendingGeneration(pending);
                    return;
                  }
                  if (draft) {
                    restoreDraft(draft);
                    toast({ title: "Quote restored", description: "Your previous quote has been loaded" });
                  }
                }}
                data-testid="button-draft-resume"
              >
                Resume Quote
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      {generateDescriptionMutation.isPending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm" data-testid="overlay-description-loading">
          <Card className="w-full max-w-lg mx-4 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2" data-testid="text-desc-loading-title">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                Creating Project Description
              </CardTitle>
              <CardDescription data-testid="text-desc-loading-subtitle">Analysing your uploads and building a detailed scope...</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-3">
                {DESCRIPTION_STEPS.map((s, i) => (
                  <div key={i} className={`flex items-start gap-3 transition-opacity duration-500 ${i <= descStep ? "opacity-100" : "opacity-30"}`} data-testid={`desc-step-${i}`}>
                    <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${i < descStep ? "bg-primary text-primary-foreground" : i === descStep ? "border-2 border-primary" : "border-2 border-muted"}`}>
                      {i < descStep && <Check className="h-3 w-3" />}
                      {i === descStep && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${i <= descStep ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</p>
                      {i === descStep && <p className="text-xs text-muted-foreground mt-0.5">{s.detail}</p>}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-3">
                <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" /> Description Preview
                </p>
                <p className="text-sm italic text-primary/70 animate-pulse">Generating...</p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex items-center gap-1.5 bg-primary/10 text-primary rounded-full px-3 py-1.5 shrink-0">
                    <Clock className="h-3.5 w-3.5" />
                    <span className="text-sm font-semibold tabular-nums">{Math.floor(descSeconds / 60)}:{String(descSeconds % 60).padStart(2, "0")}</span>
                  </div>
                  <span className="text-xs text-primary font-bold">Estimated Time to Complete</span>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">For more advanced projects, please allow additional time.</p>
              <div className="w-full bg-muted rounded-full h-1.5">
                <div className="bg-primary h-1.5 rounded-full transition-all duration-1000" style={{ width: `${Math.min(((descStep + 1) / DESCRIPTION_STEPS.length) * 100, 100)}%` }} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {analyzeProjectMutation.isPending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm" data-testid="overlay-quote-loading">
          <Card className="w-full max-w-lg mx-4 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2" data-testid="text-quote-loading-title">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                {recoveringQuote ? "Checking Your Quotation" : "Generating Your Quote"}
              </CardTitle>
              <CardDescription data-testid="text-quote-loading-subtitle">
                {recoveringQuote
                  ? generationConnection === "running"
                    ? "Your quotation is still running. You can return to this page to check the same request."
                    : "Checking your saved quotation — no new generation or additional trial quote is being started."
                  : "Our smart engine is analysing your project and calculating costs..."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-3">
                {QUOTE_STEPS.map((s, i) => (
                  <div key={i} className={`flex items-start gap-3 transition-opacity duration-500 ${i <= quoteStep ? "opacity-100" : "opacity-30"}`} data-testid={`quote-step-${i}`}>
                    <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${i < quoteStep ? "bg-primary text-primary-foreground" : i === quoteStep ? "border-2 border-primary" : "border-2 border-muted"}`}>
                      {i < quoteStep && <Check className="h-3 w-3" />}
                      {i === quoteStep && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${i <= quoteStep ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</p>
                      {i === quoteStep && <p className="text-xs text-muted-foreground mt-0.5">{s.detail}</p>}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-3">
                <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" /> Quote Preview
                </p>
                <p className="text-sm italic text-primary/70 animate-pulse">Generating...</p>
                {recoveringQuote ? (
                  <p role="status" className="text-xs text-muted-foreground mt-2">
                    {generationConnection === "reconnecting" ? "Waiting for a connection. Your recovery reference is saved."
                      : "Checking the server for your quotation. The countdown is an estimate, not the generation status."}
                  </p>
                ) : quoteSeconds > 0 ? (
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center gap-1.5 bg-primary/10 text-primary rounded-full px-3 py-1.5 shrink-0">
                      <Clock className="h-3.5 w-3.5" />
                      <span className="text-sm font-semibold tabular-nums">{Math.floor(quoteSeconds / 60)}:{String(quoteSeconds % 60).padStart(2, "0")}</span>
                    </div>
                    <span className="text-xs text-primary font-bold">Estimated Time to Complete</span>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic mt-2">Still working — complex projects can take a little longer...</p>
                )}
              </div>

              <p className="text-xs text-muted-foreground">For more advanced projects, please allow additional time.</p>
              {!guestMode && <Button type="button" variant="outline" size="sm" onClick={() => navigate("/quotes")}>
                Back to quotes
              </Button>}
              <div className="w-full bg-muted rounded-full h-1.5">
                <div className="bg-primary h-1.5 rounded-full transition-all duration-1000" style={{ width: `${Math.min(((quoteStep + 1) / QUOTE_STEPS.length) * 100, 100)}%` }} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(editingSavedQuote ? `/quotes/${editingQuoteId}` : guestMode ? "/" : "/quotes")} data-testid="button-back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2 flex-wrap" data-testid="text-ai-quote-title">
            {editingSavedQuote ? `Edit Quote – ${editingQuote?.quoteNumber ?? ""}` : firstQuoteMode ? "Test Quote – Introduction" : guestMode ? "Test Quote" : "Auto Quote"}
            <HelpTip testId="button-help-quote-generator" content="Upload your drawings, photos, or plans and describe the work needed. Our smart engine will analyse everything and produce a detailed cost breakdown. You can then review, adjust prices, and save it as a professional quote." />
          </h1>
          <p className="text-muted-foreground">
            {editingSavedQuote ? "Review and edit your saved quotation using the Auto Quote tools." : firstQuoteMode
              ? "Choose an example project to get started, or use your own drawings, photos or job description below."
              : "Upload drawings, photos or describe your project to estimate the costs."}
          </p>
        </div>
        {editingSavedQuote && <div className="ml-auto flex gap-2 shrink-0">
          <Button type="button" variant="outline" onClick={() => navigate(`/quotes/${editingQuoteId}`)}>Discard</Button>
          <Button type="button" data-testid="button-save-edits" disabled={lineItems.length === 0 || createQuoteMutation.isPending}
            onClick={() => createQuoteMutation.mutate()}>{createQuoteMutation.isPending ? "Saving..." : "Save Changes"}</Button>
        </div>}
      </div>

      {/* Progress Steps */}
      {!editingSavedQuote && <ol className="flex items-center justify-center gap-2 lg:gap-4 py-4" aria-label="Quote progress" data-testid="quote-progress">
        {["Upload & Describe", "Measurements", "Review Estimate", "Create Quote"].map((label, index) => {
          const number = index + 1;
          const reached = activeQuoteProgressStep >= number;
          return (
            <li key={label} className="flex items-center gap-2 lg:gap-4" aria-current={activeQuoteProgressStep === number ? "step" : undefined} aria-label={`${number}. ${label}`}>
              {index > 0 && <div className="w-5 lg:w-8 xl:w-16 h-0.5 bg-muted" aria-hidden="true">
                <div className={`h-full transition-all ${reached ? "bg-primary w-full" : "w-0"}`} />
              </div>}
              <div className={`flex items-center gap-2 ${reached ? "text-primary" : "text-muted-foreground"}`}>
                <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-sm font-medium ${reached ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                  {activeQuoteProgressStep > number ? <Check className="h-4 w-4" /> : number}
                </div>
                <span className="hidden lg:inline font-medium whitespace-nowrap">{label}</span>
              </div>
            </li>
          );
        })}
      </ol>}
      <Form {...form}>
        <form onSubmit={editingSavedQuote ? event => event.preventDefault() : form.handleSubmit(handleAnalyze)}>
          {/* Step 1: Upload & Describe */}
          {step === 1 && (
            <div className="space-y-6">
              {guestMode && (
                <Card className="border-primary/25 bg-primary/5" data-testid="card-guest-start-choice">
                  <CardContent className="pt-5 pb-5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="font-semibold text-base">Choose how you’d like to start</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Try one free test quote below, or create a free account now to save your work and generate more quotes.
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 sm:shrink-0">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => document.querySelector('[data-testid="card-guest-samples"], [data-testid="dropzone-images"]')?.scrollIntoView({ behavior: "smooth", block: "start" })}
                          data-testid="button-guest-try-free"
                        >
                          Try Free Quote
                        </Button>
                        <Button
                          type="button"
                          onClick={() => navigate("/login?returnTo=/quotes/new")}
                          data-testid="button-guest-register-now"
                        >
                          Sign Up
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {guestMode && guestAccessNoticeCode && (() => {
                const notice = getGuestAccessNotice(guestAccessNoticeCode)
                  || getGuestErrorNotice(guestAccessNoticeCode);
                return notice ? (
                  <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30" data-testid="banner-guest-access-notice">
                    <CardContent className="pt-5 pb-5">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                        <div className="flex gap-3 flex-1">
                          <div className="mt-0.5 text-amber-600 dark:text-amber-400 shrink-0">
                            <Info className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-semibold text-amber-950 dark:text-amber-100">{notice.title}</p>
                            <p className="text-sm text-amber-900 dark:text-amber-200 mt-1">{notice.message}</p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          size="lg"
                          onClick={() => navigate("/login?returnTo=/quotes/new")}
                          className="sm:shrink-0"
                          data-testid="button-guest-register-from-notice"
                        >
                          Sign Up
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : null;
              })()}


              {(guestMode || firstQuoteMode) && (guestSamples?.samples?.length ?? 0) > 0 && (
                <Card data-testid={firstQuoteMode ? "card-first-quote-samples" : "card-guest-samples"}>
                  <CardHeader>
                    <CardTitle className="text-base">
                      {firstQuoteMode ? "Option 1: Choose an example project" : "Try a sample project"}
                    </CardTitle>
                    <CardDescription>
                      {firstQuoteMode
                        ? "Select an example to load its full scope, then generate and review the quote."
                        : "Choose a sample to see the same full quote workflow without uploading files."}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {guestSamples?.samples.map((sample) => {
                      const imageSrc = sample.id === "structural_drawing"
                        ? "/assets/sample-structural.jpg"
                        : "/assets/sample-bathroom-clean.jpg";
                      const selected = selectedGuestSample?.id === sample.id;
                      return (
                        <div
                          key={sample.id}
                          className={`rounded-lg border-2 overflow-hidden flex flex-col sm:flex-row cursor-pointer transition-colors ${selected ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}
                          onClick={() => {
                            if (!guestMode) trackEvent("quote_example_selected", "/quotes/new");
                            setSelectedGuestSample(sample);
                            setMeasurementReviewOpen(false);
                            setMeasurementReviewInlineOpen(false);
                            setConfirmedMeasurementKey(null);
                            setMeasurementReviewSkipped(false);
                            setPreparedQuote(null);
                            setImages([]);
                            form.setValue("description", sample.description, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                            guestApprovedDescriptionRef.current = null;
                            setDescriptionFlags([]);
                            setDescriptionWorkItems([]);
                            setStructuralSummary(null);
                            setSimplifiedDescription(null);
                            setShowSimplified(false);
                            setDescriptionIsGenerated(true);
                            setDimensionEvidence([]);
                            const review = createMeasurementReview([], [sample.description]);
                            setMeasurementReviewFields(review.fields);
                            setMeasurementJobLabel(review.jobLabel);
                            setMeasurementReviewError(undefined);
                          }}
                          data-testid={`guest-sample-${sample.id}`}
                        >
                          <img src={imageSrc} alt={`${sample.label} preview`} className="w-full sm:w-28 h-28 object-cover shrink-0" />
                          <div className="p-3 flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-semibold text-sm">{sample.label}</p>
                              <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={(event) => { event.stopPropagation(); setPreviewGuestSample(sample); }}>
                                <Eye className="h-3.5 w-3.5 mr-1" /> Preview
                              </Button>
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-3 mt-1">{sample.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {(guestMode || firstQuoteMode) && (
                <Dialog open={Boolean(previewGuestSample)} onOpenChange={(open) => !open && setPreviewGuestSample(null)}>
                  <DialogContent className="max-w-4xl">
                    <DialogHeader>
                      <DialogTitle>{previewGuestSample?.label}</DialogTitle>
                      <DialogDescription>
                        Select this project to load its fixed scope into the Project Description box.
                      </DialogDescription>
                    </DialogHeader>
                    {previewGuestSample && (
                      <div className="rounded-lg border bg-muted overflow-hidden">
                        <img
                          src={previewGuestSample.id === "structural_drawing" ? "/assets/sample-structural.jpg" : "/assets/sample-bathroom-clean.jpg"}
                          alt={`${previewGuestSample.label} full preview`}
                          className="max-h-[70vh] w-full object-contain"
                        />
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              )}

              {firstQuoteMode && (
                <div data-testid="first-quote-own-project-option">
                  <p className="font-semibold text-base">Option 2: Use your own project</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Upload your own drawings or photos, or describe the job manually in the project description below.
                  </p>
                </div>
              )}

              {quotaExceeded && (
                <Card className="border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20" data-testid="banner-quota-exceeded">
                  <CardContent className="pt-5 pb-4">
                    <div className="flex gap-3 items-start">
                      <div className="flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                      </div>
                      <div className="flex-1 space-y-2">
                        {trialBlocked ? (
                          <>
                            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Free trial quote limit reached</p>
                            <p className="text-sm text-amber-700 dark:text-amber-400">
                              You've reached the shared allowance of 5 total quote creations across Auto, Template, and Manual quotes. Subscribe to a plan to keep generating quotes.
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Monthly quote limit reached</p>
                            <p className="text-sm text-amber-700 dark:text-amber-400">
                              You've used all 10 of your Auto quotes this month on the Starter plan. Your limit resets on the 1st of next month.
                            </p>
                            <p className="text-sm text-amber-700 dark:text-amber-400">
                              Upgrade to <strong>Pro</strong> for unlimited Auto quotes, Pricing Intelligence, and more.
                            </p>
                          </>
                        )}
                        <a
                          href="/settings"
                          className="inline-block mt-1 text-sm font-medium underline text-amber-800 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-200"
                          data-testid="link-upgrade-from-quota"
                        >
                          Upgrade your plan →
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {!trialBlocked && (<>
              {!quotaExceeded && quotaInfo && (
                <p className="text-xs text-muted-foreground" data-testid="text-quota-pill">
                  {quotaInfo.quotesLeft} of {quotaInfo.quotesTotal} quote{quotaInfo.quotesTotal !== 1 ? "s" : ""} remaining
                  {quotaInfo.period === "trial" ? " in your free trial" : " this month"}
                </p>
              )}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                    <Upload className="h-5 w-5 shrink-0 mt-0.5 self-start" />
                    <div className="flex flex-col">
                      <span>Project Photos & Drawings (Optional)</span>
                      <span className="text-xs font-semibold text-muted-foreground leading-tight">
                        Uploaded files will be used to create your project description
                      </span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Image Upload Area */}
                    <div
                      className={`border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer transition-colors ${isDragging ? "border-primary bg-primary/5" : "hover:border-primary/50"}`}
                      data-testid="dropzone-images"
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onClick={() => document.getElementById("file-upload-input")?.click()}
                    >
                      <input
                        id="file-upload-input"
                        type="file"
                        accept="image/*,.pdf"
                        multiple
                        disabled={isProcessingFiles}
                        className="hidden"
                        onChange={handleImageUpload}
                        data-testid="input-images"
                      />
                      <ImageIcon className={`h-12 w-12 mb-4 ${isDragging ? "text-primary" : "text-muted-foreground"}`} />
                      <p className="text-sm font-medium">{isDragging ? "Drop files here" : "Click to upload or drag and drop"}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {guestMode
                          ? "PNG, JPG, PDF up to 8 MB each (max 8 files, 25 MB total)"
                          : "PNG, JPG, PDF up to 10 MB each (max 10 files)"}
                      </p>
                    </div>

                    {/* File Previews */}
                    {(isProcessingFiles || images.length > 0) && (
                      <div className="rounded-lg border border-primary/25 bg-primary/5 p-3 text-sm" role="status" aria-live="polite" data-testid="upload-status">
                        <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap font-medium">
                          {isProcessingFiles ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : <Check className="h-4 w-4 text-primary shrink-0" />}
                          <span>{isProcessingFiles ? "Loading your files…" : `${images.length} File${images.length === 1 ? "" : "s"} Uploaded and Ready. ${generateDescriptionMutation.isPending ? "Creating your description using these files…" : "Go to Step 2: Create Description Below."}`}</span>
                        </div>
                        {isProcessingFiles && <p className="mt-1 text-muted-foreground">Please wait until your files are ready before creating the description.</p>}
                      </div>
                    )}
                    {fileUploadError && <p className="text-sm text-amber-700 dark:text-amber-400" role="alert" data-testid="upload-error">{fileUploadError}</p>}
                    {images.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {images.map((img) => (
                          <div key={img.id} className="relative group aspect-square">
                            <span className="absolute top-2 left-2 z-10 flex items-center gap-1 rounded bg-background/95 px-2 py-1 text-xs font-medium text-primary shadow-sm" data-testid="file-ready-badge">
                              <Check className="h-3 w-3" /> Ready
                            </span>
                            {img.mimeType === "application/pdf" ? (
                              <div className="w-full h-full flex flex-col items-center justify-center bg-muted rounded-lg border">
                                <FileText className="h-12 w-12 text-muted-foreground" />
                                <p className="text-xs text-muted-foreground mt-2 px-2 text-center truncate max-w-full">
                                  {img.file.name}
                                </p>
                              </div>
                            ) : (
                              <img
                                src={img.preview}
                                alt="Project photo"
                                className="w-full h-full object-cover rounded-lg"
                              />
                            )}
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => removeImage(img.id)}
                              data-testid={`button-remove-image-${img.id}`}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <button
                  type="button"
                  onClick={() => setProjectDetailsExpanded(prev => !prev)}
                  className="w-full text-left px-6 py-4 flex items-center justify-between gap-3"
                  data-testid="button-toggle-project-details"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-5 w-5 shrink-0" />
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 min-w-0">
                      <span className="font-semibold text-sm sm:text-base leading-tight">Project Details</span>
                      <span className="text-xs text-muted-foreground font-semibold leading-tight">(Client, Customer Name, Project Address)</span>
                    </div>
                  </div>
                  <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${projectDetailsExpanded ? "rotate-180" : ""}`} />
                </button>
                {projectDetailsExpanded && (
                <CardContent className="space-y-6 pt-0">
                  <FormField
                    control={form.control}
                    name="clientId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Client (Optional)</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value} disabled={guestMode}>
                          <FormControl>
                            <SelectTrigger data-testid="select-client">
                              <SelectValue placeholder="Select a client" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {clients?.map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                {client.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="customerName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Customer Name (Optional)</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="e.g., Mr John Smith" 
                              {...field} 
                              data-testid="input-customer-name"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="projectAddress"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Project Address (Optional)</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="e.g., 123 High Street, London SW1A 1AA" 
                              {...field} 
                              data-testid="input-address"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                </CardContent>
                )}
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                    <FileText className="h-5 w-5 shrink-0 mt-0.5 self-start" />
                    <div className="flex flex-col">
                      <span>Project Description</span>
                       <span className="text-xs font-semibold text-muted-foreground leading-tight">(Required before creating your quote)</span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 pt-0">
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        {form.watch("description") && (
                          <div className="flex items-center justify-between gap-4 py-2 px-3 bg-muted/30 rounded-md border mb-1" data-testid="toggle-description-view">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">
                                {showSimplified ? "Simplified view" : "Detailed view"}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {showSimplified
                                  ? "Simplified overview. Switch to see the full technical description used for quote generation."
                                  : "Full technical specification. Switch to see a simplified user friendly version."}
                              </p>
                            </div>
                            {guestMode ? (
                              simplifiedDescription ? (
                                <Switch
                                  checked={showSimplified}
                                  onCheckedChange={setShowSimplified}
                                  data-testid="switch-description-view"
                                />
                              ) : null
                            ) : simplifyDescriptionMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
                            ) : (
                              <Switch
                                checked={showSimplified}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    const desc = form.getValues("description") || "";
                                    if (simplifiedDescription) {
                                      setShowSimplified(true);
                                    } else if (desc.trim().length >= 20) {
                                      simplifyDescriptionMutation.mutate(desc);
                                    }
                                  } else {
                                    setShowSimplified(false);
                                  }
                                }}
                                data-testid="switch-description-view"
                              />
                            )}
                          </div>
                        )}
                        <div className={showSimplified ? "hidden" : ""}>
                          <FormControl>
                            <div className="relative">
                              <Textarea
                                placeholder="Type keywords or brief notes about the work, e.g.: 
- Knock through kitchen dining room 4m
- Steel beam RSJ
- Replaster walls ceiling
- Electrics first fix second fix"
                                className="min-h-[120px] pr-10"
                                {...field}
                                onChange={(e) => {
                                  applyDescriptionEdit(e.target.value);
                                  if ((guestMode || firstQuoteMode) && e.target.value.trim()) setSelectedGuestSample(null);
                                }}
                                data-testid="textarea-description"
                              />
                              <button
                                type="button"
                                title={isDescRecording ? "Stop recording" : "Speak your description"}
                                onClick={isDescRecording ? stopDescRecording : startDescRecording}
                                 disabled={guestMode || isDescTranscribing}
                                className={[
                                  "absolute bottom-2 right-2 flex items-center justify-center w-7 h-7 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                  isDescRecording
                                    ? "bg-red-500 text-white hover:bg-red-600 animate-pulse"
                                    : "bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                                  isDescTranscribing ? "opacity-50 cursor-not-allowed" : "",
                                ].join(" ")}
                                data-testid="button-desc-mic"
                              >
                                {isDescTranscribing ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : isDescRecording ? (
                                  <Square className="h-3 w-3 fill-current" />
                                ) : (
                                  <Mic className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </div>
                          </FormControl>
                          <FormDescription className="mt-2">
                            {guestMode && guestApprovedDescriptionRef.current
                              ? "You can edit the approved description, then generate your quote without regenerating the description."
                               : (
                                 <>
                                   <span className="block"><strong className="font-medium text-foreground">Drawings:</strong> If they contain all the necessary project information, leave the text box blank and click Create Description. The automatic description feature will read the drawings and create the description, no extra input needed. Add a note only if something is missing or you want to clarify the work.</span>
                                   <span className="mt-2 block"><strong className="font-medium text-foreground">Photos only:</strong> Help the automatic description feature understand your plans by describing the job in a few words. Include the proposed work, any relevant details and measurements you know, then click Create Description.</span>
                                   <span className="mt-2 block"><strong className="font-medium text-foreground">No files:</strong> Briefly describe the work and any known measurements, then click Create Description.</span>
                                 </>
                               )}
                          </FormDescription>
                          <FormMessage />
                        </div>
                        {showSimplified && simplifiedDescription && (
                          <div className="rounded-md border bg-muted/30 p-4 space-y-3" data-testid="panel-simplified-description">
                            <div className="text-sm leading-relaxed whitespace-pre-wrap">
                              {simplifiedDescription}
                            </div>
                            <p className="text-xs text-muted-foreground border-t pt-2">
                              Simplified view for reference only. The full technical description is always used for quote generation.
                            </p>
                          </div>
                        )}
                      </FormItem>
                    )}
                  />

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="default"
                      disabled={
                        isProcessingFiles || generateDescriptionMutation.isPending
                        || analyzeProjectMutation.isPending
                        || (descriptionIsGenerated && !generatedDescriptionEdited)
                        || (guestMode && (Boolean(guestApprovedDescriptionRef.current) || Boolean(selectedGuestSample)))
                      }
                      className={descRequiredFlash ? "ring-4 ring-orange-400 ring-offset-2 animate-pulse bg-orange-500 hover:bg-orange-500 transition-colors" : "transition-colors"}
                      onClick={() => {
                        if (!guestMode) trackEvent("description_create_clicked", "/quotes/new");
                        const description = form.getValues("description") || "";
                        if (!description.trim() && images.length === 0 && !selectedGuestSample) {
                          setDescGenError("Add a few project details or upload a drawing first, then choose Create Description.");
                          if (!guestMode) trackEvent("description_request_not_started", "/quotes/new", { reason: "project_information_required" });
                          return;
                        }
                        if (descConfirmShownRef.current && !descriptionIsGenerated) {
                          startDescriptionGeneration();
                        } else {
                          setShowDescConfirmModal(true);
                          if (!guestMode) trackEvent("description_confirmation_opened", "/quotes/new");
                        }
                      }}
                      data-testid="button-generate-description"
                    >
                      {generateDescriptionMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        generatedDescriptionEdited ? "Regenerate (Optional)" : descriptionIsGenerated ? "Create Description" : descGenError ? "Retry Create Description" : "Create Description"
                      )}
                    </Button>
                    {form.watch("description") && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          applyDescriptionEdit("");
                          if (guestMode || firstQuoteMode) setSelectedGuestSample(null);
                          setDescriptionFlags([]);
                          setSimplifiedDescription(null);
                          setShowSimplified(false);
                          setDescriptionIsGenerated(false);
                          setMeasurementReviewFields([]);
                          setDimensionEvidence([]);
                          setMeasurementReviewError(undefined);
                          setDescGenError(null);
                        }}
                        data-testid="button-clear-description"
                      >
                        Clear
                      </Button>
                    )}
                    {descRequiredHint && (
                      <span
                        className="text-sm text-orange-700 dark:text-orange-400 animate-in fade-in slide-in-from-left-1"
                        data-testid="hint-generate-description-required"
                      >
                        Please click "Create Description" first.
                      </span>
                    )}
                  </div>
                  {descGenError && (
                    <div role="alert" className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="desc-gen-error">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                      <span>{descGenError}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {!fixedQuoteExample && (descriptionIsGenerated || (guestMode && guestApprovedDescriptionRef.current)) && (
                <MeasurementReviewPanel
                  fields={currentMeasurementFields}
                  onFieldsChange={fields => {
                    setMeasurementReviewFields(fields);
                    setMeasurementReviewSkipped(false);
                  }}
                  jobLabel={measurementJobLabel} error={measurementReviewError}
                  isPending={measurementReviewSubmitting || analyzeProjectMutation.isPending || generateDescriptionMutation.isPending}
                  open={measurementReviewOpen}
                  onOpenChange={next => {
                    if (measurementReviewSubmitting || analyzeProjectMutation.isPending) return;
                    setMeasurementReviewOpen(next);
                    if (!next) {
                      const hasMeasurementEdits = currentMeasurementFields.some(field =>
                        (field.edited && !field.calculation) || (field.custom && field.value.trim()));
                      setMeasurementReviewSkipped(!hasMeasurementEdits);
                      setMeasurementGenerationRequested(false);
                      setMeasurementReviewError(undefined);
                    }
                  }}
                  onConfirm={() => void continueMeasurementReview(form.getValues(), measurementGenerationRequested)}
                  confirmLabel={measurementGenerationRequested ? "Confirm measurements & create quote" : "Confirm measurements"}
                  generatesQuote={measurementGenerationRequested}
                  inlineOpen={measurementReviewInlineOpen}
                  onInlineOpenChange={setMeasurementReviewInlineOpen}
                  onAddMeasurement={() => setMeasurementReviewFields(fields => fields.length >= 150 ? fields : [...fields,
                    { id: `manual-${crypto.randomUUID()}`, label: "", unit: "m", value: "", note: "", custom: true }])}
                />
              )}

              <Card>
                <button
                  type="button"
                  onClick={() => setPricingModeExpanded(prev => !prev)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left"
                  aria-expanded={pricingModeExpanded}
                  data-testid="button-toggle-pricing-mode"
                >
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 shrink-0" />
                    <span className="text-sm font-semibold">Pricing Mode</span>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${pricingModeExpanded ? "rotate-180" : ""}`} />
                </button>
                {pricingModeExpanded && (
                <CardContent className="pt-0">
                  <div className="flex gap-2 w-full">
                    <Button
                      type="button"
                      size="sm"
                      variant={pricingMode === "standard" ? "default" : "outline"}
                      onClick={() => setPricingMode("standard")}
                      className="flex-1 min-w-0 px-2 text-xs sm:text-sm"
                      data-testid="button-pricing-standard"
                    >
                      Standard
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={pricingMode === "learned" ? "default" : "outline"}
                      onClick={() => setPricingMode("learned")}
                      disabled={guestMode}
                      className="flex-1 min-w-0 px-2 text-xs sm:text-sm"
                      data-testid="button-pricing-learned"
                    >
                      Learned
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={pricingMode === "custom" ? "default" : "outline"}
                      onClick={() => setPricingMode("custom")}
                      disabled={guestMode}
                      className="flex-1 min-w-0 px-2 text-xs sm:text-sm"
                      data-testid="button-pricing-custom"
                    >
                      Custom Rules
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {pricingMode === "standard"
                      ? "Uses built-in UK unit rates for pricing."
                      : pricingMode === "learned"
                      ? "Applies pricing patterns learned from your imported historical quotes."
                      : "Applies your custom pricing rules alongside learned patterns."}
                  </p>
                </CardContent>
                )}
              </Card>

              {analysisError && (
                <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive" data-testid="quote-analysis-error">
                  {analysisError}
                  {pendingGenerationId && !guestMode && <div className="mt-2 flex flex-wrap gap-2">
                    <Button type="button" size="sm" variant="outline" disabled={analyzeProjectMutation.isPending}
                      onClick={() => analyzeProjectMutation.mutate({ ...form.getValues(), recoveryRequestId: pendingGenerationId })}>
                      Check this quotation again
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => {
                      if (!user?.id || !window.confirm("The earlier quotation may still finish and may have used an allowance. Stop recovering it and allow a new quotation? No new generation will start until you click Create Quote.")) return;
                      forgetGeneration(user.id, pendingGenerationId);
                      setPendingGenerationId(null);
                      autoRecoveryRef.current = null;
                      setAnalysisError(null);
                    }}>Stop recovery</Button>
                  </div>}
                </div>
              )}
              <div className="flex items-center justify-end gap-3">
                <Button 
                  type="submit" 
                  size="lg"
                  disabled={measurementReviewSubmitting || analyzeProjectMutation.isPending || trialBlocked}
                  data-testid="button-analyze"
                >
                  {measurementReviewSubmitting || analyzeProjectMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {"Analyzing..."}
                    </>
                  ) : (
                    "Create Quote"
                  )}
                </Button>
              </div>
              </>)}
            </div>
          )}

          {/* Step 2: Review Estimate */}
          {step === 2 && analysisResult && (
            <div className="space-y-6">

              {/* Analysis Summary */}
              <Card>
                <CardHeader className="space-y-3">
                  <div className="space-y-1.5">
                    <CardTitle className="text-base sm:text-xl">Your Quote is Completed</CardTitle>
                    {analysisResult.dimensionSource === "extracted" && (
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge data-testid="badge-dim-source" variant="outline" className="border-green-500 text-green-700 dark:text-green-400 text-xs">
                          Dimensions extracted
                        </Badge>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm sm:text-xl font-bold">{lineItems.length}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground">Work Items</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => setCustomEstDays(prev => Math.max(1, (prev ?? analysisResult.estimatedDays) - 1))}
                        className="h-6 w-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10 transition-colors"
                        data-testid="button-decrease-days"
                      >
                        <span className="text-lg font-medium leading-none">-</span>
                      </button>
                      <div className="text-center">
                        <p className="text-sm sm:text-xl font-bold">{customEstDays ?? analysisResult.estimatedDays}</p>
                        <p className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Est. Days</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCustomEstDays(prev => (prev ?? analysisResult.estimatedDays) + 1)}
                        className="h-6 w-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10 transition-colors"
                        data-testid="button-increase-days"
                      >
                        <span className="text-lg font-medium leading-none">+</span>
                      </button>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm sm:text-xl font-bold truncate">{formatCurrency(totals.subtotal)}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground">Subtotal</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm sm:text-xl font-bold text-primary truncate">{formatCurrency(totals.grandTotal)}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground">Total inc {getTaxLabel(user?.market)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Expert Review Summary — separate card, like Review Findings */}
              {analysisResult.autoAddedItems && analysisResult.autoAddedItems.length > 0 && (
                <Card data-testid="text-auto-added-items" className="bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
                  <button
                    type="button"
                    onClick={() => setIsExpertReviewExpanded(prev => !prev)}
                    className="w-full flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 text-left gap-3"
                    data-testid="button-toggle-expert-review"
                    aria-expanded={isExpertReviewExpanded}
                  >
                    <div className="flex items-start gap-3">
                      <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs sm:text-base font-semibold">Expert Review Summary</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {analysisResult.autoAddedItems.length} {analysisResult.autoAddedItems.length === 1 ? "improvement" : "improvements"} applied to your quote
                        </p>
                      </div>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${isExpertReviewExpanded ? "rotate-180" : ""}`} />
                  </button>
                  {isExpertReviewExpanded && (
                    <CardContent className="pt-0">
                      <ul className="text-xs sm:text-sm space-y-3 pl-8 pt-3 border-t border-green-200 dark:border-green-800/60">
                        {analysisResult.autoAddedItems.map((item, i) => (
                          <li key={i} className="text-green-800 dark:text-green-300 line-clamp-2">
                            {(item.name || "").replace(/\b\w/g, l => l.toUpperCase())}{item.reason ? ` ${item.reason}` : ""}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  )}
                </Card>
              )}

              {appliedAdjustments.length > 0 && (
                <Card data-testid="card-adjustments-banner">
                  <CardContent className="p-4">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between gap-3"
                      onClick={() => setIsAdjustmentsExpanded(!isAdjustmentsExpanded)}
                      data-testid="button-toggle-adjustments"
                    >
                      <div className="flex items-center gap-3">
                        <TrendingUp className="h-5 w-5 text-primary shrink-0" />
                        <div className="text-left">
                          <p className="font-medium" data-testid="text-adjustments-banner">
                            Price has been upgraded
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {appliedAdjustments.map(adj => {
                              const catLabel = adj.category === "all" ? "All Items" : capitalizeCategory(adj.category);
                              return `${catLabel} ${adj.percent > 0 ? "+" : ""}${adj.percent}%`;
                            }).join(", ")}
                          </p>
                        </div>
                      </div>
                      {isAdjustmentsExpanded ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
                    </button>

                    {isAdjustmentsExpanded && (
                      <div className="mt-4 space-y-3 border-t pt-4" data-testid="text-adjustments-list">
                        {appliedAdjustments.map((adj, index) => {
                          const catLabel = adj.category === "all" ? "All Items" : capitalizeCategory(adj.category);
                          const isEditing = editingAdjustmentIndex === index;

                          return (
                            <div key={index} className="flex items-center justify-between gap-3" data-testid={`adjustment-row-${index}`}>
                              {isEditing ? (
                                <div className="flex-1 space-y-2">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="shrink-0">{catLabel}</Badge>
                                    <PercentInput
                                      value={editAdjustmentPercent}
                                      onChange={setEditAdjustmentPercent}
                                      data-testid={`input-edit-adjustment-percent-${index}`}
                                    />
                                  </div>
                                  <Input
                                    value={editAdjustmentReason}
                                    onChange={(e) => setEditAdjustmentReason(e.target.value)}
                                    placeholder="Reason (optional)"
                                    className="text-sm"
                                    data-testid={`input-edit-adjustment-reason-${index}`}
                                  />
                                  <div className="flex gap-2">
                                    <Button
                                      type="button"
                                      size="sm"
                                      onClick={() => {
                                        const newPercent = parseFloat(editAdjustmentPercent);
                                        if (isNaN(newPercent)) return;
                                        const oldFactor = 1 + adj.percent / 100;
                                        const newFactor = 1 + newPercent / 100;
                                        const correctionFactor = newFactor / oldFactor;
                                        setLineItems(prev => prev.map(item => {
                                          if (adj.category === "all" || item.category === adj.category) {
                                            return { ...item, unitCost: Math.round(item.unitCost * correctionFactor) };
                                          }
                                          return item;
                                        }));
                                        setAppliedAdjustments(prev => {
                                          const updated = [...prev];
                                          updated[index] = { ...adj, percent: newPercent, reason: editAdjustmentReason };
                                          return updated;
                                        });
                                        setEditingAdjustmentIndex(null);
                                        toast({ title: "Adjustment updated", description: `${catLabel} now ${newPercent > 0 ? "+" : ""}${newPercent}%` });
                                      }}
                                      data-testid={`button-save-adjustment-${index}`}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setEditingAdjustmentIndex(null)}
                                      data-testid={`button-cancel-edit-adjustment-${index}`}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Badge variant="outline">{catLabel}</Badge>
                                    <span className={`font-medium text-sm ${adj.percent >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                                      {adj.percent > 0 ? "+" : ""}{adj.percent}%
                                    </span>
                                    {adj.reason && (
                                      <span className="text-xs text-muted-foreground">{adj.reason}</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        setEditingAdjustmentIndex(index);
                                        setEditAdjustmentPercent(String(adj.percent));
                                        setEditAdjustmentReason(adj.reason);
                                      }}
                                      data-testid={`button-edit-adjustment-${index}`}
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        const reverseFactor = 1 / (1 + adj.percent / 100);
                                        setLineItems(prev => prev.map(item => {
                                          if (adj.category === "all" || item.category === adj.category) {
                                            return { ...item, unitCost: Math.round(item.unitCost * reverseFactor) };
                                          }
                                          return item;
                                        }));
                                        setAppliedAdjustments(prev => prev.filter((_, i) => i !== index));
                                        toast({ title: "Adjustment removed", description: `${catLabel} ${adj.percent > 0 ? "+" : ""}${adj.percent}% has been reversed` });
                                      }}
                                      data-testid={`button-remove-adjustment-${index}`}
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Expert Validation Flags */}
              {analysisResult.validationFlags && analysisResult.validationFlags.length > 0 && (
                <Card data-testid="card-validation-flags">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 text-left gap-3"
                    onClick={() => setIsReviewFindingsExpanded(!isReviewFindingsExpanded)}
                    data-testid="button-toggle-review-findings"
                    aria-expanded={isReviewFindingsExpanded}
                  >
                    <div className="flex items-start gap-3">
                      <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-primary mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs sm:text-base font-semibold">Review Findings</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {analysisResult.validationFlags.length} {analysisResult.validationFlags.length === 1 ? "item" : "items"} flagged for your attention
                        </p>
                      </div>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${isReviewFindingsExpanded ? "rotate-180" : ""}`} />
                  </button>
                  {isReviewFindingsExpanded && (
                    <CardContent className="pt-0">
                      <div className="space-y-3">
                      {analysisResult.validationFlags.map((flag: ValidationFlag, i: number) => (
                        <div
                          key={`vflag-${i}`}
                          className="flex items-start gap-3 p-3 rounded-md border"
                          data-testid={`card-validation-flag-${i}`}
                        >
                          <div className="shrink-0 mt-0.5">
                            {flag.type === "scope_gap" && <AlertCircle className="h-4 w-4 text-amber-500" />}
                            {flag.type === "measurement_issue" && <AlertCircle className="h-4 w-4 text-red-500" />}
                            {flag.type === "quantity_concern" && <AlertCircle className="h-4 w-4 text-amber-500" />}
                            {flag.type === "unit_mismatch" && <AlertCircle className="h-4 w-4 text-red-500" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className="text-xs">
                                {flag.type === "scope_gap" ? "Scope Gap" :
                                 flag.type === "measurement_issue" ? "Measurement" :
                                 flag.type === "quantity_concern" ? "Quantity" :
                                 "Unit Mismatch"}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={`text-xs ${
                                  flag.confidence === "high" ? "border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-400" :
                                  flag.confidence === "medium" ? "border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400" :
                                  "border-red-300 text-red-700 dark:border-red-700 dark:text-red-400"
                                }`}
                              >
                                {flag.confidence} confidence
                              </Badge>
                            </div>
                            <p className="text-sm mt-1.5" data-testid={`text-validation-flag-detail-${i}`}>
                              {flag.itemName && <span className="font-medium">{flag.itemName}: </span>}
                              {flag.detail}
                            </p>
                            {flag.suggestion && (
                              <p className="text-xs text-muted-foreground mt-1" data-testid={`text-validation-flag-suggestion-${i}`}>
                                Suggestion: {flag.suggestion}
                              </p>
                            )}
                            {(flag.suggestedUnitCost != null || flag.suggestedQuantity != null || flag.suggestRemoval) && typeof flag.itemIndex === "number" && (
                              <div className="flex items-center gap-3 mt-2 flex-wrap">
                                {flag.suggestRemoval && (
                                  <span className="text-xs text-muted-foreground">
                                    Suggested action: <span className="font-medium text-red-600 dark:text-red-400">Remove from quote</span>
                                  </span>
                                )}
                                {flag.suggestedUnitCost != null && !flag.suggestRemoval && (
                                  <span className="text-xs text-muted-foreground">
                                    Suggested price: <span className="font-medium text-foreground">{getMarketCurrency(user?.market).symbol}{flag.suggestedUnitCost.toFixed(2)}/{lineItems[flag.itemIndex]?.unit || "unit"}</span>
                                  </span>
                                )}
                                {flag.suggestedQuantity != null && !flag.suggestRemoval && (
                                  <span className="text-xs text-muted-foreground">
                                    Suggested qty: <span className="font-medium text-foreground">{flag.suggestedQuantity} {lineItems[flag.itemIndex]?.unit || "units"}</span>
                                  </span>
                                )}
                                {appliedFlags.has(i) ? (
                                  <Badge variant="outline" className={`text-xs ${flag.suggestRemoval ? "text-red-600 dark:text-red-400 border-red-300 dark:border-red-700" : "text-emerald-600 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700"}`} data-testid={`badge-flag-applied-${i}`}>
                                    {flag.suggestRemoval ? "Removed" : "Applied"}
                                  </Badge>
                                ) : (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => applyFlagMutation.mutate({ flag, flagIndex: i })}
                                    disabled={guestMode || applyFlagMutation.isPending}
                                    data-testid={`button-apply-flag-${i}`}
                                  >
                                    {applyFlagMutation.isPending ? "Applying..." : "Apply"}
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                  )}
                </Card>
              )}

              {/* Suggested Items from Expert Validation */}
              {analysisResult.suggestedItems && analysisResult.suggestedItems.filter((_, i) => !dismissedSuggestions.has(i)).length > 0 && (
                <Card className="bg-background" data-testid="card-suggested-items">
                    <button
                    type="button"
                    onClick={() => setSuggestedItemsExpanded(prev => !prev)}
                    className="w-full flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 text-left gap-3"
                  >
                    <div className="flex items-start gap-3">
                      <Lightbulb className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs sm:text-base font-semibold">Suggested Additional Items</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {analysisResult.suggestedItems.filter((_: SuggestedItem, i: number) => !dismissedSuggestions.has(i)).length} items identified that may be required
                        </p>
                      </div>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${suggestedItemsExpanded ? "rotate-180" : ""}`} />
                  </button>
                  {suggestedItemsExpanded && (
                  <CardContent className="pt-0">
                    <div className="space-y-3">
                      {analysisResult.suggestedItems.map((item: SuggestedItem, i: number) => {
                        if (dismissedSuggestions.has(i)) return null;
                        const total = item.quantity * item.unitCost;
                        const isDescOpen = expandedSuggestions.has(i);
                        return (
                          <div
                            key={`suggestion-${i}`}
                            className="p-3 rounded-md border space-y-2 bg-background"
                            data-testid={`card-suggested-item-${i}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-sm" data-testid={`text-suggested-item-name-${i}`}>{item.name}</span>
                                {isDescOpen && (
                                  <>
                                    <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5 italic" data-testid={`text-suggested-item-reason-${i}`}>
                                      {item.reason}
                                    </p>
                                  </>
                                )}
                                <button
                                  type="button"
                                  onClick={() => toggleSuggestion(i)}
                                  className="text-xs text-primary underline underline-offset-2 mt-1 block"
                                >
                                  {isDescOpen ? "Hide" : "Details"}
                                </button>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-sm font-medium" data-testid={`text-suggested-item-total-${i}`}>
                                  {formatMoney(total, user?.market)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {item.quantity} {item.unit} x {formatMoney(item.unitCost, user?.market)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 pt-1">
                              <Button
                                type="button"
                                size="sm"
                                className="text-xs h-7 px-2.5"
                                onClick={() => {
                                  const newLineItem: AnalyzedLineItem = {
                                    section: item.section,
                                    category: item.category,
                                    trade: item.trade,
                                    name: item.name,
                                    description: item.description,
                                    inclusions: item.inclusions,
                                    exclusions: item.exclusions,
                                    quantity: item.quantity,
                                    unit: item.unit,
                                    unitCost: item.unitCost,
                                    labourHours: item.labourHours,
                                    labourCost: item.labourCost,
                                    materialCost: item.materialCost,
                                    plantCost: item.plantCost,
                                    isOptional: false,
                                  };
                                  setLineItems(prev => [...prev, newLineItem]);
                                  baseLineItemsRef.current = [...baseLineItemsRef.current, { ...newLineItem }];
                                  sliderBaseRef.current = null;
                                  setDismissedSuggestions(prev => {
                                    const next = new Set(Array.from(prev));
                                    next.add(i);
                                    return next;
                                  });
                                  toast({
                                    title: "Item added to quote",
                                    description: `${item.name} has been added to your work items`,
                                  });
                                }}
                                data-testid={`button-accept-suggestion-${i}`}
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Add to Quote
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="text-xs h-7 px-2.5"
                                onClick={() => {
                                  setDismissedSuggestions(prev => {
                                    const next = new Set(Array.from(prev));
                                    next.add(i);
                                    return next;
                                  });
                                }}
                                data-testid={`button-dismiss-suggestion-${i}`}
                              >
                                <X className="h-3 w-3 mr-1" />
                                Dismiss
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                  )}
                </Card>
              )}

              {/* Quote Format — pick before reviewing items so the builder
                  knows upfront whether they need to fine-tune every line */}
              <Card>
                <CardContent className="pt-5 pb-4">
                  <p className="text-sm font-medium mb-3">Quote Format</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setSummaryMode(false)}
                      className={`flex flex-col gap-1 p-3 rounded-lg border-2 text-left transition-colors ${!summaryMode ? "border-primary bg-primary/5" : "border-border hover:border-muted"}`}
                    >
                      <div className="flex items-center gap-2">
                        <FileText className={`h-4 w-4 ${!summaryMode ? "text-primary" : "text-muted-foreground"}`} />
                        <span className="font-medium text-sm">Detailed Quote</span>
                      </div>
                      <span className="text-xs text-muted-foreground">Every work item listed with individual pricing</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSummaryMode(true)}
                      className={`flex flex-col gap-1 p-3 rounded-lg border-2 text-left transition-colors ${summaryMode ? "border-primary bg-primary/5" : "border-border hover:border-muted"}`}
                    >
                      <div className="flex items-center gap-2">
                        <LayoutGrid className={`h-4 w-4 ${summaryMode ? "text-primary" : "text-muted-foreground"}`} />
                        <span className="font-medium text-sm">Summary Quote</span>
                      </div>
                      <span className="text-xs text-muted-foreground">Categories and grouped works only</span>
                    </button>
                  </div>
                </CardContent>
              </Card>

              {/* Line Items */}
              <Card className="bg-background">
                <CardHeader className="pb-3">
                  <div className="space-y-1">
                    <CardTitle className="text-base sm:text-xl" data-testid="estimate-work-items-heading">Estimated Work Items</CardTitle>
                    <CardDescription>Review and adjust quantities or prices as needed</CardDescription>
                  </div>
                </CardHeader>
                <CardContent>
                  <QuoteScopeReview className="mb-3" notes={analysisResult?.reviewerNotes}
                    onChange={guestMode ? undefined : notes => setAnalysisResult(prev => prev ? { ...prev, reviewerNotes: notes } : prev)}
                    onAdd={guestMode ? undefined : item => {
                      setLineItems(prev => [...prev, { ...item,
                        ...(activeScopeTab && activeScopeTab !== "__combined__" ? { scopeName: activeScopeTab } : {}) }]);
                      setActiveCategoryFilter(null);
                      setCollapsedStages(prev => { const next = new Set(prev); next.delete(getItemStage(item.category, undefined, item.name)); return next; });
                    }} />
                  {/* Row 1: scope (project) tabs — only for multi-scope quotes */}
                  {analysisResult?.isMultiScope && (analysisResult.scopes?.length ?? 0) > 1 && (() => {
                    const sym = getMarketCurrency(user?.market).symbol;
                    const scopeTotal = (scopeKey: string) =>
                      lineItems
                        .filter(li => !li.isOptional && (scopeKey === "__combined__" || li.scopeName === scopeKey))
                        .reduce((sum, li) => sum + (li.quantity * li.unitCost), 0);
                    const tabs = [
                      { key: "__combined__", label: "Combined" },
                      ...(analysisResult.scopes ?? []).map(s => ({ key: s.name, label: s.name })),
                    ];
                    const getScopeIcon = (label: string) => {
                      const l = label.toLowerCase();
                      if (l === "combined" || l === "all") return LayoutGrid;
                      if (l.includes("shared")) return Share2;
                      if (l.includes("extension")) return Building2;
                      if (l.includes("loft")) return ArrowUpFromLine;
                      if (l.includes("renovation") || l.includes("refurb")) return Hammer;
                      if (l.includes("kitchen")) return Wrench;
                      if (l.includes("bathroom")) return Droplets;
                      if (l.includes("garage")) return Car;
                      if (l.includes("new build")) return Landmark;
                      if (l.includes("roof")) return Home;
                      if (l.includes("structural") || l.includes("opening")) return Columns;
                      return HardHat;
                    };
                    return (
                      <div className="flex flex-wrap gap-2 mb-3" data-testid="scope-tabs">
                        {tabs.map(tab => {
                          const Icon = getScopeIcon(tab.label);
                          const isActive = activeScopeTab === tab.key;
                          const shortLabel = shortenScopeLabel(tab.label);
                          return (
                            <button
                              key={tab.key}
                              type="button"
                              onClick={() => setActiveScopeTab(tab.key)}
                              data-testid={`scope-tab-${tab.key}`}
                              className={`flex flex-col items-center gap-1 px-3 py-3 rounded-lg border transition-colors w-[76px] ${
                                isActive
                                  ? "bg-primary/10 text-primary border-primary"
                                  : "bg-card text-muted-foreground border-border hover:bg-muted/50"
                              }`}
                            >
                              <Icon className="h-5 w-5 shrink-0" />
                              <span className="text-[11px] font-medium text-center leading-tight">{shortLabel}</span>
                              <span className={`text-[11px] font-semibold ${isActive ? "text-primary" : "text-foreground"}`}>
                                {sym}{scopeTotal(tab.key).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                  <QuoteAddItemsLayout enabled={!guestMode}
                    addedTaskKeys={new Set(lineItems.map(item => item.catalogueTaskKey
                      ?? (item.costLibraryTaskId ? `default:${item.costLibraryTaskId}` : "")).filter(Boolean))}
                    onAdd={async task => {
                      const item = await createCatalogueQuoteItem(task);
                      const added = { ...item, ...(activeScopeTab && activeScopeTab !== "__combined__"
                        ? { scopeName: activeScopeTab } : {}) };
                      setLineItems(prev => [...prev, added]);
                      baseLineItemsRef.current = [...baseLineItemsRef.current, added];
                      sliderBaseRef.current = null;
                      setActiveCategoryFilter(null);
                      setCollapsedStages(prev => {
                        const next = new Set(prev);
                        next.delete(getItemStage(item.category, item.trade, item.name));
                        return next;
                      });
                      const group = getItemSubGroup(item.category, item.name);
                      if (group) setExpandedServiceGroups(prev => new Set([...prev,
                        `${getItemStage(item.category, item.trade, item.name)}::${group}`]));
                      toast({ title: "Work item added", description: `${item.name} added to this quote only.` });
                    }}
                    toolbar={
                  /* Category filters are hidden in Summary mode. */
                  !summaryMode && (() => {
                    const sym = getMarketCurrency(user?.market).symbol;
                    const isMulti = !!(analysisResult?.isMultiScope && (analysisResult.scopes?.length ?? 0) > 1);
                    // Respect the active scope tab for multi-scope; use all items for single-scope.
                    const scopeFiltered = isMulti
                      ? lineItems.filter(li =>
                          activeScopeTab === "__combined__" ||
                          li.scopeName === activeScopeTab ||
                          li.scopeName === SHARED_SCOPE_NAME
                        )
                      : lineItems;
                    // Exclude optional items from category tab totals; they are shown separately below.
                    const scopeFilteredCore = scopeFiltered.filter(li => !li.isOptional);
                    const catTotal = (groupLabel: string) =>
                      scopeFilteredCore
                        .filter(li => getDisplayGroup(li as any) === groupLabel)
                        .reduce((sum, li) => sum + (li.quantity * li.unitCost), 0);
                    const visibleGroups = DISPLAY_CATEGORIES.filter(group => catTotal(group.label) > 0);
                    if (visibleGroups.length === 0) return null;
                    return (
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(56px,1fr))] gap-1.5 sm:grid-cols-[repeat(auto-fill,minmax(76px,1fr))] sm:gap-2" data-testid="category-filter-tabs" role="group" aria-label="Work item categories">
                        <button
                          type="button"
                          onClick={() => setActiveCategoryFilter(null)}
                          data-testid="category-tab-all"
                          className={`flex min-w-0 flex-col items-center gap-0.5 px-1 py-1.5 sm:gap-1 sm:px-2 sm:py-2.5 rounded-lg border transition-colors ${
                            activeCategoryFilter === null
                              ? "bg-primary/10 text-primary border-primary"
                              : "bg-card text-muted-foreground border-border hover:bg-muted/50 cursor-pointer"
                          }`}
                        >
                          <LayoutGrid className={`h-4 w-4 shrink-0 ${activeCategoryFilter === null ? "text-primary" : "text-muted-foreground"}`} />
                          <span className="text-[10px] font-medium text-center leading-tight">All</span>
                          <span className={`text-[10px] font-semibold ${activeCategoryFilter === null ? "text-primary" : "text-foreground"}`}>
                            {sym}{scopeFilteredCore.reduce((s, li) => s + li.quantity * li.unitCost, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </span>
                        </button>
                        {visibleGroups.map(group => {
                          const Icon = group.icon;
                          const isActive = activeCategoryFilter === group.label;
                          const total = catTotal(group.label);
                          return (
                            <button
                              key={group.label}
                              type="button"
                              onClick={() => setActiveCategoryFilter(isActive ? null : group.label)}
                              data-testid={`category-tab-${group.label.toLowerCase()}`}
                              className={`flex min-w-0 flex-col items-center gap-0.5 px-1 py-1.5 sm:gap-1 sm:px-2 sm:py-2.5 rounded-lg border transition-colors ${
                                isActive
                                  ? "bg-primary/10 text-primary border-primary"
                                  : "bg-card text-muted-foreground border-border hover:bg-muted/50 cursor-pointer"
                              }`}
                            >
                              <Icon className={`h-4 w-4 shrink-0 ${isActive ? "text-primary" : "text-muted-foreground"}`} />
                              <span className="text-[10px] font-medium text-center leading-tight">{group.label}</span>
                              <span className={`text-[10px] font-semibold ${isActive ? "text-primary" : "text-foreground"}`}>
                                {sym}{total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}>
                  <div className="space-y-4">
                    {(() => {
                      const isMulti = !!(analysisResult?.isMultiScope && (analysisResult.scopes?.length ?? 0) > 1);
                      const rawWithIndex = lineItems
                        .map((item, index) => ({ item, index }))
                        .filter(({ item }) =>
                          !isMulti ||
                          activeScopeTab === "__combined__" ||
                          item.scopeName === activeScopeTab ||
                          // Include shared site-cost items in every individual scope tab
                          // so the view is complete (prelims, scaffold, skip hire etc.)
                          item.scopeName === SHARED_SCOPE_NAME,
                        );
                      // In combined view, merge items that represent the same work
                      // type across scopes. "Extension Foundation" and "Porch
                      // Foundation" both strip to "Foundation" and become one row.
                      // The merge key encodes work-type name + unit dimension so
                      // sqm and "each" items never collapse into the same row.
                      const withIndex = (() => {
                        if (!isMulti || activeScopeTab !== "__combined__") return rawWithIndex;
                        // Equivalence-based grouping: same work type across scopes merges
                        // even when the AI worded the names differently ("Waste Removal
                        // Skips" + "Porch Waste Skip"). Core and optional items are grouped
                        // separately so cost never crosses the quoted-price boundary.
                        const mergeBucket = (bucket: typeof rawWithIndex) => groupEquivalentLineItems(
                          bucket.map(({ item }) => ({
                            name: item.name || "",
                            category: item.category || "",
                            unit: item.unit || "",
                            unitCost: item.unitCost,
                          })),
                        ).map(g => ({ ...g, bucket }));
                        const groups = [
                          ...mergeBucket(rawWithIndex.filter(({ item }) => !item.isOptional)),
                          ...mergeBucket(rawWithIndex.filter(({ item }) => !!item.isOptional)),
                        ];
                        return groups.map(({ indices, displayName, bucket }) => {
                          const members = indices.map(i => bucket[i]);
                          const first = members[0];
                          if (members.length === 1) {
                            // Show the clean work-type label rather than a location-prefixed one.
                            return { item: { ...first.item, name: displayName }, index: first.index };
                          }
                          const mergedTotal = members.reduce((s, m) => s + m.item.quantity * m.item.unitCost, 0);
                          const isBundled = (first.item.unit || "").toLowerCase() === "item";
                          const mergedQty = isBundled ? 1 : members.reduce((s, m) => s + m.item.quantity, 0);
                          return {
                            item: {
                              ...first.item,
                              name: displayName,
                              quantity: mergedQty,
                              unitCost: isBundled ? mergedTotal : (mergedQty > 0 ? mergedTotal / mergedQty : first.item.unitCost),
                              labourCost: members.reduce((s, m) => s + (m.item.labourCost ?? 0), 0),
                              materialCost: members.reduce((s, m) => s + (m.item.materialCost ?? 0), 0),
                              plantCost: members.reduce((s, m) => s + (m.item.plantCost ?? 0), 0),
                            },
                            index: first.index,
                          };
                        });
                      })();
                      // Split into core items (included in quoted price) and optional items (separate section).
                      const coreWithIndex = withIndex.filter(({ item }) => !item.isOptional);
                      const optionalWithIndex = withIndex.filter(({ item }) => !!item.isOptional);
                      const optionalSubtotal = optionalWithIndex.reduce((sum, { item }) => sum + (item.quantity * item.unitCost), 0);
                      const sym = getMarketCurrency(user?.market).symbol;

                      // Summary mode: flat category cards with editable sub-group rows.
                      if (summaryMode) {
                        // Derive optional totals directly from lineItems so scope-tab filtering
                        // (which can silently drop optional items that lack a scopeName) never
                        // hides the Optional Works row when items genuinely exist.
                        const summaryOptionalItems = lineItems.filter(li => !!li.isOptional);
                        const summaryOptionalSubtotal = summaryOptionalItems.reduce((s, li) => s + li.quantity * li.unitCost, 0);

                        // Scale all items in a stage+subgroup proportionally to a new total.
                        const applyGroupTotal = (stage: string, sg: string | null, newTotal: number) => {
                          const members = coreWithIndex.filter(({ item }) => {
                            if (getEffectiveQuoteStage(item) !== stage) return false;
                            const itemSg = getEffectiveQuoteSubGroup(item);
                            return sg === null ? itemSg === null : itemSg === sg;
                          });
                          const currentTotal = members.reduce((s, { item }) => s + item.quantity * item.unitCost, 0);
                          setLineItems(prev => {
                            const updated = [...prev];
                            for (const { index, item } of members) {
                              if (currentTotal === 0) {
                                // Distribute evenly when all items are zero
                                updated[index] = { ...item, unitCost: members.length > 0 ? (item.quantity > 0 ? newTotal / members.length / item.quantity : 0) : 0 };
                              } else {
                                updated[index] = { ...item, unitCost: item.unitCost * (newTotal / currentTotal) };
                              }
                            }
                            return updated;
                          });
                        };

                        const activeStagesSummary = STAGE_ORDER.filter(stage =>
                          coreWithIndex.some(({ item }) => getEffectiveQuoteStage(item) === stage)
                        );
                        return (
                          <>
                            <div className="space-y-2">
                              {activeStagesSummary.map(stage => {
                                const stageItems = coreWithIndex.filter(({ item }) => getEffectiveQuoteStage(item) === stage);
                                const stageTotal = stageItems.reduce((sum, { item }) => sum + item.quantity * item.unitCost, 0);
                                const StageIcon = STAGE_ICONS[stage];

                                // Build ordered sub-groups for this stage
                                const sgOrder = STAGE_SUB_GROUP_ORDER[stage] ?? [];
                                const sgTotals = new Map<string, number>();
                                for (const { item } of stageItems) {
                                  const sg = getEffectiveQuoteSubGroup(item);
                                  if (sg) sgTotals.set(sg, (sgTotals.get(sg) ?? 0) + item.quantity * item.unitCost);
                                }
                                const orderedSgs: { sg: string; label: string; total: number }[] = [];
                                for (const sg of sgOrder) {
                                  if (sgTotals.has(sg)) orderedSgs.push({ sg, label: ITEM_SUB_GROUP_LABELS[sg] ?? sg, total: sgTotals.get(sg)! });
                                }
                                for (const [sg, total] of sgTotals) {
                                  if (!sgOrder.includes(sg as ItemSubGroupId)) orderedSgs.push({ sg, label: ITEM_SUB_GROUP_LABELS[sg as ItemSubGroupId] ?? sg, total });
                                }
                                const hasSubGroups = orderedSgs.length > 0;

                                return (
                                  <div key={stage} className="border border-primary/30 rounded-lg overflow-hidden">
                                    {/* Category header row */}
                                    <div className="flex items-center gap-3 px-4 py-3 bg-card">
                                      {StageIcon && <StageIcon className="h-4 w-4 text-primary shrink-0" />}
                                      <span className="font-medium text-sm flex-1">{quoteHeadingLabel(headingNames, stage)}</span>
                                      {hasSubGroups ? (
                                        // Total is auto-derived from sub-groups — not directly editable
                                        <span className="font-semibold text-sm text-primary">
                                          {sym}{stageTotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                        </span>
                                      ) : (
                                        // No sub-groups — edit the whole stage total directly
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-sm text-muted-foreground">{sym}</span>
                                          <Input
                                            type="number"
                                            value={stageTotal > 0 ? Math.round(stageTotal) : ""}
                                            onChange={(e) => applyGroupTotal(stage, null, parseFloat(e.target.value) || 0)}
                                            onBlur={(e) => { if (e.target.value === "") applyGroupTotal(stage, null, 0); }}
                                            placeholder="0"
                                            className="h-8 w-28 text-right text-sm"
                                          />
                                        </div>
                                      )}
                                    </div>
                                    {/* Sub-group rows */}
                                    {hasSubGroups && (
                                      <div className="border-t border-primary/20 divide-y divide-muted/60">
                                        {orderedSgs.map(({ sg, label, total }) => (
                                          <div key={sg} className="flex items-center gap-3 px-4 py-2.5 bg-background">
                                            <span className="text-sm text-primary font-medium flex-1 pl-2">{quoteHeadingLabel(headingNames, label, "subgroup")}</span>
                                            <div className="flex items-center gap-1.5">
                                              <span className="text-sm text-muted-foreground">{sym}</span>
                                              <Input
                                                type="number"
                                                value={total > 0 ? Math.round(total) : ""}
                                                onChange={(e) => applyGroupTotal(stage, sg, parseFloat(e.target.value) || 0)}
                                                onBlur={(e) => { if (e.target.value === "") applyGroupTotal(stage, sg, 0); }}
                                                placeholder="0"
                                                className="h-8 w-28 text-right text-sm"
                                              />
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            {summaryOptionalItems.length > 0 && (
                              <details className="group/optional mt-2 border border-amber-300/40 rounded-lg overflow-hidden">
                                {/* Optional Works header */}
                                <summary className="cursor-pointer list-none flex items-center justify-between gap-2 px-4 py-3 bg-amber-50/40 [&::-webkit-details-marker]:hidden">
                                  <span className="text-sm text-amber-800 font-medium">
                                    Optional Works ({summaryOptionalItems.length} item{summaryOptionalItems.length !== 1 ? "s" : ""})
                                  </span>
                                  <span className="text-sm font-semibold text-amber-700">
                                    {sym}{summaryOptionalSubtotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                  </span>
                                  <ChevronRight className="h-4 w-4 shrink-0 text-amber-600 transition-transform group-open/optional:rotate-90" aria-hidden="true" />
                                </summary>
                                {/* Individual optional items */}
                                <div className="border-t border-amber-300/40 divide-y divide-amber-200/40">
                                  {summaryOptionalItems.map((li, i) => {
                                    const itemTotal = li.quantity * li.unitCost;
                                    return (
                                      <div key={i} className="flex items-center gap-3 px-4 py-2.5 bg-background">
                                        <span className="text-sm flex-1 pl-2 text-foreground">{li.name}</span>
                                        <span className="text-sm font-medium text-muted-foreground shrink-0">
                                          {sym}{itemTotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </details>
                            )}
                          </>
                        );
                      }

                      const categoryFilteredItems = activeCategoryFilter === null
                        ? coreWithIndex
                        : coreWithIndex.filter(({ item }) => getDisplayGroup(item as any) === activeCategoryFilter);
                      const activeStages = STAGE_ORDER.filter(stage =>
                        categoryFilteredItems.some(({ item }) => getEffectiveQuoteStage(item) === stage)
                      );
                      return (
                        <DndContext
                          sensors={placementSensors}
                          onDragEnd={handlePlacementDragEnd}
                        >
                        {activeStages.map(stage => {
                        const stageItems = categoryFilteredItems.filter(({ item }) => getEffectiveQuoteStage(item) === stage);
                        const isCollapsed = collapsedStages.has(stage);
                        const stageSubtotal = stageItems.reduce((sum, { item }) => sum + (item.quantity * item.unitCost), 0);
                        return (
                          <div key={stage} className="quote-placement-stage border border-primary/30 rounded-lg overflow-hidden">
                            {/* Stage header */}
                            <QuotePlacementDropTarget
                              id={`stage-${stage}`}
                              data={{ kind: "stage", stage, subGroup: null }}
                            >
                              <button
                                type="button"
                                onClick={() => toggleStage(stage)}
                                aria-expanded={!isCollapsed}
                                className="w-full flex items-center gap-3 py-3 px-4 bg-sidebar hover:bg-sidebar-accent transition-colors group"
                              >
                                {(() => {
                                  const StageIcon = STAGE_ICONS[stage];
                                  return StageIcon ? <StageIcon className="h-4 w-4 text-primary shrink-0" /> : null;
                                })()}
                                <span className="font-semibold text-sm flex-1 text-left text-primary">{quoteHeadingLabel(headingNames, stage)}</span>
                                <span className="text-xs text-slate-400">{stageItems.length} {stageItems.length === 1 ? "item" : "items"}</span>
                                <span className="text-xs font-semibold text-primary ml-2">{sym}{stageSubtotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                                {isCollapsed
                                  ? <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
                                  : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
                                }
                              </button>
                            </QuotePlacementDropTarget>
                            {/* Stage items */}
                            {!isCollapsed && (
                              <div className="space-y-3 p-4 border-t border-primary/20">
                                {(() => {
                                  // Sub-group pre-processing: for Services-stage items (electrics,
                                  // plumbing, heating), sort so same sub-group items are adjacent
                                  // and insert collapsible group headers at each boundary.
                                  const hasSubGroups = stageItems.some(({ item: si }) => getEffectiveQuoteSubGroup(si) !== null);
                                  const renderItems = groupQuoteRows(stageItems, ({ item }) => getEffectiveQuoteSubGroup(item));
                                  const subGroupStats = new Map<string, { count: number; total: number }>();
                                  if (hasSubGroups) {
                                    for (const { item: si } of stageItems) {
                                      const sg = getEffectiveQuoteSubGroup(si);
                                      if (sg) {
                                        const s = subGroupStats.get(sg) ?? { count: 0, total: 0 };
                                        s.count++; s.total += si.quantity * si.unitCost;
                                        subGroupStats.set(sg, s);
                                      }
                                    }
                                  }
                                  return renderItems.flatMap(({ item, index }, sortedIndex, sortedArr) => {
                                  const subGroup = hasSubGroups ? getEffectiveQuoteSubGroup(item) : null;
                                  const prevSg = sortedIndex > 0 ? getEffectiveQuoteSubGroup(sortedArr[sortedIndex - 1].item) : "___none___";
                                  const isFirstInSubGroup = subGroup !== null && subGroup !== prevSg;
                                  const sgKey = subGroup ? `${stage}::${subGroup}` : null;
                                  const isSubGroupCollapsed = sgKey ? !expandedServiceGroups.has(sgKey) : false;
                                  const sgStats = subGroup ? subGroupStats.get(subGroup) : null;
                                  const isItemExpanded = expandedItems.has(index);
                                  const itemSubtotal = item.quantity * item.unitCost;
                                  const navMatch = item.subcategory
                                    ? COST_NAV_SECTIONS.find(s => s.items.some(i => i.id === item.subcategory))
                                    : COST_NAV_SECTIONS.find(s => s.items.some(i => i.categoryFilter === item.category && i.tradeFilter === item.trade));
                                  const _sgIconMap: Partial<Record<ItemSubGroupId, LucideIcon>> = {
                                    // Demolition / Substructure / Structure
                                    demolition_works: Trash2, ground_works: Landmark,
                                    masonry_works: Building2, structural_steel: Columns,
                                    thermal_insulation: Layers,
                                    // Roofing / External
                                    pitched_roofing: Home, flat_roofing: Layers, roof_windows: Sun, fascias_guttering: Droplets,
                                    windows_doors: DoorOpen,
                                    // Services
                                    first_fix_electrics: Zap, second_fix_electrics: Zap,
                                    first_fix_plumbing: Droplets, second_fix_plumbing: Droplets,
                                    heating: Flame,
                                    // Finishes
                                    drylining_works: Layers, second_fix_joinery: Hammer,
                                    making_good: Wrench, decorative_finishes: PaintBucket,
                                  };
                                  const _catIcon = navMatch?.icon ?? getCategoryIcon(item.category);
                                  const CategoryIcon = (subGroup ? (_sgIconMap[subGroup as ItemSubGroupId] ?? null) : null) ?? _catIcon;
                                  const lab = Number(item.labourCost ?? 0);
                                  const mat = Number(item.materialCost ?? 0);
                                  const plt = Number(item.plantCost ?? 0);
                                  const result: React.ReactNode[] = [];
                                  if (isFirstInSubGroup && sgKey && sgStats) {
                                    result.push(
                                      <QuotePlacementDropTarget
                                        key={`sg-${sgKey}`}
                                        id={`subgroup-${sgKey}`}
                                        data={{ kind: "subgroup", stage, subGroup }}
                                      >
                                        <button
                                          type="button"
                                          onClick={() => toggleServiceGroup(sgKey)}
                                          className="w-full flex items-center gap-2 px-3 py-2.5 bg-muted/30 hover:bg-muted/50 rounded-md border border-border/40 transition-colors text-left"
                                        >
                                          <ChevronRight className={`h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform duration-150${!isSubGroupCollapsed ? " rotate-90" : ""}`} />
                                          <span className="font-semibold text-sm flex-1 text-primary">{quoteHeadingLabel(headingNames, ITEM_SUB_GROUP_LABELS[subGroup as ItemSubGroupId], "subgroup")}</span>
                                          <span className="text-xs font-bold text-amber-500 mr-2">{sgStats.count} {sgStats.count === 1 ? "item" : "items"}</span>
                                          <span className="text-xs font-semibold">{sym}{Math.round(sgStats.total).toLocaleString()}</span>
                                        </button>
                                      </QuotePlacementDropTarget>
                                    );
                                  }
                                  if (!isSubGroupCollapsed) {
                                    result.push(
                                      <React.Fragment key={item.name + index}>
                                      <DraggableQuoteRow
                                        itemKey={`${index}-${item.name}`}
                                        index={index}
                                        stage={stage}
                                        subGroup={subGroup}
                                      >
                                      <div id={`quote-work-item-${index}`} className="rounded-md border border-border overflow-hidden">
                                      {/* Compact summary row */}
                                      <div className="flex items-center gap-2 px-3 py-2 bg-card">
                                        {CategoryIcon ? <CategoryIcon className={`h-4 w-4 shrink-0 ${subGroup ? "text-amber-500" : "text-primary"}`} /> : <Package className="h-4 w-4 shrink-0 text-muted-foreground" />}
                                        {/* Accordion toggle: rotating chevron + name */}
                                        <button
                                          type="button"
                                          onClick={() => toggleItem(index)}
                                          className="flex-1 flex items-center gap-1.5 min-w-0 text-left group"
                                          data-testid={`button-expand-item-${index}`}
                                        >
                                          <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-150${isItemExpanded ? " rotate-90" : ""}`} />
                                          <span className="flex-1 text-xs sm:text-sm font-medium [overflow-wrap:anywhere] line-clamp-2 leading-tight sm:line-clamp-none sm:truncate sm:leading-normal select-none min-w-0 group-hover:text-foreground/80">
                                            {(() => {
                                              let dn = item.name || "Unnamed item";
                                              // Legacy bundled "First Fix" items rescued into first-fix electrics
                                              // display as "First-Fix Wiring" to match their sub-group context.
                                              if (subGroup === "first_fix_electrics" && /^first.?fix$/i.test(dn.trim())) {
                                                dn = "First-Fix Wiring";
                                              }
                                              return dn.replace(/\b\w/g, l => l.toUpperCase());
                                            })()}
                                          </span>
                                        </button>
                                        {/* Shared project-wide cost badge (multi-scope quotes) */}
                                        {item.scopeName === SHARED_SCOPE_NAME && !!analysisResult?.isMultiScope && (
                                          <span
                                            className="hidden sm:inline-flex items-center shrink-0 rounded-full bg-primary/10 text-primary text-[10px] font-medium px-2 py-0.5"
                                            title="Shared project-wide cost — charged once across all parts of the project"
                                            data-testid={`badge-shared-cost-${index}`}
                                          >
                                            Shared (Charged Once)
                                          </span>
                                        )}
                                        {/* Desktop: Save Item / Set Price — hidden on mobile, shown BEFORE price input */}
                                        <div className="hidden sm:flex items-center gap-2 mr-2">
                                          {!guestMode && !item.costLibraryTaskId && (
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (isAdminUser) {
                                                  const typical = item.unitCost;
                                                  const low = Math.round(typical * 0.8 * 100) / 100;
                                                  const high = Math.round(typical * 1.2 * 100) / 100;
                                                  setPublishToDefaultLibItem(item);
                                                  setPublishToDefaultLibIndex(index);
                                                  setPublishToDefaultLibName(item.name);
                                                  setPublishToDefaultLibDescription(item.description || "");
                                                  setPublishToDefaultLibCategory(item.category);
                                                  setPublishToDefaultLibUnit(item.unit);
                                                  setPublishToDefaultLibRateTypical(typical.toFixed(2));
                                                  setPublishToDefaultLibRateLow(low.toFixed(2));
                                                  setPublishToDefaultLibRateHigh(high.toFixed(2));
                                                  setPublishToDefaultLibOpen(true);
                                                } else {
                                                  setAddToCostLibItem(item);
                                                  setAddToCostLibIndex(index);
                                                  setAddToCostLibName(item.name);
                                                  setAddToCostLibCategory(item.category);
                                                  setAddToCostLibUnit(item.unit);
                                                  setAddToCostLibRate(item.unitCost.toFixed(2));
                                                  setAddToCostLibOpen(true);
                                                }
                                              }}
                                              disabled={isAdminUser ? publishToDefaultLibMutation.isPending : addToCostLibMutation.isPending}
                                              className="shrink-0 px-2 py-1 rounded text-xs font-medium text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                                              data-testid={`button-add-to-library-compact-${index}`}
                                            >
                                              Save Item
                                            </button>
                                          )}
                                          {isAdminUser && publishedToDefaultLibItems.has(index) && (
                                            <span className="shrink-0 px-2 py-1 rounded text-xs font-medium text-teal-700 dark:text-teal-300 border border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-950 whitespace-nowrap">
                                              Published ✓
                                            </span>
                                          )}
                                          {!guestMode && item.costLibraryTaskId && item.costLibraryPricingCompatible !== false && hasManualPriceOverride(item) && (
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const typical = item.unitCost;
                                                const low = Math.round(typical * 0.8 * 100) / 100;
                                                const high = Math.round(typical * 1.2 * 100) / 100;
                                                setSetPriceItem(item);
                                                setSetPriceItemIndex(index);
                                                setSetPriceRateTypical(typical.toFixed(2));
                                                setSetPriceRateLow(low.toFixed(2));
                                                setSetPriceRateHigh(high.toFixed(2));
                                                setSetPriceOpen(true);
                                              }}
                                              disabled={learnPriceMutation.isPending}
                                              className="shrink-0 px-2 py-1 rounded text-xs font-medium bg-primary text-white hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                                              data-testid={`button-remember-price-compact-${index}`}
                                            >
                                              {learnPriceMutation.isPending ? "Saving…" : "Set Price"}
                                            </button>
                                          )}
                                        </div>
                                        {/* Price input — visible on all breakpoints */}
                                        <div className="relative shrink-0 w-16 sm:w-28">
                                          <span className="absolute left-1.5 sm:left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">{sym}</span>
                                          <QuotePriceInput
                                            value={Math.round(itemSubtotal * 100) / 100}
                                            onCommit={(newSubtotal) => {
                                              if (item.quantity > 0) updateLineItem(index, "unitCost", newSubtotal / item.quantity);
                                            }}
                                            placeholder="0"
                                            className="h-8 pl-4 sm:pl-6 pr-0.5 sm:pr-2 text-xs sm:text-sm text-right"
                                            data-testid={`input-item-subtotal-${index}`}
                                          />
                                        </div>
                                        {/* Desktop: ↑↓ reorder + delete — hidden on mobile, shown AFTER price input */}
                                        <div className="hidden sm:flex items-center gap-2">
                                          <div className="flex items-center shrink-0">
                                            <button
                                              type="button"
                                              className="p-0.5 rounded text-muted-foreground disabled:opacity-30 disabled:pointer-events-none hover:text-foreground"
                                              disabled={sortedIndex === 0}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const prevOrigIdx = sortedArr[sortedIndex - 1]?.index;
                                                if (prevOrigIdx === undefined) return;
                                                setLineItems(prev => {
                                                  const items = [...prev];
                                                  [items[index], items[prevOrigIdx]] = [items[prevOrigIdx], items[index]];
                                                  return items;
                                                });
                                              }}
                                              data-testid={`button-compact-move-up-${index}`}
                                              title="Move up"
                                            >
                                              <ChevronUp className="h-4 w-4" />
                                            </button>
                                            <button
                                              type="button"
                                              className="p-0.5 rounded text-muted-foreground disabled:opacity-30 disabled:pointer-events-none hover:text-foreground"
                                              disabled={sortedIndex === stageItems.length - 1}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const nextOrigIdx = sortedArr[sortedIndex + 1]?.index;
                                                if (nextOrigIdx === undefined) return;
                                                setLineItems(prev => {
                                                  const items = [...prev];
                                                  [items[index], items[nextOrigIdx]] = [items[nextOrigIdx], items[index]];
                                                  return items;
                                                });
                                              }}
                                              data-testid={`button-compact-move-down-${index}`}
                                              title="Move down"
                                            >
                                              <ChevronDown className="h-4 w-4" />
                                            </button>
                                          </div>
                                          <QuoteRowActions index={index} onRemove={() => removeLineItem(index)} />
                                        </div>
                                        {/* Mobile ⋮ menu — hidden on sm+ */}
                                        <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                            <button
                                              type="button"
                                              className="sm:hidden shrink-0 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                                              data-testid={`button-item-menu-${index}`}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <MoreVertical className="h-4 w-4" />
                                            </button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="end" className="w-44">
                                            <DropdownMenuItem onSelect={() => toggleItem(index)}>
                                              <Eye className="h-4 w-4 mr-2" />
                                              Show Details
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            {!guestMode && item.costLibraryTaskId && item.costLibraryPricingCompatible !== false && hasManualPriceOverride(item) && (
                                              <DropdownMenuItem
                                                onSelect={() => {
                                                  const typical = item.unitCost;
                                                  const low = Math.round(typical * 0.8 * 100) / 100;
                                                  const high = Math.round(typical * 1.2 * 100) / 100;
                                                  setSetPriceItem(item);
                                                  setSetPriceItemIndex(index);
                                                  setSetPriceRateTypical(typical.toFixed(2));
                                                  setSetPriceRateLow(low.toFixed(2));
                                                  setSetPriceRateHigh(high.toFixed(2));
                                                  setSetPriceOpen(true);
                                                }}
                                              >
                                                <Save className="h-4 w-4" />
                                                Set Price
                                              </DropdownMenuItem>
                                            )}
                                             {!guestMode && !item.costLibraryTaskId && (
                                              <DropdownMenuItem
                                                onSelect={() => {
                                                  if (isAdminUser) {
                                                    const typical = item.unitCost;
                                                    const low = Math.round(typical * 0.8 * 100) / 100;
                                                    const high = Math.round(typical * 1.2 * 100) / 100;
                                                    setPublishToDefaultLibItem(item);
                                                    setPublishToDefaultLibIndex(index);
                                                    setPublishToDefaultLibName(item.name);
                                                    setPublishToDefaultLibDescription(item.description || "");
                                                    setPublishToDefaultLibCategory(item.category);
                                                    setPublishToDefaultLibUnit(item.unit);
                                                    setPublishToDefaultLibRateTypical(typical.toFixed(2));
                                                    setPublishToDefaultLibRateLow(low.toFixed(2));
                                                    setPublishToDefaultLibRateHigh(high.toFixed(2));
                                                    setPublishToDefaultLibOpen(true);
                                                  } else {
                                                    setAddToCostLibItem(item);
                                                    setAddToCostLibIndex(index);
                                                    setAddToCostLibName(item.name);
                                                    setAddToCostLibCategory(item.category);
                                                    setAddToCostLibUnit(item.unit);
                                                    setAddToCostLibRate(item.unitCost.toFixed(2));
                                                    setAddToCostLibOpen(true);
                                                  }
                                                }}
                                                disabled={isAdminUser ? publishToDefaultLibMutation.isPending : addToCostLibMutation.isPending}
                                              >
                                                <Plus className="h-4 w-4" />
                                                Save Item
                                              </DropdownMenuItem>
                                            )}
                                            {isAdminUser && publishedToDefaultLibItems.has(index) && (
                                              <DropdownMenuItem disabled>
                                                <Check className="h-4 w-4" />
                                                Published ✓
                                              </DropdownMenuItem>
                                            )}
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                              disabled={sortedIndex === 0}
                                              onSelect={() => {
                                                const prevOrigIdx = sortedArr[sortedIndex - 1]?.index;
                                                if (prevOrigIdx === undefined) return;
                                                setLineItems(prev => {
                                                  const items = [...prev];
                                                  [items[index], items[prevOrigIdx]] = [items[prevOrigIdx], items[index]];
                                                  return items;
                                                });
                                              }}
                                            >
                                              <ArrowUp className="h-4 w-4" />
                                              Move Up
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                              disabled={sortedIndex === stageItems.length - 1}
                                              onSelect={() => {
                                                const nextOrigIdx = sortedArr[sortedIndex + 1]?.index;
                                                if (nextOrigIdx === undefined) return;
                                                setLineItems(prev => {
                                                  const items = [...prev];
                                                  [items[index], items[nextOrigIdx]] = [items[nextOrigIdx], items[index]];
                                                  return items;
                                                });
                                              }}
                                            >
                                              <ArrowDown className="h-4 w-4" />
                                              Move Down
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                              className="text-destructive focus:text-destructive"
                                              onSelect={() => removeLineItem(index)}
                                            >
                                              <Trash2 className="h-4 w-4" />
                                              Remove Item
                                            </DropdownMenuItem>
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      </div>
                                      {/* Expanded detail panel */}
                                      {isItemExpanded && (
                                        <div className="border-t border-border px-3 pt-3 pb-3 space-y-3 bg-muted/20">
                                          <div>
                                            <Button
                                              type="button"
                                              variant="outline"
                                              size="sm"
                                              onClick={() => openCategoryPicker(index)}
                                              className="border-dashed text-xs gap-1.5 max-w-full"
                                              data-testid={`select-item-category-expanded-${index}`}
                                            >
                                              {CategoryIcon ? <CategoryIcon className="h-3.5 w-3.5 flex-shrink-0" /> : null}
                                              <span className="truncate">Advanced classification: {getCategoryTradeLabel(item.category, item.trade, item.subcategory, user?.market)}</span>
                                              <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                                            </Button>
                                          </div>
                                          <QuoteItemFields item={item} index={index} symbol={sym} market={user?.market}
                                            onUpdate={(field, value) => updateLineItem(index, field, value)} />
                                          <QuoteDimensions data={item.quantityDerivation} quantity={item.quantity} unit={item.unit} recipe={item.quoteCostRecipe} itemName={item.name}
                                            specification={`${item.name} ${item.description ?? ""} ${item.sourceVariant?.name ?? ""} ${item.sourceVariant?.specification ?? ""}`}
                                            onChange={(data, quantity) => updateLineItem(index, "quantity", quantity, data)} />
                                          <div data-testid={`text-breakdown-${index}`} className="pt-2 border-t">
                                            <QuoteItemRecipeBreakdown item={item} index={index} purchasingManaged
                                              sessionKey={user?.id}
                                              onMaterialAllowanceChange={approval => setLineItems(prev => prev.map((line, i) =>
                                                i === index ? { ...line, materialAllowanceApproval: approval } : line))}
                                              market={user?.market || "uk"} guestMode={guestMode}
                                              symbol={sym} onSplitChange={(field, value) => updateBreakdownField(index, field, value)}
                                              action={<button
                                                type="button"
                                                onClick={() => updateLineItem(index, "isOptional", true)}
                                                className="px-1 sm:px-2 py-1 rounded text-[10px] sm:text-xs font-medium text-amber-700 border border-amber-300 hover:bg-amber-50 dark:text-amber-400 dark:border-amber-600 dark:hover:bg-amber-950/30 transition-colors whitespace-nowrap"
                                                data-testid={`button-toggle-optional-${index}`}
                                                title="Move to Optional Works — excluded from quoted price"
                                              >
                                                Add As Optional
                                              </button>}
                                              onApply={recipe => setLineItems(prev => prev.map((current, i) => i === index
                                                ? { ...current, quoteCostRecipe: recipe, unitCost: Number(deriveCostRecipe(recipe).rateTypical),
                                                    ...quoteRecipeSplit(recipe, Number(current.quantity) || 0) }
                                                : current))} />
                                          </div>
                                        </div>
                                      )}
                                      </div>
                                      </DraggableQuoteRow>
                                      </React.Fragment>
                                    );
                                  }
                                  return result;
                                  });
                                })()}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* Optional Works section — items excluded from the quoted price */}
                      {optionalWithIndex.length > 0 && (
                        <details className={`group/optional ${optionalWorksStyles.border}`}>
                          <summary className={optionalWorksStyles.header}>
                            <QuoteOptionalHeader count={optionalWithIndex.length} total={optionalSubtotal} symbol={sym} />
                            <ChevronRight className="h-4 w-4 shrink-0 text-amber-600 transition-transform group-open/optional:rotate-90" aria-hidden="true" />
                          </summary>
                          <div className={optionalWorksStyles.body}>
                            {optionalWithIndex.map(({ item, index }) => {
                              const isItemExpanded = expandedItems.has(index);
                              const itemSubtotal = item.quantity * item.unitCost;
                              const navMatch = item.subcategory
                                ? COST_NAV_SECTIONS.find(s => s.items.some(i => i.id === item.subcategory))
                                : COST_NAV_SECTIONS.find(s => s.items.some(i => i.categoryFilter === item.category && i.tradeFilter === item.trade));
                              const CategoryIcon = navMatch?.icon ?? getCategoryIcon(item.category);
                              const lab = Number(item.labourCost ?? 0);
                              const mat = Number(item.materialCost ?? 0);
                              const plt = Number(item.plantCost ?? 0);
                              return (
                                <div key={index} id={`quote-work-item-${index}`} className={optionalWorksStyles.row}>
                                  <div className="flex items-center gap-2 px-3 py-2.5">
                                    <button type="button" onClick={() => toggleItem(index)} className="flex-1 flex items-center gap-1.5 min-w-0 text-left group" data-testid={`button-expand-item-${index}`}>
                                      <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-150${isItemExpanded ? " rotate-90" : ""}`} />
                                      <span className="flex-1 text-xs sm:text-sm font-medium [overflow-wrap:anywhere] line-clamp-2 leading-tight sm:line-clamp-none sm:truncate sm:leading-normal select-none min-w-0 group-hover:text-foreground/80">
                                        {(item.name || "Unnamed item").replace(/\b\w/g, l => l.toUpperCase())}
                                      </span>
                                    </button>
                                    <button type="button" onClick={(e) => { e.stopPropagation(); updateLineItem(index, "isOptional", false); }} className="shrink-0 px-1.5 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300 border border-amber-300 dark:border-amber-600 whitespace-nowrap hover:bg-amber-200 dark:hover:bg-amber-900 transition-colors" data-testid={`button-remove-optional-${index}`} title="Move back to main works">
                                      Optional ✓
                                    </button>
                                    <div className="relative shrink-0 w-16 sm:w-28">
                                      <span className="absolute left-1.5 sm:left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">{sym}</span>
                                      <QuotePriceInput value={Math.round(itemSubtotal * 100) / 100} onCommit={(ns) => { if (item.quantity > 0) updateLineItem(index, "unitCost", ns / item.quantity); }} placeholder="0" className="h-8 pl-4 sm:pl-6 pr-0.5 sm:pr-2 text-xs sm:text-sm text-right" data-testid={`input-item-subtotal-${index}`} />
                                    </div>
                                    <QuoteRowActions index={index} onRemove={() => removeLineItem(index)} />
                                  </div>
                                  {isItemExpanded && (
                                    <div className="border-t border-amber-200/60 dark:border-amber-800/40 px-3 pt-3 pb-3 space-y-3 bg-muted/20">
                                      <div>
                                        <Button type="button" variant="outline" size="sm" onClick={() => openCategoryPicker(index)} className="border-dashed text-xs gap-1.5 max-w-full">
                                          {CategoryIcon ? <CategoryIcon className="h-3.5 w-3.5 flex-shrink-0" /> : null}
                                          <span className="truncate">{getCategoryTradeLabel(item.category, item.trade, item.subcategory, user?.market)}</span>
                                          <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                                        </Button>
                                      </div>
                                      <QuoteItemFields item={item} index={index} symbol={sym} market={user?.market}
                                        onUpdate={(field, value) => updateLineItem(index, field, value)} />
                                      <QuoteDimensions data={item.quantityDerivation} quantity={item.quantity} unit={item.unit} recipe={item.quoteCostRecipe} itemName={item.name}
                                        specification={`${item.name} ${item.description ?? ""} ${item.sourceVariant?.name ?? ""} ${item.sourceVariant?.specification ?? ""}`}
                                        onChange={(data, quantity) => updateLineItem(index, "quantity", quantity, data)} />
                                      <div className="pt-2 border-t">
                                        <QuoteItemRecipeBreakdown item={item} index={index} purchasingManaged
                                          sessionKey={user?.id}
                                          onMaterialAllowanceChange={approval => setLineItems(prev => prev.map((line, i) =>
                                            i === index ? { ...line, materialAllowanceApproval: approval } : line))}
                                          symbol={sym} onSplitChange={(field, value) => updateBreakdownField(index, field, value)}
                                          market={user?.market || "uk"} guestMode={guestMode}
                                          action={<Button type="button" variant="outline" size="sm" className="h-7 px-2 text-xs text-amber-600 border-amber-300/60 bg-transparent hover:bg-amber-50 hover:text-amber-700 whitespace-nowrap"
                                            onClick={() => updateLineItem(index, "isOptional", false)}>Include in quoted price</Button>}
                                          onApply={recipe => setLineItems(prev => prev.map((current, i) => i === index
                                            ? { ...current, quoteCostRecipe: recipe, unitCost: Number(deriveCostRecipe(recipe).rateTypical),
                                                ...quoteRecipeSplit(recipe, Number(current.quantity) || 0) }
                                            : current))} />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </details>
                      )}
                       </DndContext>
                      );
                    })()}
                  </div>
                  </QuoteAddItemsLayout>
                  <div className="mt-4 space-y-4" data-testid="estimate-full-width-reviews">
                    {!guestMode && <QuoteMaterialsWorkspace>
                    <QuantityMaterialsReview items={lineItems} autoSuggest sessionKey={user?.id}
                      onJump={index => {
                        setActiveCategoryFilter(null);
                        setActiveScopeTab("__combined__");
                        setCollapsedStages(prev => { const next = new Set(prev); const item = lineItems[index]; if (item) next.delete(getEffectiveStage(item)); return next; });
                        setExpandedItems(prev => new Set([...prev, index]));
                        setExpandedServiceGroups(new Set(lineItems.map(i => `${getEffectiveStage(i)}::${getEffectiveSubGroup(i)}`)));
                        setTimeout(() => {
                          const target = document.getElementById(`quote-work-item-${index}`);
                          const optional = target?.closest("details");
                          if (optional) optional.open = true;
                          target?.scrollIntoView({ behavior: "smooth", block: "center" });
                        }, 100);
                      }}
                      onAllowanceChange={(index, approval) => setLineItems(prev => prev.map((line, i) =>
                        i === index ? { ...line, materialAllowanceApproval: approval } : line))} />
                    <QuoteMaterialsList items={lineItems} requireApproval saved={editingSavedQuote} quoteId={editingQuoteId} saving={createQuoteMutation.isPending}
                      onApprovalChange={(index, approval) => setLineItems(prev => prev.map((line, i) =>
                        i === index ? { ...line, materialAllowanceApproval: approval } : line))}
                      onPriceChange={(id, price) => {
                        try {
                          const old = lineItems.flatMap(i => i.quoteCostRecipe?.materials ?? []).find(m => m.material_id === id)?.unit_price ?? 0;
                          const result = updateMaterialPrice(lineItems, id, price);
                          setLineItems(result.lines);
                          toast({ title: `Updated £${old.toFixed(2)} → £${price.toFixed(2)} on ${result.affected} ${result.affected === 1 ? "line" : "lines"}.` });
                        } catch (error) { toast({ title: error instanceof Error ? error.message : "Could not update material price", variant: "destructive" }); }
                      }}
                      onRoundingChange={(row, enabled) => setLineItems(prev => setMaterialPackRounding(prev, row, enabled))}
                      onOrderQuantityChange={(row, quantity) => setLineItems(prev => setMaterialOrderQuantity(prev, row, quantity))}
                      onJump={index => {
                        setActiveCategoryFilter(null);
                        setActiveScopeTab("__combined__");
                        setCollapsedStages(prev => {
                          const next = new Set(prev);
                          const item = lineItems[index];
                          if (item) next.delete(getEffectiveStage(item));
                          return next;
                        });
                        setExpandedItems(prev => new Set([...prev, index]));
                        setExpandedServiceGroups(new Set(lineItems.map(i => `${getEffectiveStage(i)}::${getEffectiveSubGroup(i)}`)));
                        setTimeout(() => {
                          const target = document.getElementById(`quote-work-item-${index}`);
                          const optional = target?.closest("details");
                          if (optional) optional.open = true;
                          target?.scrollIntoView({ behavior: "smooth", block: "center" });
                        }, 100);
                    }} />
                    </QuoteMaterialsWorkspace>}
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        const newItem: AnalyzedLineItem = {
                          category: "",
                          trade: "",
                          name: "",
                          description: "",
                          quantity: 1,
                          unit: "item",
                          unitCost: 0,
                          labourHours: 0,
                          materialCost: 0,
                        };
                        setLineItems(prev => {
                          const newList = [...prev, newItem];
                          setTimeout(() => openCategoryPicker(newList.length - 1, "add"), 100);
                          return newList;
                        });
                      }}
                       disabled={guestMode}
                       data-testid="button-add-work-item"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Work Item
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {categoryPickerIndex !== null && lineItems[categoryPickerIndex] && (
                <CategoryPickerDialog
                  open={true}
                  onOpenChange={(open) => { if (!open) setCategoryPickerIndex(null); }}
                  currentCategory={lineItems[categoryPickerIndex].category}
                  currentTrade={lineItems[categoryPickerIndex].trade}
                  currentSubcategory={lineItems[categoryPickerIndex].subcategory}
                  onSelect={(category, trade, subcategory) => {
                    const item = lineItems[categoryPickerIndex];
                    const destinationLabel = getCategoryTradeLabel(category, trade, subcategory, user?.market);
                    const destinationStage = getItemStage(category, trade, item.name);
                    const destinationGroup = getDisplayGroup({
                      category,
                      trade,
                      name: item.name,
                      scopeName: item.scopeName,
                    }) || destinationStage;
                    const resetFilter = categoryPickerPurpose === "move"
                      && shouldResetCategoryFilter(activeCategoryFilter, destinationGroup);
                    reassignLineItemCategory(categoryPickerIndex, category, trade, subcategory);
                    // Keep the moved item visible: reveal its destination stage and
                    // sub-group, and remove a category filter that would hide it.
                    setCollapsedStages(prev => {
                      const next = new Set(prev);
                      next.delete(destinationStage);
                      return next;
                    });
                    const destinationSubGroup = getItemSubGroup(category, item.name);
                    if (destinationSubGroup) {
                      setExpandedServiceGroups(prev => new Set([
                        ...prev,
                        `${destinationStage}::${destinationSubGroup}`,
                      ]));
                    }
                    setExpandedItems(prev => new Set([...prev, categoryPickerIndex]));
                    if (resetFilter) setActiveCategoryFilter(null);
                    setCategoryPickerIndex(null);
                    toast({
                      title: categoryPickerPurpose === "add" ? "Work item added" : "Category moved",
                      description: categoryPickerPurpose === "add"
                        ? `Added to ${destinationLabel}.`
                        : `Moved to ${destinationLabel} in ${destinationStage} for this quote only. Your Cost Library was not changed.${resetFilter ? " Showing all categories." : ""}`,
                    });
                  }}
                />
              )}
              <CategoryPickerDialog
                open={addToCostLibCategoryPickerOpen}
                onOpenChange={setAddToCostLibCategoryPickerOpen}
                currentCategory={addToCostLibCategory}
                currentTrade={addToCostLibTrade}
                currentSubcategory={addToCostLibSubcategory}
                onSelect={(category, trade, subcategory) => {
                  setAddToCostLibCategory(category);
                  setAddToCostLibTrade(trade);
                  setAddToCostLibSubcategory(subcategory);
                  setAddToCostLibCategoryPickerOpen(false);
                }}
              />
              <CategoryPickerDialog
                open={publishToDefaultLibCategoryPickerOpen}
                onOpenChange={setPublishToDefaultLibCategoryPickerOpen}
                currentCategory={publishToDefaultLibCategory}
                currentTrade={publishToDefaultLibTrade}
                currentSubcategory={publishToDefaultLibSubcategory}
                onSelect={(category, trade, subcategory) => {
                  setPublishToDefaultLibCategory(category);
                  setPublishToDefaultLibTrade(trade);
                  setPublishToDefaultLibSubcategory(subcategory);
                  setPublishToDefaultLibCategoryPickerOpen(false);
                }}
              />

              {/* Add Missing Work Item - AI Work Item Generator */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base sm:text-xl">Something Missing?</CardTitle>
                  <CardDescription>Describe a work item you'd like to add and the system will price it and include it in your quote.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-3 py-2">
                      <AlertCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800 dark:text-amber-300">Only one work item can be added per request. Please describe each item separately for accurate pricing.</p>
                    </div>
                    <Textarea
                      value={addDetailText}
                      onChange={(e) => setAddDetailText(e.target.value)}
                      placeholder="e.g. Install new radiator in the kitchen"
                      className="resize-none w-full"
                      rows={3}
                      data-testid="input-add-detail"
                    />
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        onClick={() => {
                          if (addDetailText.trim().length >= 3) {
                            generateWorkItemMutation.mutate(addDetailText.trim());
                          }
                        }}
                        size="sm"
                        disabled={guestMode || generateWorkItemMutation.isPending || addDetailText.trim().length < 3}
                        data-testid="button-add-detail"
                      >
                        {generateWorkItemMutation.isPending ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            Adding...
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                            Add Item
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Price Adjustment Sliders */}
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <CardTitle className="text-base sm:text-xl">Adjust Pricing</CardTitle>
                      <CardDescription>Use the sliders to proportionally adjust all work item prices</CardDescription>
                    </div>
                    <Dialog open={isUpgradeDialogOpen} onOpenChange={setIsUpgradeDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" className="shrink-0" disabled={guestMode} data-testid="button-learn-adjustment">
                          <TrendingUp className="h-4 w-4 mr-1.5" />
                          Learn
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Learn Adjustment</DialogTitle>
                          <DialogDescription>
                            Adjust prices by category percentage. The system will apply changes to all matching items.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <div>
                            <Label>Category</Label>
                            <Select value={upgradeCategory} onValueChange={setUpgradeCategory}>
                              <SelectTrigger data-testid="select-upgrade-category">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All Categories</SelectItem>
                                {COST_CATEGORIES.map((cat) => (
                                  <SelectItem key={cat.value} value={cat.value}>
                                    {cat.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Adjustment Percentage</Label>
                            <PercentInput
                              value={upgradePercent}
                              onChange={setUpgradePercent}
                              data-testid="input-upgrade-percent"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Enter positive to increase, negative to decrease
                            </p>
                          </div>
                          <div>
                            <Label>Reason (optional)</Label>
                            <Textarea
                              value={upgradeReason}
                              onChange={(e) => setUpgradeReason(e.target.value)}
                              placeholder="e.g., Labour always higher in this area"
                              className="resize-none"
                              data-testid="input-upgrade-reason"
                            />
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => setIsUpgradeDialogOpen(false)}>
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              onClick={applyUpgradePrices}
                              data-testid="button-apply-upgrade"
                            >
                              Apply
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {(() => {
                    // Anchor: snapshot of core prices at first slider touch.
                    // Falls back to current core subtotal so the slider always
                    // starts at 0% relative to where the user currently is.
                    const baseSubtotal = sliderBaseRef.current
                      ? sliderBaseRef.current.reduce((s, i) => i.isOptional ? s : s + i.quantity * i.unitCost, 0)
                      : totals.subtotal || 1000;
                    const sliderMin = Math.round(baseSubtotal * 0.5);
                    const sliderMax = Math.round(baseSubtotal * 1.5);
                    const displayVal = subtotalSliderVal !== null
                      ? Math.min(Math.max(subtotalSliderVal, sliderMin), sliderMax)
                      : baseSubtotal; // show 0% until user moves the slider
                    const pctChange = Math.round(((displayVal - baseSubtotal) / baseSubtotal) * 100);
                    return (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium">Subtotal</label>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{formatCurrency(totals.subtotal)}</span>
                            {pctChange !== 0 && (
                              <span className={`text-sm font-semibold ${pctChange > 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                                {pctChange > 0 ? `+${pctChange}%` : `${pctChange}%`}
                              </span>
                            )}
                          </div>
                        </div>
                        <Slider
                          value={[displayVal]}
                          min={sliderMin}
                          max={sliderMax}
                          step={50}
                          onValueChange={([val]) => {
                            setSubtotalSliderVal(val);
                            applySubtotalScale(val);
                          }}
                          data-testid="slider-subtotal"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>-50% {formatCurrency(sliderMin)}</span>
                          <span>+50% {formatCurrency(sliderMax)}</span>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Pricing Options */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base sm:text-xl">Pricing Options</CardTitle>
                  <CardDescription>Enable and set additional charges as needed</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 flex-1">
                        <Switch
                          checked={contingencyEnabled}
                          onCheckedChange={(checked) => {
                            setContingencyEnabled(checked);
                            if (checked && contingencyPercent === "0") setContingencyPercent("5");
                          }}
                          data-testid="switch-contingency"
                        />
                        <label className="text-sm font-medium">Contingency</label>
                      </div>
                      {contingencyEnabled && (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={contingencyPercent}
                            onChange={(e) => setContingencyPercent(e.target.value)}
                            className="w-20"
                            data-testid="input-contingency"
                          />
                          <span className="text-sm text-muted-foreground">%</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 flex-1">
                        <Switch
                          checked={overheadEnabled}
                          onCheckedChange={(checked) => {
                            setOverheadEnabled(checked);
                            if (checked && overheadPercent === "0") setOverheadPercent("10");
                          }}
                          data-testid="switch-overhead"
                        />
                        <label className="text-sm font-medium">Overhead</label>
                      </div>
                      {overheadEnabled && (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={overheadPercent}
                            onChange={(e) => setOverheadPercent(e.target.value)}
                            className="w-20"
                            data-testid="input-overhead"
                          />
                          <span className="text-sm text-muted-foreground">%</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 flex-1">
                        <Switch
                          checked={profitEnabled}
                          onCheckedChange={(checked) => {
                            setProfitEnabled(checked);
                            if (checked && profitPercent === "0") setProfitPercent("15");
                          }}
                          data-testid="switch-profit"
                        />
                        <label className="text-sm font-medium">Profit</label>
                      </div>
                      {profitEnabled && (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={profitPercent}
                            onChange={(e) => setProfitPercent(e.target.value)}
                            className="w-20"
                            data-testid="input-profit"
                          />
                          <span className="text-sm text-muted-foreground">%</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 pt-2 border-t">
                      <Switch
                        checked={includeVat}
                        onCheckedChange={setIncludeVat}
                        data-testid="switch-vat"
                      />
                      <label className="text-sm font-medium">Include {getTaxLabel(user?.market)}</label>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Totals Summary */}
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Subtotal</span>
                      <span>{formatCurrency(totals.subtotal)}</span>
                    </div>
                    {contingencyEnabled && (
                      <div className="flex justify-between text-muted-foreground">
                        <span>Contingency ({contingencyPercent}%)</span>
                        <span>{formatCurrency(totals.contingency)}</span>
                      </div>
                    )}
                    {overheadEnabled && (
                      <div className="flex justify-between text-muted-foreground">
                        <span>Overhead ({overheadPercent}%)</span>
                        <span>{formatCurrency(totals.overhead)}</span>
                      </div>
                    )}
                    {profitEnabled && (
                      <div className="flex justify-between text-muted-foreground">
                        <span>Profit ({profitPercent}%)</span>
                        <span>{formatCurrency(totals.profit)}</span>
                      </div>
                    )}
                    <div className="border-t pt-2 flex justify-between font-medium">
                      <span>Total (ex {getTaxLabel(user?.market)})</span>
                      <span>{formatCurrency(totals.totalPrice)}</span>
                    </div>
                    {includeVat && (
                      <div className="flex justify-between text-muted-foreground">
                        <span>{getTaxLabel(user?.market)}</span>
                        <span>{formatCurrency(totals.vatAmount)}</span>
                      </div>
                    )}
                    <div className="border-t pt-2 flex justify-between text-lg font-bold text-primary">
                      <span>Grand Total</span>
                      <span data-testid="text-grand-total">{formatCurrency(totals.grandTotal)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {editingSavedQuote && <Card data-testid="saved-quote-details">
                <CardHeader><CardTitle className="text-base">Quote details</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2"><Label htmlFor="edit-project-address">Project address</Label>
                    <Input id="edit-project-address" {...form.register("projectAddress")} /></div>
                  <div className="space-y-2"><Label htmlFor="edit-project-description">Project description</Label>
                    <Textarea id="edit-project-description" {...form.register("description")} /></div>
                  <div className="space-y-2"><Label htmlFor="edit-quote-terms">Terms &amp; Conditions</Label>
                    <Textarea id="edit-quote-terms" value={termsConditions} onChange={event => setTermsConditions(event.target.value)} /></div>
                  <div className="space-y-2"><Label htmlFor="edit-payment-terms">Payment Terms</Label>
                    <Textarea id="edit-payment-terms" value={paymentTerms} onChange={event => setPaymentTerms(event.target.value)} /></div>
                  <div className="space-y-2"><Label htmlFor="edit-quote-assumptions">Assumptions</Label>
                    <Textarea id="edit-quote-assumptions" value={editedAssumptions.join("\n")} onChange={event => setEditedAssumptions(event.target.value.split("\n"))} /></div>
                  <div className="space-y-2"><Label htmlFor="edit-quote-exclusions">Exclusions</Label>
                    <Textarea id="edit-quote-exclusions" value={editedExclusions.join("\n")} onChange={event => setEditedExclusions(event.target.value.split("\n"))} /></div>
                </CardContent>
              </Card>}
              <div className="flex justify-between">
                <Button 
                  type="button" 
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (editingSavedQuote) { navigate(`/quotes/${editingQuoteId}`); return; }
                    setStep(1);
                    setAnalysisResult(null);
                    setLineItems([]);
                    setDescriptionFlags([]);
                    setStructuralSummary(null);
                    setAppliedDescFlags(new Set());
                    setAppliedFlags(new Set());
                    setAppliedAdjustments([]);
                    clearDraftFromStorage();
                    if (user?.id && pendingGenerationId) forgetGeneration(user.id, pendingGenerationId);
                    generationController.current?.abort();
                    activeGenerationRequestRef.current = null;
                    completedGenerationRef.current = null;
                    autoRecoveryRef.current = null;
                    setPendingGenerationId(null);
                  }}
                  data-testid="button-back-step"
                >
                  <ArrowLeft className="h-4 w-4 mr-1.5" />
                  {editingSavedQuote ? "Discard Changes" : "Back"}
                </Button>
                <Button 
                  type="button"
                  onClick={() => {
                    if (editingSavedQuote) createQuoteMutation.mutate();
                    else setStep(3);
                  }}
                  disabled={lineItems.length === 0 || createQuoteMutation.isPending}
                  data-testid="button-next-review"
                >
                  {editingSavedQuote ? (createQuoteMutation.isPending ? "Saving..." : "Save Changes") : guestMode ? "Review & Save" : "Create Quote"}
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Create Quote */}
          {step === 3 && (
            <div className="space-y-6">
              <Card data-testid="quote-final-summary">
                <CardHeader>
                  <CardTitle className="text-base font-semibold">Ready to Create Quote</CardTitle>
                  <CardDescription>
                    Review your quote summary and create the final quote document
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Project Type</p>
                      <p className="font-medium">{analysisResult?.projectType?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "General Works"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Items</p>
                      <p className="font-medium">{lineItems.length} work items</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Estimated Duration</p>
                      <p className="font-medium">{customEstDays ?? analysisResult?.estimatedDays} working days</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Grand Total</p>
                      <p className="font-medium text-primary text-lg">{formatCurrency(totals.grandTotal)}</p>
                    </div>
                  </div>

                  {editedAssumptions.length > 0 && (
                    <div className="border rounded-md">
                      <button
                        type="button"
                        onClick={() => setAssumptionsOpen(o => !o)}
                        className="flex items-center justify-between w-full px-3 py-2 text-left hover:bg-muted/30 rounded-md transition-colors"
                      >
                        <p className="text-sm font-medium">Assumptions ({editedAssumptions.length})</p>
                        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${assumptionsOpen ? "rotate-180" : ""}`} />
                      </button>
                      {assumptionsOpen && (
                        <div className="px-3 pb-3 space-y-2 max-h-48 overflow-y-auto border-t pt-2">
                          {editedAssumptions.map((a, i) => (
                            <div key={i} className="flex gap-2">
                              <Input
                                value={a}
                                onChange={(e) => {
                                  const updated = [...editedAssumptions];
                                  updated[i] = e.target.value;
                                  setEditedAssumptions(updated);
                                }}
                                className="text-sm h-8"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 shrink-0"
                                onClick={() => setEditedAssumptions(prev => prev.filter((_, idx) => idx !== i))}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-xs h-7"
                            onClick={() => setEditedAssumptions(prev => [...prev, ""])}
                          >
                            + Add Assumption
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {editedExclusions.length > 0 && (
                    <div className="border rounded-md">
                      <button
                        type="button"
                        onClick={() => setExclusionsOpen(o => !o)}
                        className="flex items-center justify-between w-full px-3 py-2 text-left hover:bg-muted/30 rounded-md transition-colors"
                      >
                        <p className="text-sm font-medium">Exclusions ({editedExclusions.length})</p>
                        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${exclusionsOpen ? "rotate-180" : ""}`} />
                      </button>
                      {exclusionsOpen && (
                        <div className="px-3 pb-3 space-y-2 max-h-48 overflow-y-auto border-t pt-2">
                          {editedExclusions.map((e, i) => (
                            <div key={i} className="flex gap-2">
                              <Input
                                value={e}
                                onChange={(e) => {
                                  const updated = [...editedExclusions];
                                  updated[i] = e.target.value;
                                  setEditedExclusions(updated);
                                }}
                                className="text-sm h-8"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 shrink-0"
                                onClick={() => setEditedExclusions(prev => prev.filter((_, idx) => idx !== i))}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-xs h-7"
                            onClick={() => setEditedExclusions(prev => [...prev, ""])}
                          >
                            + Add Exclusion
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-between gap-3">
                <Button 
                  type="button" 
                  size="sm"
                  variant="outline" 
                  onClick={() => setStep(2)}
                  disabled={guestMode && guestFinalized}
                  data-testid="button-back-to-review"
                >
                  <ArrowLeft className="h-4 w-4 mr-1.5" />
                  Back to Edit
                </Button>
                {(!guestMode || !guestFinalized) && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => guestMode ? guestFinalizeMutation.mutate() : createQuoteMutation.mutate()}
                    disabled={guestMode ? guestFinalizeMutation.isPending : createQuoteMutation.isPending}
                    data-testid="button-create-quote"
                  >
                    {(guestMode ? guestFinalizeMutation.isPending : createQuoteMutation.isPending) ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {guestMode ? "Saving..." : "Creating..."}
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        {guestMode ? "Finish & Save" : "Create Quote"}
                      </>
                    )}
                  </Button>
                )}
                {guestMode && guestFinalized && (
                  <Button
                    type="button"
                    variant="default"
                    onClick={() => navigate("/login?returnTo=/try-a-quote/claim")}
                    data-testid="button-guest-signup"
                  >
                    Sign Up
                  </Button>
                )}
              </div>
            </div>
          )}
        </form>
      </Form>

      {/* Set Price confirmation dialog */}
      <Dialog open={setPriceOpen} onOpenChange={setSetPriceOpen}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-set-price">
          <DialogHeader>
            <DialogTitle>Set Price</DialogTitle>
            <DialogDescription>
              {isAdminUser && setPriceItem?.costLibraryTaskId
                ? "This will update the Master Cost Library and become the new default rate for all users. Individual price overrides will not be changed."
                : "The Smart Engine will use the typical rate exactly on all future quotes for this item — no range adjustments or outlier caps. You can reset it to the default at any time in your Cost Library."}
            </DialogDescription>
          </DialogHeader>
          {setPriceItem && (
            <div className="space-y-4">
              <div className="rounded-md bg-muted/50 px-3 py-2 text-sm font-medium text-foreground">
                {setPriceItem.name}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="sp-rate-low" className="text-xs text-muted-foreground">Rate Low (£)</Label>
                  <Input
                    id="sp-rate-low"
                    type="number"
                    step="0.01"
                    min="0"
                    value={setPriceRateLow}
                    onChange={(e) => setSetPriceRateLow(e.target.value)}
                    placeholder="0.00"
                    data-testid="input-set-price-rate-low"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sp-rate-typical" className="text-xs text-muted-foreground">Typical (£)</Label>
                  <Input
                    id="sp-rate-typical"
                    type="number"
                    step="0.01"
                    min="0"
                    value={setPriceRateTypical}
                    onChange={(e) => setSetPriceRateTypical(e.target.value)}
                    placeholder="0.00"
                    data-testid="input-set-price-rate-typical"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sp-rate-high" className="text-xs text-muted-foreground">Rate High (£)</Label>
                  <Input
                    id="sp-rate-high"
                    type="number"
                    step="0.01"
                    min="0"
                    value={setPriceRateHigh}
                    onChange={(e) => setSetPriceRateHigh(e.target.value)}
                    placeholder="0.00"
                    data-testid="input-set-price-rate-high"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setSetPriceOpen(false)} data-testid="button-set-price-cancel">
                  Cancel
                </Button>
                <Button
                  className="bg-teal-500 hover:bg-teal-600 text-white"
                  disabled={
                    (isAdminUser && !!setPriceItem?.costLibraryTaskId ? adminSetPriceMutation.isPending : learnPriceMutation.isPending) ||
                    !setPriceRateTypical
                  }
                  onClick={() => {
                    if (!setPriceItem) return;
                    if (isAdminUser && setPriceItem.costLibraryTaskId) {
                      adminSetPriceMutation.mutate({
                        taskId: setPriceItem.costLibraryTaskId,
                        rateLow: setPriceRateLow,
                        rateTypical: setPriceRateTypical,
                        rateHigh: setPriceRateHigh,
                        itemIndex: setPriceItemIndex,
                      });
                    } else {
                      const originalValue = baseLineItemsRef.current[setPriceItemIndex]?.unitCost || setPriceItem.unitCost;
                      const pricedItem = { ...setPriceItem, unitCost: parseFloat(setPriceRateTypical) || setPriceItem.unitCost };
                      learnPriceMutation.mutate({ item: pricedItem, oldPrice: originalValue, itemIndex: setPriceItemIndex });
                    }
                    setSetPriceOpen(false);
                  }}
                  data-testid="button-set-price-confirm"
                >
                  {(isAdminUser && !!setPriceItem?.costLibraryTaskId ? adminSetPriceMutation.isPending : learnPriceMutation.isPending)
                    ? "Saving…" : "Set Price"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={addToCostLibOpen} onOpenChange={setAddToCostLibOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Cost Library</DialogTitle>
            <DialogDescription>This item was not found in your Cost Library. Add it so future quotes can use your rate.</DialogDescription>
          </DialogHeader>
          {addToCostLibItem && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="cl-dialog-name">Task Name</Label>
                <Input
                  id="cl-dialog-name"
                  className="mt-1"
                  value={addToCostLibName}
                  onChange={(e) => setAddToCostLibName(e.target.value)}
                  placeholder="Task name"
                  data-testid="input-add-cost-lib-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-1 border-dashed text-xs gap-1.5 w-full justify-start"
                    onClick={() => setAddToCostLibCategoryPickerOpen(true)}
                    data-testid="button-add-cost-lib-category"
                  >
                    {addToCostLibCategory
                      ? getCategoryTradeLabel(addToCostLibCategory, addToCostLibTrade, addToCostLibSubcategory, user?.market).split(" > ").pop()
                      : "Select category"}
                    <ChevronDown className="h-3 w-3 text-muted-foreground ml-auto shrink-0" />
                  </Button>
                </div>
                <div>
                  <Label>Unit</Label>
                  <Select value={addToCostLibUnit} onValueChange={setAddToCostLibUnit}>
                    <SelectTrigger className="mt-1" data-testid="select-add-cost-lib-unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(getUnits(user?.market)).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label as string}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="cl-dialog-rate">Your Rate ({getMarketCurrency(user?.market).symbol} per {addToCostLibUnit})</Label>
                <Input
                  id="cl-dialog-rate"
                  type="number"
                  className="mt-1"
                  value={addToCostLibRate}
                  onChange={(e) => setAddToCostLibRate(e.target.value)}
                  placeholder="0.00"
                  data-testid="input-add-cost-lib-rate"
                />
              </div>
              {!addToCostLibCategory && (
                <div className="text-sm text-destructive">Please select a category for this item before adding to the library.</div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setAddToCostLibOpen(false)} data-testid="button-cancel-add-cost-lib">
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    const editedItem = {
                      ...addToCostLibItem,
                      name: addToCostLibName || addToCostLibItem.name,
                      category: addToCostLibCategory || addToCostLibItem.category,
                      unit: addToCostLibUnit || addToCostLibItem.unit,
                      unitCost: parseFloat(addToCostLibRate) || addToCostLibItem.unitCost,
                    };
                    addToCostLibMutation.mutate({ item: editedItem, trade: addToCostLibTrade || editedItem.trade || "general", itemIndex: addToCostLibIndex });
                  }}
                  disabled={addToCostLibMutation.isPending || !addToCostLibCategory}
                  data-testid="button-confirm-add-cost-lib"
                >
                  {addToCostLibMutation.isPending ? "Adding..." : "Add to Cost Library"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin: Publish to Default Library dialog */}
      <Dialog open={publishToDefaultLibOpen} onOpenChange={setPublishToDefaultLibOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Publish to Default Library</DialogTitle>
            <DialogDescription>Review and confirm the details before publishing this item as a default entry available to all users.</DialogDescription>
          </DialogHeader>
          {publishToDefaultLibItem && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="ptdl-name">Task Name</Label>
                <input
                  id="ptdl-name"
                  className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={publishToDefaultLibName}
                  onChange={(e) => setPublishToDefaultLibName(e.target.value)}
                  placeholder="Task name"
                  data-testid="input-publish-default-lib-name"
                />
              </div>
              <div>
                <Label htmlFor="ptdl-description">Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Textarea
                  id="ptdl-description"
                  className="mt-1 resize-none"
                  rows={2}
                  value={publishToDefaultLibDescription}
                  onChange={(e) => setPublishToDefaultLibDescription(e.target.value)}
                  placeholder="Short description of this work item…"
                  data-testid="input-publish-default-lib-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-1 border-dashed text-xs gap-1.5 w-full justify-start"
                    onClick={() => setPublishToDefaultLibCategoryPickerOpen(true)}
                    data-testid="button-publish-default-lib-category"
                  >
                    {publishToDefaultLibCategory
                      ? getCategoryTradeLabel(publishToDefaultLibCategory, publishToDefaultLibTrade, publishToDefaultLibSubcategory, user?.market).split(" > ").pop()
                      : "Select category"}
                    <ChevronDown className="h-3 w-3 text-muted-foreground ml-auto shrink-0" />
                  </Button>
                </div>
                <div>
                  <Label>Unit</Label>
                  <Select value={publishToDefaultLibUnit} onValueChange={setPublishToDefaultLibUnit}>
                    <SelectTrigger className="mt-1" data-testid="select-publish-default-lib-unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(getUnits(user?.market)).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label as string}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="ptdl-rate-low">Rate Low ({getMarketCurrency(user?.market).symbol})</Label>
                  <input
                    id="ptdl-rate-low"
                    type="number"
                    className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    value={publishToDefaultLibRateLow}
                    onChange={(e) => setPublishToDefaultLibRateLow(e.target.value)}
                    placeholder="0.00"
                    data-testid="input-publish-default-lib-rate-low"
                  />
                </div>
                <div>
                  <Label htmlFor="ptdl-rate-typical">Typical ({getMarketCurrency(user?.market).symbol})</Label>
                  <input
                    id="ptdl-rate-typical"
                    type="number"
                    className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    value={publishToDefaultLibRateTypical}
                    onChange={(e) => setPublishToDefaultLibRateTypical(e.target.value)}
                    placeholder="0.00"
                    data-testid="input-publish-default-lib-rate-typical"
                  />
                </div>
                <div>
                  <Label htmlFor="ptdl-rate-high">Rate High ({getMarketCurrency(user?.market).symbol})</Label>
                  <input
                    id="ptdl-rate-high"
                    type="number"
                    className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    value={publishToDefaultLibRateHigh}
                    onChange={(e) => setPublishToDefaultLibRateHigh(e.target.value)}
                    placeholder="0.00"
                    data-testid="input-publish-default-lib-rate-high"
                  />
                </div>
              </div>
              {!publishToDefaultLibCategory && (
                <div className="text-sm text-destructive">Please select a category before publishing.</div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPublishToDefaultLibOpen(false)} data-testid="button-cancel-publish-default-lib">
                  Cancel
                </Button>
                <Button
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={() => {
                    publishToDefaultLibMutation.mutate({
                      name: publishToDefaultLibName || publishToDefaultLibItem.name,
                      description: publishToDefaultLibDescription,
                      category: publishToDefaultLibCategory || publishToDefaultLibItem.category,
                      trade: publishToDefaultLibTrade || publishToDefaultLibItem.trade || "general",
                      unit: publishToDefaultLibUnit || publishToDefaultLibItem.unit,
                      rateLow: publishToDefaultLibRateLow,
                      rateTypical: publishToDefaultLibRateTypical,
                      rateHigh: publishToDefaultLibRateHigh,
                      itemIndex: publishToDefaultLibIndex,
                    });
                  }}
                  disabled={publishToDefaultLibMutation.isPending || !publishToDefaultLibCategory || !publishToDefaultLibRateTypical}
                  data-testid="button-confirm-publish-default-lib"
                >
                  {publishToDefaultLibMutation.isPending ? "Publishing…" : "Publish to Default Library"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showDescConfirmModal} onOpenChange={setShowDescConfirmModal}>
        <DialogContent className="sm:max-w-lg" data-testid="dialog-desc-confirm">
          <DialogHeader className="pb-1">
            <DialogTitle className="text-xl">Before You Continue</DialogTitle>
          </DialogHeader>
          {descriptionIsGenerated && <p className="text-sm font-medium text-amber-900 rounded-md border border-amber-200 bg-amber-50 p-3">
            Regeneration is optional and will replace your current description and measurement review. Cancel to keep your edits and continue to your quote.
          </p>}
          {/* Amber callout banner */}
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
            <p>The quality of your quote depends on the information you provide. <span className="font-semibold">Better input = Better quotes.</span></p>
          </div>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-3 items-start text-muted-foreground">
              <Upload className="h-4 w-4 shrink-0 mt-0.5 text-foreground/50" />
              <span>
                <span className="font-medium text-foreground">Upload only relevant files.</span>{" "}
                Avoid large, multi-page documents. Upload only the drawings or details needed for this job. For example, upload the structural drawings rather than the entire calculation package.
              </span>
            </li>
            <li className="flex gap-3 items-start text-muted-foreground">
              <Layers className="h-4 w-4 shrink-0 mt-0.5 text-foreground/50" />
              <span>
                <span className="font-medium text-foreground">Include key dimensions</span>{" "}
                such as floor area, beam spans, wall heights, widths, and lengths where available. The Smart Engine can read dimensions but cannot measure or scale drawings.
              </span>
            </li>
            <li className="flex gap-3 items-start text-muted-foreground">
              <Pencil className="h-4 w-4 shrink-0 mt-0.5 text-foreground/50" />
              <span>
                <span className="font-medium text-foreground">Write a clear description of the work,</span>{" "}
                especially if you're not uploading files.{" "}
                <span className="font-medium text-foreground">More detail = Better results.</span>
              </span>
            </li>
            <li className="flex gap-3 items-start text-muted-foreground">
              <Eye className="h-4 w-4 shrink-0 mt-0.5 text-foreground/50" />
              <span>
                <span className="font-medium text-foreground">Review the generated description</span>{" "}
                and make sure nothing important is missing before generating the quote.
              </span>
            </li>
            <li className="flex gap-3 items-start rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 -mx-1">
              <Check className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
              <span className="text-amber-900">
                <span className="font-semibold">Always review the final quote</span>{" "}
                before sending it to your client.
              </span>
            </li>
          </ul>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setShowDescConfirmModal(false);
                if (!guestMode) trackEvent("description_confirmation_cancelled", "/quotes/new");
              }}
              data-testid="button-desc-confirm-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                descConfirmShownRef.current = true;
                setShowDescConfirmModal(false);
                startDescriptionGeneration();
              }}
              data-testid="button-desc-confirm-proceed"
            >
              {descriptionIsGenerated ? "Replace with a fresh AI description" : "Create Description"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1]); // Remove data URL prefix
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
